import { primeKeyboard } from "../../capacitor/keyboardNow";
import ConfirmHeader from "../../components/ConfirmHeader";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal as createPortalDom, flushSync } from "react-dom";

import { Capacitor } from "@capacitor/core";
import {
  AlertTriangle, Archive, ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle, CheckSquare,
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, Filter, LayoutGrid, ListTodo, Lock, LockOpen, Maximize2, Milestone,
  Minimize2, MoreHorizontal, Pencil, Plus, RotateCcw, Save, Settings, SkipForward, SlidersVertical, Sparkles,
  Trash2, Trophy, X,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListUl, faCalendarDays as faCalendarDaysSolid } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../components/Modal";
import { PanelWarmup } from "../../components/PanelWarmup";
import { Card } from "../../components/Card";
import { CategoryIcon } from "../../components/CategoryIcon";
import { LocationAutocomplete } from "../../components/pickers/LocationAutocomplete";
import { ReminderPicker } from "../../components/pickers/ReminderPicker";
import { ModalColorPicker } from "../../components/pickers/ModalColorPicker";
import { RichNotesEditor } from "../../components/RichNotesEditor";
import { HabitCheckbox } from "../../components/HabitCheckbox";
import { EmbedPill } from "../../components/EmbedPill";
import { AttachmentField } from "../../components/attachments/AttachmentField";
import { PullTabDrawer } from "../../components/PullTabDrawer";
import { TasksTab } from "../TasksTab";
import { ListItem } from "./ListView";
import { ItemDetailPanel } from "./ItemDetailPanel";
import { NativePickerInput } from "../../components/NativePickerInput";
import { ItemPanel } from "./CalendarExtras";
import { formatTimeRange } from "../../utils/timeRange";
import { weekDropTimes, clampGridStart } from "../../utils/weekDrop";
import { isGoogleTaskEvent } from "../../google/googleTask";
import { QuickAddBar } from "./QuickAddBar";
import { useHolidays } from "./useHolidays";
import { MONTHS, DAYS_SHORT, DAYS_FULL } from "../../constants/dates";
import { EVENT_COLORS, PRIORITY_COLORS, DEFAULT_TASK_COLOR, DEFAULT_EVENT_COLOR } from "../../constants/palettes";
import { LINK_CATS } from "../../constants/misc";
import { S, DETAIL_BACKDROP, CONFIRM_STACK, CONFIRM_DANGER, CONFIRM_CANCEL, CONFIRM_PRIMARY, SKIP_CAL_SHADOW } from "../../theme/sharedStyles";
import { holdNavBlur } from "../../utils/navBlur";
import { isDarkBgTheme, isDarkMode, categoryInk, themeTone } from "../../theme/colorEngine";
import { now, fmt, todayStr, daysInMonth, toGridMinutes, toAbsoluteMinutes } from "../../utils/dateHelpers";
import { isOccurringOn, isTaskOccurringOn, removeEventCascade, removeEventsCascade, isTaskDoneOn, toggleTaskDoneOn } from "../../utils/taskLogic";
import { labelColor } from "../../utils/labelColors";
import { load, save, uid } from "../../utils/storage";
import { softDeleteCalendarItems, DELETED_ITEMS_KEY, TRASH_RETENTION_MS } from "../../utils/trash";
import { listDragState, monthDragOverState } from "../../utils/dragState";
import { PLANNER_OPEN_DATE_FLAG, PLANNER_OPEN_DATE_EVENT } from "../../capacitor/widgetSync";
import { isEventPassed, isRecurringEvent } from "../../utils/eventStatus";

// Public holidays (src/utils/holidays.js): always this green, whatever the
// background (Jade; an accent tint was tried and dropped). Fixed light colours
// suit the Month grid and Week header, which are always white.
const HOLIDAY_BG = "#dff3e4";
const HOLIDAY_TEXT = "#2e7d4f";

// Every Planner portal goes through this wrapper so App can hide them all with
// one CSS rule while the Planner tab lingers hidden after you leave it (see
// App.jsx: html[data-tab]). display:contents keeps the wrapper box-less, so
// fixed positioning and stacking of the portalled node are unchanged.
const createPortal = (node, target) => createPortalDom(<div data-cal-portal="" style={{ display: "contents" }}>{node}</div>, target);

// Week view carousel. Every pane drops the card chrome (the fixed host draws
// it). Neighbours never show hour labels; the live pane's labels are opaque so
// the previous week's Saturday, tucked under them, stays hidden. While a drag
// is in progress (.cal-dragging on the host) the live labels and header corner
// (which holds the all-day row's chevron, and would otherwise slide over the
// previous week's Saturday) hide and the fixed
// overlay — a copy of the current week showing ONLY what's the same every
// week: hour labels, header corner and the horizontal lines — takes over.
const CAL_STRIP_CSS = `
.cal-strip-pane .cal-week-card, .cal-strip-labels .cal-week-card {
  border-width: 0 !important; background: transparent !important;
  box-shadow: none !important; border-radius: 0 !important;
}
.cal-strip-pane .wk-header, .cal-strip-labels .wk-header { background: transparent !important; }
.cal-pane-live .wk-label, .cal-pane-live .wk-corner { background: var(--c-cal-surface); }
.cal-strip-pane:not(.cal-pane-live) .wk-label, .cal-dragging .cal-pane-live .wk-label { visibility: hidden; }
.cal-dragging .cal-pane-live .wk-corner { visibility: hidden; }
.cal-strip-labels { visibility: hidden; opacity: 0; }
.cal-dragging .cal-strip-labels { opacity: 1; }
.cal-strip-labels .wk-header, .cal-strip-labels .wk-hrow { visibility: visible; }
.cal-strip-labels .wk-hday, .cal-strip-labels .wk-slot { visibility: hidden; }
.cal-strip-labels .wk-label, .cal-strip-labels .wk-corner { background: var(--c-cal-surface); }
/* "Match background" grid has no surface, so the opaque labels above can't hide
   the previous week's Saturday tucked under them: hide the neighbours until a
   swipe starts instead. */
html[data-cal-grid="match"] .cal-host:not(.cal-dragging) .cal-strip-pane:not(.cal-pane-live) { visibility: hidden !important; }
/* Same reason, after a committed swipe: until the new period renders, the old
   live pane's last day sits under the see-through labels (only on "next" — the
   other way it is off-screen). .cal-landed is set when the slide ends and
   cleared in the same commit that re-renders the panes. */
html[data-cal-grid="match"] .cal-landed .cal-pane-live { visibility: hidden !important; }
/* Match mode mid-swipe: with no opaque labels, the days sliding under the label
   column stayed visible, and the overlay's see-through grid lines stacked on the
   panes' (lines looked thick until the swipe ended — Jade). So the two split the
   width: the sliding strip is clipped off the label column and the overlay is
   clipped to it, never overlapping. */
html[data-cal-grid="match"] .cal-dragging > .cal-strip-clip { clip-path: inset(0 0 0 var(--cal-gutter)); }
html[data-cal-grid="match"] .cal-dragging > .cal-strip-labels { clip-path: inset(0 calc(100% - var(--cal-gutter)) 0 0); }
`;

// Plain conditional rendering already unmounts/remounts the list-view root
// and the calendar-view root when plannerView flips (mobile: exactly one of
// them is present at a time; desktop: the list panel mounts/unmounts beside
// the always-present calendar panel), so a mount-triggered CSS animation is
// enough — no key/state machine needed to "detect" the switch.
const VIEW_SWITCH_CSS = `
@keyframes clarityViewEnter { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.clarity-view-enter { animation: clarityViewEnter 0.22s ease-out; }
`;

// Runs fn once the next frame has painted. Closing the detail panel re-renders
// the whole Planner, so when Edit hands off to the app-level edit panel the
// close waits until that panel is on screen (it covers the detail anyway).
const afterPaint = (fn) => requestAnimationFrame(() => setTimeout(fn, 0));

// Dark text on a light item block. Deliberately not #1a1a1a / rgba(0,0,0,..):
// dark mode's catch-alls (darkModeCss.js) turn those into light text, which
// vanished on a pastel block. A block's text follows the block's own colour.
const BLOCK_INK = "#191919";
const BLOCK_INK_SOFT = "rgba(25, 25, 25, 0.6)";

// Frosted-glass button surface: the desktop Week/Month/Year dropdown, and (desktop
// only) the period arrows beside it (Jade, 2026-10-20). Translucent white over a
// blur, so it reads on the white, dark and gradient backgrounds alike.
const FROSTED_SURFACE = {
  border: "1px solid rgba(255,255,255,0.4)",
  background: "rgba(255,255,255,0.35)",
  backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
  boxShadow: "0 1px 6px var(--c-shadow)",
};

export function CalendarTab({ tasks, setTasks, categories, setCategories = () => {}, calEvents, setCalEvents, calReminders, setCalReminders, focusSessions = [], setFocusSessions, setWeekBlockPop, onEditItem = () => {}, externalPanelOpen = false, plannerView, setPlannerView, isDesktop = false, bgTheme = "none", taskOccurrenceOverrides = {}, setTaskOccurrenceOverrides = () => {}, calEventOccurrenceOverrides = {}, setCalEventOccurrenceOverrides = () => {}, onOpenSettings = () => {}, weekStart = "sunday", dayStartHour = 0 }) {
  // { "YYYY-MM-DD": "Holiday name" } for the detected country; read-only labels.
  const holidays = useHolidays();
  // Everything below builds week/month grids assuming Sunday sits in column 0
  // (raw Date.getDay(): 0=Sun…6=Sat). `dowOffset` re-maps that into a 0-based
  // column index for whichever day the user picked as their week's start, so
  // one small helper fixes every grid/offset call site instead of each one
  // reimplementing the shift. Per-date weekday *labels* (DAYS_SHORT/DAYS_FULL
  // indexed straight by getDay()) don't need this — a date's name doesn't
  // change, only where it sits in the grid.
  const mondayFirst = weekStart === "monday";
  const dowOffset = (jsDay) => mondayFirst ? (jsDay + 6) % 7 : jsDay;
  const weekdayHeaderShort = mondayFirst ? [...DAYS_SHORT.slice(1), DAYS_SHORT[0]] : DAYS_SHORT;
  const weekdayHeaderLetter = mondayFirst ? ["M","T","W","T","F","S","S"] : ["S","M","T","W","T","F","S"];
  const weekdayHeaderTwo = mondayFirst ? ["Mo","Tu","We","Th","Fr","Sa","Su"] : ["Su","Mo","Tu","We","Th","Fr","Sa"];
  // ── List-view multi-select (bulk delete) ──────────────────────────────────
  // Select mode lets the user tick tasks/events/reminders across all three
  // sub-tabs (upcoming/finished/missed) and delete them in one go. Deleting
  // removes them from the underlying tasks/calEvents/calReminders state, so they
  // disappear from the calendar too. Keys are `${type}:${id}` so selection
  // survives switching between sub-tabs.
  const [listSelectMode, setListSelectMode] = useState(false);
  // Select/Recently Deleted/Reflections live behind a pull-tab on the list
  // panel's edge instead of a full row of buttons beside the Tasks/Events
  // tabs, which get to fill the freed width instead.
  const [listDrawerOpen, setListDrawerOpen] = useState(false);
  const [listSelectedKeys, setListSelectedKeys] = useState(() => new Set());
  const [listDeleteConfirm, setListDeleteConfirm] = useState(false);
  const [dayPanelDeleteConfirm, setDayPanelDeleteConfirm] = useState(null); // { type, id, title }
  // Soft tint of the primary color used for the month-view grid lines.
  // Same lines as the Week grid (Jade, 2026-10-15: Month/Year lines were too faint).
  const monthGridLine = "var(--c-cal-line)";
  // Returns "#fff" or BLOCK_INK based on background luminance (WCAG formula)
  const getContrastText = (hexColor) => {
    if (!hexColor || !hexColor.startsWith("#")) return BLOCK_INK;
    const hex = hexColor.replace("#", "");
    if (hex.length < 6) return BLOCK_INK;
    const r = parseInt(hex.substring(0,2), 16) / 255;
    const g = parseInt(hex.substring(2,4), 16) / 255;
    const b = parseInt(hex.substring(4,6), 16) / 255;
    const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    return L > 0.35 ? BLOCK_INK : "#fff";
  };

  const [calYear, setCalYear] = useState(now().getFullYear());
  const [calMonth, setCalMonth] = useState(now().getMonth());
  // A widget's "+" (Add task/event) sets this sentinel (plus the desired type) then
  // switches to this tab; since the tab shell unmounts CalendarTab entirely on
  // tab-away, reading it here (and clearing it) opens the Add panel — pre-set to
  // the right type — the instant this mounts fresh.
  const openAddOnMount = (() => { try { return localStorage.getItem("clarity_calendar_openAdd") === "1"; } catch { return false; } })();
  const openAddType = (() => { try { return localStorage.getItem("clarity_calendar_openAddType") || "task"; } catch { return "task"; } })();
  if (openAddOnMount) { try { localStorage.removeItem("clarity_calendar_openAdd"); localStorage.removeItem("clarity_calendar_openAddType"); } catch {} }
  const [selectedDate, setSelectedDate] = useState(() => openAddOnMount ? todayStr() : null);
  const [calView, setCalView] = useState(null); // "task" | "event" | "reminder"

  // Feature 4 panel state: null | "day" | "add" | "detail"
  const [calPanel, setCalPanel] = useState(() => (openAddOnMount && plannerView !== "list") ? "add" : null);
  // The day panel (and an item's detail panel opened from it) normally shows
  // only on Month/Year. A tap on a holiday label in the Week header opens it
  // there too (Jade); cleared when the panels close.
  const [weekDayPanel, setWeekDayPanel] = useState(false);
  // Week view's all-day row: collapsed shows up to 3 lines per day (2 + "+N"
  // when a day has more); the chevron in the corner opens it to show them all.
  const [weekAllDayOpen, setWeekAllDayOpen] = useState(false);
  useEffect(() => { if (!calPanel) setWeekDayPanel(false); }, [calPanel]);
  // Quick-add bar (QuickAddBar.jsx): { date, type } while open. List view only (Jade, round 5):
  // the calendar views' "+" (and the widget's +, when the Planner opens on a calendar view) use the full form.
  const [quickBar, setQuickBar] = useState(() => (openAddOnMount && plannerView === "list") ? { date: todayStr(), type: openAddType } : null);
  // The day panel (Month/Year: a day's item list) pops in only when a day is
  // tapped — i.e. when it opens from nothing. Coming BACK to it from an item's
  // detail / edit panel re-mounts it, and that used to replay the pop (Jade,
  // 2026-10-06). Decided once per transition into "day", so later re-renders
  // can't re-add the class and replay it.
  const prevCalPanelRef = useRef(calPanel);
  const dayPanelPopsRef = useRef(false);
  if (calPanel !== prevCalPanelRef.current) {
    if (calPanel === "day") dayPanelPopsRef.current = prevCalPanelRef.current == null;
    prevCalPanelRef.current = calPanel;
  }
  const [dayDetailItem, setDayDetailItem] = useState(null);
  const [dayDetailType, setDayDetailType] = useState(null); // "task" | "event" | "reminder"
  const [dayDetailDate, setDayDetailDate] = useState(null); // which occurrence date this popup was opened for (recurring tasks track done per-day)
  const [quickAddTime, setQuickAddTime] = useState(""); // pre-filled time for quick-add panel
  const [quickAddTitle, setQuickAddTitle] = useState(""); // pre-filled title for quick-add panel
  // "Set as Task" is the default first-checked card when adding a new item (task/event
  // toggle) — pre-set to "event" only when a widget/shortcut explicitly asked for one.
  const [quickAddType, setQuickAddType] = useState(() => openAddOnMount ? openAddType : "task");

  // ── Sub-tab state ────────────────────────────────────────────────────────────
  // Persisted so switching to another app tab and back (which remounts
  // CalendarTab, since App.jsx only renders it while tab === "calendar")
  // returns to the same week/month/year view instead of resetting to Week.
  const [calSubTab, setCalSubTabState] = useState(() => load("clarity_calSubTab", "week"));
  const setCalSubTab = (v) => { setCalSubTabState(v); save("clarity_calSubTab", v); };
  const [todayBump, setTodayBump] = useState(0); // re-keys the Today icon so its tap animation replays

  // Week view's "lock" toggle — disables resizing task/event blocks so a
  // finger can scroll past one without accidentally grabbing its resize
  // handle. Persisted (like calSubTab above) so it survives a tab switch.
  const [weekResizeLocked, setWeekResizeLockedState] = useState(() => load("clarity_weekResizeLocked", false));
  // Both call sites pass an updater function (v => !v), so it has to be
  // resolved to the actual boolean here before persisting — passing it
  // straight to save() as before stringified the FUNCTION itself, which
  // JSON.stringify turns into undefined, corrupting storage to the literal
  // string "undefined" and making every reload fall back to false. That's
  // why the lock never appeared to remember its state.
  const setWeekResizeLocked = (v) => {
    setWeekResizeLockedState(prev => {
      const next = typeof v === "function" ? v(prev) : v;
      save("clarity_weekResizeLocked", next);
      return next;
    });
  };

  // The lock is a phone thing: desktop has no lock button, so it always
  // resizes freely, even when the (synced) setting was turned on on the phone.
  const resizeLocked = weekResizeLocked && !isDesktop;

  const [viewPanelOpen, setViewPanelOpen] = useState(false);
  const [viewPanelRect, setViewPanelRect] = useState(null); // trigger's screen rect, for the portalled menu (inline topbar only)
  const [catFilterOpen, setCatFilterOpen] = useState(false);
  const [catFilterRect, setCatFilterRect] = useState(null); // list title trigger's screen rect (its panel is portalled)
  // Measure the fixed (transparent) planner topbar and publish its height as a CSS
  // var so the content below can reserve exactly the right space (mobile only).
  const topbarRef = useRef(null);
  const listPanelContainerRef = useRef(null); // anchors the list/reflections pull-tab's drag range
  // Mobile list view: the topbar sits in the page flow and scrolls away with
  // the list instead of staying pinned over it, so it reserves no space here.
  const inlineTopbar = !isDesktop && plannerView === "list";
  // The view-switch fade starts at opacity 0, so it must not play when the
  // Planner TAB mounts (App.jsx's tabSettle already animates that, and on a
  // phone the heavy mount stalls the fade into a visible blank "blink").
  // Only a Calendar<->List flip after mount gets it — and only the LIST side
  // now: on the calendar side the same stall showed as a blank frame then a
  // fade, a "blink" switching List -> Calendar (Jade, 2026-10-06), so the
  // calendar view just appears.
  const plannerViewAtMountRef = useRef(plannerView);
  const viewSwitchedRef = useRef(false);
  if (plannerView !== plannerViewAtMountRef.current) viewSwitchedRef.current = true;
  const viewEnterClass = viewSwitchedRef.current ? "clarity-view-enter" : undefined;
  React.useLayoutEffect(() => {
    const el = topbarRef.current;
    if (!el) return;
    // Publish the real top-bar height (+ a small buffer) so the content below
    // reserves exactly enough space and the day-header can never tuck under the
    // fixed category pills — even when a late reflow grows the bar.
    // Only when it changes: a custom property on <html> re-styles the whole app,
    // and this runs on every resize (the keyboard opening and closing included).
    const apply = () => {
      const v = inlineTopbar ? "0px" : (el.offsetHeight + 6) + "px";
      const root = document.documentElement.style;
      if (root.getPropertyValue("--cal-topbar-h") !== v) root.setProperty("--cal-topbar-h", v);
    };
    apply();
    // Re-measure after the first paint and once web fonts settle: Quicksand loads
    // async and changes the pills' text metrics (hence the bar height), which is the
    // main reason the overlap only showed up "sometimes". Also re-measure on resize.
    const raf = requestAnimationFrame(apply);
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(apply).catch(() => {});
    }
    let ro;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(apply); ro.observe(el); }
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    return () => {
      cancelAnimationFrame(raf);
      ro && ro.disconnect();
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
    };
  }, [plannerView, calSubTab, isDesktop, categories.length, inlineTopbar]);
  // A phone browser lets the whole page scroll by its address bar's height (the
  // app's 100vh is the tall, bar-hidden viewport), and scrolling the List view
  // does that. The calendar's top bar is fixed while its grid isn't, so coming
  // back to the calendar with the page still scrolled slid the grid up under the
  // Week / Month / Year row (Jade, 2026-11-02, phone Chrome after List -> Calendar).
  // The calendar views never scroll the page, so put it back at the top.
  React.useLayoutEffect(() => {
    if (isDesktop || plannerView === "list") return;
    if (window.scrollY || document.documentElement.scrollTop || document.body.scrollTop) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [plannerView, isDesktop]);
  const [catPanelOpen, setCatPanelOpen] = useState(false);

  // ── List View state (Step 1) ─────────────────────────────────────────────
  const [listDoneToast, setListDoneToast] = useState(false);  // brief "moved to completed" toast
  const [listScope, setListScope] = useState("week");          // "week" | "calendar" | "year"
  const [listDragPreview, setListDragPreview] = useState(null); // ghost block while dragging list→week
  const listDragPreviewRef = useRef(null); // track last snap so setState is only called on changes
  const [showListAddPanel, setShowListAddPanel] = useState(false); // list view FAB modal
  const [listDetailItem, setListDetailItem] = useState(null);  // item tapped in list view
  const [listDetailType, setListDetailType] = useState(null);  // "task" | "event" | "reminder"
  const [listDetailDate, setListDetailDate] = useState(null);  // date string of tapped item

  // ── List Add Panel state ──────────────────────────────────────────────────
  // The list add panel now uses the shared <ItemPanel>, which owns its own form
  // state, so there's nothing to reset here. Kept as a no-op so existing callers
  // (closePanel) stay valid.
  const resetListAddForm = () => {};

  // ── List skip flow state — skipping/rescheduling is a plain action now, no
  // note-writing step.
  const [listSkipFlow, setListSkipFlow] = useState(null); // { item, type, step: "confirm", rescheduledTo }
  const [listSkipCalMonth, setListSkipCalMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  // The list-view reflections browser was removed; this stays hardwired false so
  // the list view's layout guards keep resolving to their normal-view branch.
  const listReflectMode = false;

  // ── List main tab (Tasks / Events) — each shows ONE sub-section at a time
  // (Pending/Finished for Tasks, Upcoming/Passed for Events), tracked by
  // listSubTab. Mobile switches between them with a horizontal swipe on the
  // list content itself (like paging between homescreens); desktop uses the
  // same Overdue/Pending/Finished pill row as mobile, right below Tasks/Events.
  const [listMainTab, setListMainTab] = useState("tasks"); // "tasks" | "events"
  const [listSubTab, setListSubTab] = useState("pending"); // "pending"|"finished" | "upcoming"|"passed"
  // Swipe carousel plumbing (mobile only) — the strip's transform is driven
  // imperatively via a plain ref during drag (touchmove firing on every pixel
  // would be too much to push through React state), then synced declaratively
  // by the effect below whenever listSubTab/listMainTab change some other way
  // (tapping a label, switching Tasks↔Events).
  //
  // The strip <div> only exists while the user is actually in mobile list
  // view (it's inside a conditional deep in the render tree), so a plain
  // `useRef` + an effect keyed on [listSubTab, listMainTab] would miss the
  // moment it first mounts — switching from calendar-grid to list view
  // doesn't touch either of those, so the effect would never re-run to
  // attach the listener to the newly-created node. A callback ref that bumps
  // a mount "tick" whenever the node changes fixes that: it forces the
  // attach-effects below to re-run exactly when there's a new node to attach to.
  const listSwipeStripElRef = useRef(null);
  const [listSwipeStripTick, setListSwipeStripTick] = useState(0);
  const listSwipeStripRef = useCallback((node) => {
    listSwipeStripElRef.current = node;
    if (node) setListSwipeStripTick(t => t + 1);
  }, []);
  const listSwipeTouch = useRef(null);
  const listSwipePrevMainTab = useRef(listMainTab);
  // Idx actually applied last, so we can tell "the page really changed" (user
  // tapped a pill / committed a swipe — animate) apart from "this effect just
  // re-ran for the same page" (starts null on every fresh mount, including a
  // remount from switching tabs away from and back to Planner — a mount
  // reliably fires this effect TWICE in a row for the identical target idx,
  // once early and once after the tick bump settles, with no guaranteed paint
  // between them — so a one-shot "just mounted" flag consumed by the first of
  // those two runs left the second one free to arm the animated transition
  // for what the browser still saw as one continuous, unpainted style change
  // from the strip's true initial position. Comparing against the last
  // idx actually positioned is idempotent no matter how many times the effect
  // fires for that same idx, so it can't be caught out by that: only an idx
  // that's different from last time counts as "changed" and gets animated.
  const listSwipeStripLastIdxRef = useRef(null);
  useEffect(() => {
    const el = listSwipeStripElRef.current;
    if (!el || isDesktop) return;
    const pages = listMainTab === "tasks" ? ["overdue", "pending", "finished"] : ["upcoming", "passed"];
    const idx = Math.max(0, pages.indexOf(listSubTab));
    const mainTabChanged = listSwipePrevMainTab.current !== listMainTab;
    listSwipePrevMainTab.current = listMainTab;
    const idxActuallyChanged = !mainTabChanged && listSwipeStripLastIdxRef.current !== null && listSwipeStripLastIdxRef.current !== idx;
    listSwipeStripLastIdxRef.current = idx;
    el.style.transition = idxActuallyChanged ? "transform 0.3s cubic-bezier(0.25,0.8,0.3,1)" : "none";
    // Percentage translate is relative to the STRIP's own box (pages.length*100%
    // wide), so one page-width over is -(100/pages.length)%. translate3d keeps
    // it on the GPU compositor so the drag stays smooth.
    const stepPct = 100 / pages.length;
    el.style.transform = `translate3d(${-idx * stepPct}%,0,0)`;
  }, [listSubTab, listMainTab, isDesktop, listSwipeStripTick]);

  const handleListSwipeStart = (e) => {
    const t = e.touches[0];
    // axis: null until the gesture commits to horizontal ("x") or vertical
    // ("y"). Only "x" drives the carousel; "y" is left entirely to native
    // page scroll. This lock is decided ONCE per gesture (see onMove) instead
    // of aborting on the first noisy diagonal frame, which is what made the
    // old handler feel like it "couldn't swipe" on a real finger.
    listSwipeTouch.current = { startX: t.clientX, startY: t.clientY, axis: null, dx: 0, width: e.currentTarget.offsetWidth || window.innerWidth };
  };
  const handleListSwipeEnd = () => {
    const st = listSwipeTouch.current;
    listSwipeTouch.current = null;
    if (!st || st.axis !== "x") return;
    const pages = listMainTab === "tasks" ? ["overdue", "pending", "finished"] : ["upcoming", "passed"];
    const idx = Math.max(0, pages.indexOf(listSubTab));
    // Commit on either a decent distance OR a quick flick.
    const threshold = Math.min(st.width * 0.2, 90);
    let newIdx = idx;
    if (st.dx <= -threshold && idx < pages.length - 1) newIdx = idx + 1;
    else if (st.dx >= threshold && idx > 0) newIdx = idx - 1;
    const stepPct = 100 / pages.length;
    if (listSwipeStripElRef.current) {
      listSwipeStripElRef.current.style.transition = "transform 0.3s cubic-bezier(0.25,0.8,0.3,1)";
      listSwipeStripElRef.current.style.transform = `translate3d(${-newIdx * stepPct}%,0,0)`;
    }
    if (newIdx !== idx) setListSubTab(pages[newIdx]);
  };
  // touchmove needs a real (non-passive) listener so preventDefault can stop
  // the page from also scrolling vertically while the user is mid-swipe —
  // React's JSX onTouchMove is passive by default and silently ignores it.
  useEffect(() => {
    if (isDesktop) return;
    const el = listSwipeStripElRef.current;
    if (!el) return;
    const onMove = (e) => {
      const st = listSwipeTouch.current;
      if (!st) return;
      const t = e.touches[0];
      const dx = t.clientX - st.startX;
      const dy = t.clientY - st.startY;
      // Decide the axis once, after the finger has clearly moved (10px), by
      // whichever delta dominates. Until then, do nothing (let the browser
      // keep the gesture live for either direction).
      if (st.axis === null) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        st.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      }
      if (st.axis !== "x") return; // vertical → native page scroll, hands off
      if (e.cancelable) e.preventDefault();
      const pages = listMainTab === "tasks" ? ["overdue", "pending", "finished"] : ["upcoming", "passed"];
      const idx = Math.max(0, pages.indexOf(listSubTab));
      let clamped = dx;
      if ((idx === 0 && dx > 0) || (idx === pages.length - 1 && dx < 0)) clamped = dx / 3; // rubber-band at the ends
      st.dx = clamped;
      el.style.transition = "none";
      const stepPct = 100 / pages.length;
      el.style.transform = `translate3d(calc(${-idx * stepPct}% + ${clamped}px),0,0)`;
    };
    el.addEventListener("touchmove", onMove, { passive: false });
    return () => el.removeEventListener("touchmove", onMove);
  }, [listMainTab, listSubTab, isDesktop, listSwipeStripTick]);

  // ── Recently Deleted (soft-delete trash, 30-day retention) ────────────────
  // The actual read/write lives in utils/trash.js (localStorage-only) so
  // App.jsx's Week-block popover — mounted at the app root, outside this
  // component — can append to the same trash without needing this
  // component's React state. This component still owns `deletedItems`
  // (what the panel renders), so it re-syncs from localStorage whenever
  // that panel opens, picking up anything appended from outside too.
  const THIRTY_DAYS_MS = TRASH_RETENTION_MS;
  const [deletedItems, setDeletedItems] = useState(() => {
    const raw = load(DELETED_ITEMS_KEY, []);
    const cutoff = Date.now() - THIRTY_DAYS_MS;
    return raw.filter(d => new Date(d.deletedAt).getTime() >= cutoff);
  });
  const [showRecentlyDeleted, setShowRecentlyDeleted] = useState(false);
  React.useEffect(() => {
    if (!showRecentlyDeleted) return;
    const cutoff = Date.now() - THIRTY_DAYS_MS;
    setDeletedItems(load(DELETED_ITEMS_KEY, []).filter(d => new Date(d.deletedAt).getTime() >= cutoff));
  }, [showRecentlyDeleted]); // eslint-disable-line react-hooks/exhaustive-deps
  // Snapshot items into the trash before they're removed from their live array.
  // entries: [{ id, type, item }]
  const softDeleteItems = (entries) => {
    if (!entries.length) return;
    setDeletedItems(softDeleteCalendarItems(entries));
  };
  const restoreDeletedItem = (entry) => {
    if (entry.type === "task") setTasks(p => [...p, entry.item]);
    else if (entry.type === "event") setCalEvents(p => [...p, entry.item]);
    else setCalReminders(p => [...p, entry.item]);
    setDeletedItems(prev => {
      const next = prev.filter(d => !(d.type === entry.type && d.id === entry.id && d.deletedAt === entry.deletedAt));
      save(DELETED_ITEMS_KEY, next);
      return next;
    });
  };
  const permanentlyDeleteItem = (entry) => {
    setDeletedItems(prev => {
      const next = prev.filter(d => !(d.type === entry.type && d.id === entry.id && d.deletedAt === entry.deletedAt));
      save(DELETED_ITEMS_KEY, next);
      return next;
    });
  };

  // Globally saved custom colors (persisted to localStorage)
  const [savedColors, setSavedColors] = useState(() => {
    try { return JSON.parse(localStorage.getItem("clarity_savedColors") || "[]"); } catch { return []; }
  });
  const addSavedColor = (hex) => {
    setSavedColors(prev => {
      const next = [hex, ...prev.filter(c => c !== hex)].slice(0, 16);
      localStorage.setItem("clarity_savedColors", JSON.stringify(next));
      return next;
    });
  };

  // ── Calendar filter state ─────────────────────────────────────────────────
  // visibleFilters: Set of category IDs + special keys "unlinked-tasks" | "unlinked-events" | "unlinked-reminders"
  // null means "all visible". Once user interacts it becomes a Set.
  // Restore the user's last filter choice across tab switches, app switches and
  // full relaunches: whatever they last picked (All, or a specific set of
  // categories) is what they see when they come back. The saved value is null
  // when "All" was on, or an array of category/bucket keys otherwise. Stale keys
  // (e.g. deleted categories) are pruned by the cleanup effect below.
  const [visibleFilters, setVisibleFilters] = useState(() => {
    const saved = load("clarity_calFilters", null);
    return Array.isArray(saved) ? new Set(saved) : null;
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The List view's own category filter (Jade, 2026-10-10): separate from the
  // calendar's — ticking categories in one view never changes the other.
  // Same shape and storage rules as visibleFilters.
  const [listFilters, setListFilters] = useState(() => {
    const saved = load("clarity_listFilters", null);
    return Array.isArray(saved) ? new Set(saved) : null;
  });
  useEffect(() => {
    save("clarity_listFilters", listFilters === null ? null : [...listFilters]);
  }, [listFilters]);

  // Persist filter state whenever it changes
  useEffect(() => {
    if (visibleFilters === null) {
      save("clarity_calFilters", null);
    } else {
      save("clarity_calFilters", [...visibleFilters]);
    }
  }, [visibleFilters]);

  // Clean up filter when categories are deleted — remove stale category IDs
  useEffect(() => {
    const validKeys = new Set([
      ...categories.map(c => c.id),
      "unlinked-events", "unlinked-tasks", "unlinked-reminders",
    ]);
    // Both filters (calendar + list) get the same pruning.
    for (const [cur, set] of [[visibleFilters, setVisibleFilters], [listFilters, setListFilters]]) {
      if (cur === null) continue;
      const cleaned = new Set([...cur].filter(k => validKeys.has(k)));
      // If cleaned size differs, update — but if everything valid is checked, reset to null
      if (cleaned.size !== cur.size) set(cleaned.size === validKeys.size ? null : cleaned);
    }
  }, [categories]); // eslint-disable-line

  // Build the full filter list: one entry per category + 3 unlinked buckets
  const buildFilterList = () => {
    const catEntries = categories.map(c => ({
      key: c.id,
      label: c.title,
      emoji: c.emoji || "🗂",
      color: c.color || "#d4789a",
      isCategory: true,
      cat: c,
    }));
    return [
      ...catEntries,
      { key: "unlinked-events",    label: "Other Events",    emoji: "📅", color: "#a07be8", isCategory: false, cat: null },
      { key: "unlinked-tasks",     label: "Other Tasks",     emoji: "✅", color: "#b8940a", isCategory: false, cat: null },
    ];
  };

  // Returns the effective Set of visible keys (all keys if null)
  const getActiveFilters = () => {
    if (visibleFilters === null) return null; // null = show everything
    return visibleFilters;
  };

  // Toggle a single filter key
  // `filters`/`setFilters` default to the calendar's; the List view passes its own.
  const toggleFilter = (key, filters = visibleFilters, setFilters = setVisibleFilters) => {
    const allKeys = new Set(buildFilterList().map(f => f.key));
    const current = filters === null ? new Set(allKeys) : new Set(filters);
    if (current.has(key)) {
      current.delete(key);
    } else {
      current.add(key);
    }
    // If all are checked again, reset to null (show everything cleanly)
    if (current.size === allKeys.size) {
      setFilters(null);
    } else {
      setFilters(current);
    }
  };

  const selectAllFilters  = () => setVisibleFilters(null);
  const prevFiltersRef = React.useRef(null); // remembers last non-null filter before "All" was pressed
  const prevListFiltersRef = React.useRef(null); // same, for the List view's filter

  // Check if a specific key is visible
  const isFilterVisible = (key) => {
    if (visibleFilters === null) return true;
    return visibleFilters.has(key);
  };

  // Get the filter key for an item
  const getItemFilterKey = (item, type) => {
    const linkedId = item.goalId || item.categoryId || null;
    if (linkedId && categories.find(c => c.id === linkedId)) return linkedId;
    if (type === "event")    return "unlinked-events";
    if (type === "task")     return "unlinked-tasks";
    if (type === "reminder") return "unlinked-reminders";
    return null;
  };

  // Filter an array of {item, type} pairs by current visibility
  const applyFilter = (pairs, filters = visibleFilters) => {
    if (filters === null) return pairs;
    return pairs.filter(({ item, type }) => {
      const key = getItemFilterKey(item, type);
      return key ? filters.has(key) : true;
    });
  };

  // ── Day View state ───────────────────────────────────────────────────────────
  const [dayViewDate, setDayViewDate] = useState(todayStr());
  const [weekAnchor, setWeekAnchor] = useState(todayStr());

  // ── Drag-and-drop state (week view) ──────────────────────────────────────
  const [dragItem, setDragItem]         = useState(null);  // { item, type, origDate }
  const [dragPos, setDragPos]           = useState(null);  // { x, y } screen coords
  const [dragSnap, setDragSnap]         = useState(null);  // { date, time, endTime } snapped target
  const [isDragging, setIsDragging]     = useState(false);
  const isDraggingRef                   = React.useRef(false); // mirrors isDragging; readable in window callbacks without stale closure
  const isResizingRef                   = React.useRef(false); // true while a resize handle drag is active
  // Resize state — kept in React so heightPx renders reactively during resize
  const [resizeItem, setResizeItem]     = useState(null); // { item, type, origEndMins, startY }
  const resizeItemRef                   = React.useRef(null); // mirrors resizeItem for window listeners
  const [resizeActive, setResizeActive] = useState(false); // true only while a resize drag is in progress — gates the passive:false move listeners (see below)
  const dragGridRef                     = React.useRef(null);
  const longPressTimer                  = React.useRef(null);
  const dragTouchId                     = React.useRef(null);
  const dragSnapRef                     = React.useRef(null); // mirrors dragSnap+wDays; readable in window mouseup without stale closure
  const dragItemRef                     = React.useRef(null); // mirrors dragItem; readable in window mouseup without stale closure
  const mouseDragStartRef               = React.useRef(null); // pending mouse press on a week block: starts its drag on first move
  const lastDragPointRef                = React.useRef(null); // last mouse position during a week drag (wheel / edge scrolling re-snaps from it)
  // The block hidden by hand (style.opacity = 0) when a week drag starts. React
  // only undoes that if it rendered the block with isBeingDragged in between; a
  // quick press-move-release ends the drag before that render, so the block
  // stayed invisible (but still clickable) until a reload (Jade, 2026-10-20).
  // Every drag end bumps dragEndTick and the layout effect below shows it again.
  const hiddenDragBlockRef              = React.useRef(null);
  const [dragEndTick, setDragEndTick]   = useState(0);
  React.useLayoutEffect(() => {
    const el = hiddenDragBlockRef.current;
    if (!el || isDraggingRef.current) return;
    hiddenDragBlockRef.current = null;
    el.style.opacity = "";
    el.style.transition = "box-shadow 0.15s";
  }, [dragEndTick]);
  const dayStartHourRef                 = React.useRef(dayStartHour); // read by the window mouseup drop (attached once)
  dayStartHourRef.current = dayStartHour;
  const weekItemRefsMap                 = React.useRef({}); // keyed by item.id — stores DOM refs for week timed-event blocks
  const lastTouchRef                    = React.useRef(0); // timestamp of last touch — suppress synthesized mouse events after touch
  const tapStartRef                     = React.useRef(null); // { x, y } of pointer-down on a block — used to distinguish tap vs swipe
  const tapMovedRef                     = React.useRef(false); // true once finger/mouse moved beyond tap threshold — swipe opens nothing

  // Week view draw-to-create state (must live in parent, not IIFE)
  const [weekDrawState, setWeekDrawState] = React.useState(null);
  const weekDrawActiveRef = React.useRef(false);
  weekDrawActiveRef.current = !!weekDrawState;
  // weekDrawState: { colIdx, startMins, endMins, startClientY } | null
  const weekDrawTouchId   = React.useRef(null);
  const weekMouseDrawRef  = React.useRef(null); // { colIdx, startMins }
  const weekScrollRef     = React.useRef(null);
  const weekScrollPosRef  = React.useRef(null); // remembers scrollTop across the pane unmounting behind the add/edit modal
  const yearScrollPosRef  = React.useRef(0);

  // ── Swipe-to-navigate on the calendar grid (mobile week/month/year) ──────
  // The only way to change period on mobile (there are no arrows). The
  // [prev, cur, next] panes are ALWAYS mounted edge to edge (no gap) inside a
  // frame that never moves, so a swipe only ever moves one pre-built,
  // GPU-composited strip — nothing mounts mid-gesture, which is what made the
  // first version lag behind the finger. Only the middle pane is interactive.
  // On release a rAF tween finishes the slide at the finger's speed, the anchor
  // advances, and a layout effect snaps the strip back to 0 in the same frame
  // the panes re-render one step over, so the hand-off never paints.
  // goToPrevWeek/nextMonth/etc. are declared later but only read from the
  // closure when a touch fires, after the body has run.
  const calStripRef = useRef(null);
  const calHostElRef = useRef(null);
  const calGutterRef = useRef(0);        // from the view currently rendered
  const [calStride, setCalStride] = useState(0); // px between neighbouring panes
  const calStrideRef = useRef(0);
  const calResizeObsRef = useRef(null);
  const calHostRef = useCallback((node) => {
    calResizeObsRef.current?.disconnect();
    calHostElRef.current = node;
    if (!node) return;
    const measure = () => {
      // 0 = hidden (display:none while the tab lingers after you leave);
      // re-rendering every pane at stride 0 would be wasted work.
      if (!node.clientWidth) return;
      const s = Math.max(0, node.clientWidth - calGutterRef.current);
      calStrideRef.current = s;
      setCalStride(prev => (prev === s ? prev : s));
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      calResizeObsRef.current = new ResizeObserver(measure);
      calResizeObsRef.current.observe(node);
    }
  }, []);
  // The swipe neighbours (prev/next pane + the drag-only label overlay) are
  // two extra full grids. Building all three in the same commit as the tab
  // switch roughly tripled Planner's open time (measured: ~0.5s -> ~1.1-1.5s
  // at 4x CPU throttle, regression introduced with the pre-mounted panes).
  // So the first commit builds only the live pane, and the neighbours mount
  // right after that first paint — long before a swipe normally starts, so
  // the swipe itself still finds them pre-built (mounting them at drag-lock
  // time made the swipe lag behind the finger; that's NOT what this does,
  // except as a fallback if a swipe starts inside that first split second).
  // Keyed on the view, so switching List->Calendar or Week/Month/Year inside
  // Planner gets the same live-pane-first treatment as opening the tab.
  const calSidesKey = `${plannerView}|${calSubTab}`;
  const [calSidesReadyKey, setCalSidesReadyKey] = useState(null);
  const calSidesReady = calSidesReadyKey === calSidesKey;
  const calSidesReadyRef = useRef(false);
  calSidesReadyRef.current = calSidesReady;
  const calSidesKeyRef = useRef(calSidesKey);
  calSidesKeyRef.current = calSidesKey;
  useEffect(() => {
    if (calSidesReady) return;
    let t = 0;
    const raf = requestAnimationFrame(() => {
      t = setTimeout(() => setCalSidesReadyKey(calSidesKey), 0);
    });
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [calSidesKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const calSubviewElRef = useRef(null);
  const calTouch = useRef(null);  // { startX, startY, axis, dx, lastX, lastT, vx, subTab, detach }
  const calTweenRef = useRef(0);
  const calAnimatingRef = useRef(false);
  const calResetPendingRef = useRef(false);

  const applyCalDrag = (dx) => {
    const strip = calStripRef.current;
    if (strip) strip.style.transform = `translate3d(${dx}px,0,0)`;
    // How far the swipe has got (0→1) — the Week all-day row eases its height
    // between the two weeks with it (see ALLDAY_CSS).
    //
    // Set on the few .wk-adrow elements only, and only when the incoming week's
    // row is a different height (calAdRowDragRef, set by the Week render). It
    // used to be set on the host on every move: a custom property there
    // re-styles the host's whole subtree (three full week grids) each frame —
    // the main reason the Week swipe lagged on phones (Jade, 2026-10-11).
    const rows = calAdRowsRef.current;
    if (rows.length) {
      const stride = calStrideRef.current || window.innerWidth;
      const p = String(Math.min(1, Math.abs(dx) / stride));
      for (let i = 0; i < rows.length; i++) rows[i].style.setProperty("--cal-drag-p", p);
    }
  };
  // Elements whose height follows the finger mid-swipe (see applyCalDrag).
  const calAdRowsRef = useRef([]);
  const calAdRowDragRef = useRef(false);
  // The nav's frosted blur is paused for the whole swipe (src/utils/navBlur.js):
  // re-blurring it every frame is what made swipes lag on gradient backgrounds.
  const calNavBlurReleaseRef = useRef(null);
  const setCalDraggingClass = (on) => {
    if (on && !calNavBlurReleaseRef.current) calNavBlurReleaseRef.current = holdNavBlur();
    if (!on && calNavBlurReleaseRef.current) { calNavBlurReleaseRef.current(); calNavBlurReleaseRef.current = null; }
    const host = calHostElRef.current;
    if (!host) return;
    host.classList.toggle("cal-dragging", on);
    if (on) calAdRowsRef.current = calAdRowDragRef.current ? Array.from(host.querySelectorAll(".wk-adrow")) : [];
    else calAdRowsRef.current = [];
  };
  // Keep every pane's vertical scroller (week grid, year list) at the live
  // pane's depth, so neighbours line up the moment they slide into view.
  const syncCalScroll = (src) => {
    calHostElRef.current?.querySelectorAll("[data-sync-scroll]").forEach(el => {
      if (el !== src && el.scrollTop !== src.scrollTop) el.scrollTop = src.scrollTop;
    });
  };

  const handleCalTouchEnd = () => {
    const st = calTouch.current;
    calTouch.current = null;
    if (!st) return;
    st.detach();
    if (st.axis !== "x") return;
    const stride = calStrideRef.current || window.innerWidth;
    // A quick flick commits even when short (fast finger, or a short gesture
    // over 30px); otherwise past ~20% of the width.
    const FLICK = 0.35; // px/ms
    const quick = performance.now() - st.t0 < 250 && Math.abs(st.dx) > 30;
    const far = Math.min(stride * 0.2, 90);
    let dir = 0;
    if (st.vx <= -FLICK || st.dx <= -far || (quick && st.dx < 0)) dir = 1;       // left → next
    else if (st.vx >= FLICK || st.dx >= far || (quick && st.dx > 0)) dir = -1;   // right → prev
    if ((dir === 1 && st.vx > FLICK / 2) || (dir === -1 && st.vx < -FLICK / 2)) dir = 0; // reversed at the end
    const from = st.dx, to = -dir * stride;
    const speed = Math.max(Math.abs(st.vx), 1.2); // carry the finger's momentum
    const DUR = Math.min(320, Math.max(160, Math.abs(to - from) / speed));
    const t0 = performance.now();
    calAnimatingRef.current = true;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / DUR);
      applyCalDrag(from + (to - from) * (1 - Math.pow(1 - k, 3))); // ease-out cubic
      if (k < 1) { calTweenRef.current = requestAnimationFrame(step); return; }
      calAnimatingRef.current = false;
      if (dir === 0) { applyCalDrag(0); setCalDraggingClass(false); return; }
      calResetPendingRef.current = true; // the layout effect below zeroes the strip
      calHostElRef.current?.classList.add("cal-landed");
      if (st.subTab === "week") { if (dir > 0) goToNextWeek(); else goToPrevWeek(); }
      else if (st.subTab === "year") setCalYear(y => y + dir);
      else { if (dir > 0) nextMonth(); else prevMonth(); }
    };
    calTweenRef.current = requestAnimationFrame(step);
  };
  const handleCalTouchMove = (e) => {
    const st = calTouch.current;
    if (!st) return;
    const t = e.touches[0];
    const dx = t.clientX - st.startX;
    const dy = t.clientY - st.startY;
    if (st.axis === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      // An event being dragged/resized or a block being drawn owns the finger.
      // (weekDrawTouchId is set on every touch and never cleared, so it can't
      // be used here — weekDrawState is the real "drawing" signal.)
      const busy = isDraggingRef.current || isResizingRef.current || weekDrawActiveRef.current || weekMouseDrawRef.current;
      st.axis = !busy && Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (st.axis !== "x") { handleCalTouchEnd(); return; } // vertical → native scroll
      clearTimeout(longPressTimer.current); // a swipe that began on an event must not turn into a drag
      weekDrawPendingRef.current = null;    // ...or a draw-to-create
      if (!calSidesReadyRef.current) { calSidesReadyRef.current = true; flushSync(() => setCalSidesReadyKey(calSidesKeyRef.current)); }
      setCalDraggingClass(true);
    }
    if (e.cancelable) e.preventDefault();
    const now_ = e.timeStamp || performance.now();
    const dt = now_ - st.lastT;
    if (dt > 0) st.vx = 0.7 * ((t.clientX - st.lastX) / dt) + 0.3 * st.vx;
    st.lastX = t.clientX; st.lastT = now_;
    st.dx = dx;
    // Which neighbour is coming in — the week all-day row grows to fit it.
    const host = calHostElRef.current;
    if (host) { host.classList.toggle("cal-dir-next", dx < 0); host.classList.toggle("cal-dir-prev", dx > 0); }
    applyCalDrag(dx);
  };
  // Listeners go on the touched element itself (non-passive, so preventDefault
  // can stop stray page scrolling); the pane holding it can re-render
  // underneath without the gesture losing its events.
  const handleCalTouchStart = (e) => {
    if (isDesktop || calTouch.current || calAnimatingRef.current || e.touches.length > 1) return;
    const t = e.touches[0];
    const target = e.target;
    const detach = () => {
      target.removeEventListener("touchmove", handleCalTouchMove);
      target.removeEventListener("touchend", handleCalTouchEnd);
      target.removeEventListener("touchcancel", handleCalTouchEnd);
    };
    calTouch.current = { startX: t.clientX, startY: t.clientY, axis: null, dx: 0, lastX: t.clientX, lastT: e.timeStamp || performance.now(), vx: 0, t0: performance.now(), subTab: calSubTab, detach };
    target.addEventListener("touchmove", handleCalTouchMove, { passive: false });
    target.addEventListener("touchend", handleCalTouchEnd);
    target.addEventListener("touchcancel", handleCalTouchEnd);
  };
  useEffect(() => () => { cancelAnimationFrame(calTweenRef.current); calResizeObsRef.current?.disconnect(); calNavBlurReleaseRef.current?.(); }, []);
  // Late-mounted neighbours must sit at the live pane's scroll depth.
  React.useLayoutEffect(() => {
    if (!calSidesReady) return;
    const live = calHostElRef.current?.querySelector(".cal-pane-live [data-sync-scroll]");
    if (live) syncCalScroll(live);
  }, [calSidesReady]); // eslint-disable-line react-hooks/exhaustive-deps
  // After a committed swipe the panes have just re-rendered one period over;
  // zero the strip before that frame paints so the hand-off is invisible.
  React.useLayoutEffect(() => {
    if (!calResetPendingRef.current) return;
    calResetPendingRef.current = false;
    applyCalDrag(0);
    setCalDraggingClass(false);
    calHostElRef.current?.classList.remove("cal-landed");
  }, [weekAnchor, calMonth, calYear]); // eslint-disable-line react-hooks/exhaustive-deps
  const calSwipeProps = isDesktop ? {} : { ref: calSubviewElRef, onTouchStart: handleCalTouchStart };
  // `renderPane(anchor, role)` renders one view for a week-anchor string, a
  // year number, or a {y,m} month object; role is "live" (the interactive
  // middle pane), "side" (a neighbour) or "overlay". Options:
  //   gutter     — px at the pane's left that must NOT slide (week's hour
  //                labels): neighbours overlap by this much, so the day columns
  //                still meet edge to edge, and `overlay` redraws it on top
  //                while dragging.
  //   frameStyle — card chrome drawn by the fixed host (panes drop their own).
  //   seam       — colour of the line where two panes meet, for grids whose
  //                last column has no right border (month, year).
  const renderCalCarousel = (renderPane, prev, cur, next, { gutter = 0, frameStyle = null, seam = null, overlay = null } = {}) => {
    const hostStyle = { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" };
    if (isDesktop) return <div key="desk" style={hostStyle}>{renderPane(cur, "live")}</div>;
    calGutterRef.current = gutter;
    const stride = calStride;
    return (
      <div key="strip" ref={calHostRef} className="cal-host" data-tour="planner-calendar-grid" style={{ ...hostStyle, overflow: "hidden", touchAction: "pan-y", "--cal-gutter": `${gutter}px`, ...frameStyle }}>
        <style>{CAL_STRIP_CSS}</style>
        <div className="cal-strip-clip" style={{ position: "absolute", inset: 0 }}>
        <div ref={calStripRef} style={{ position: "absolute", inset: 0, transform: "translate3d(0px,0,0)", willChange: "transform" }}>
          {[prev, cur, next].map((a, i) => (
            <div key={i} className={i === 1 ? "cal-strip-pane cal-pane-live" : "cal-strip-pane"}
              style={{
                position: "absolute", top: 0, bottom: 0, left: (i - 1) * stride, width: "100%",
                display: "flex", flexDirection: "column", zIndex: i === 1 ? 2 : 1,
                pointerEvents: i === 1 ? "auto" : "none",
                visibility: i === 1 || stride ? "visible" : "hidden",
              }}>
              {(i === 1 || calSidesReady) && renderPane(a, i === 1 ? "live" : "side")}
              {seam && <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: "var(--c-cal-line-w)", background: seam, zIndex: 3 }} />}
            </div>
          ))}
        </div>
        </div>
        {overlay && calSidesReady && (
          <div className="cal-strip-labels" style={{ position: "absolute", inset: 0, pointerEvents: "none", display: "flex", flexDirection: "column", zIndex: 4 }}>
            {overlay()}
          </div>
        )}
      </div>
    );
  };
  const weekDrawPendingRef = React.useRef(null); // pending touch: { startX, startY }
  const weekCommitPending = React.useRef(null); // stores { colIdx, startMins, endMins, clientY, wDays }
  const [weekCommitTick, setWeekCommitTick] = React.useState(0); // incremented to trigger commit useEffect
  const WEEK_DRAW_SNAP    = 15; // minutes snap grid

  const onWeekMouseMoveFn = React.useCallback((e) => {
    // Skip entirely if an event block is being dragged — the grid's onMouseMove handles that path
    if (isDraggingRef.current) return;
    if (!weekMouseDrawRef.current) return;
    const scrollEl = weekScrollRef.current;
    if (!scrollEl) return;
    const rect = scrollEl.getBoundingClientRect();
    const relY = e.clientY - rect.top + scrollEl.scrollTop;
    const endMins = Math.max(0, Math.min(23 * 60 + 45, Math.round((relY / 56) * 60 / 15) * 15));
    setWeekDrawState(prev => prev ? { ...prev, endMins } : null);
  }, []);

  const onWeekMouseUpFn = React.useCallback((e) => {
    // Skip entirely if an event block is being dragged — the grid's onMouseUp handles that path
    if (isDraggingRef.current) return;
    if (!weekMouseDrawRef.current) return;
    setWeekDrawState(prev => {
      if (prev && weekMouseDrawRef.current) {
        // Snapshot wDays from weekAnchor so the commit useEffect has it without render context
        const d = new Date(weekAnchor + "T00:00:00");
        const sunday = new Date(d);
        sunday.setDate(d.getDate() - dowOffset(d.getDay()));
        const snapDays = Array.from({ length: 7 }, (_, i) => {
          const dd = new Date(sunday);
          dd.setDate(sunday.getDate() + i);
          return fmt(dd);
        });
        weekCommitPending.current = {
          ...weekMouseDrawRef.current,
          endMins: prev.endMins,
          clientY: e.clientY,
          wDays: snapDays,
        };
        setWeekCommitTick(t => t + 1); // signal the useEffect to process the commit
      }
      weekMouseDrawRef.current = null;
      return null;
    });
  }, [weekAnchor]);

  React.useEffect(() => {
    window.addEventListener("mousemove", onWeekMouseMoveFn);
    window.addEventListener("mouseup", onWeekMouseUpFn);
    return () => {
      window.removeEventListener("mousemove", onWeekMouseMoveFn);
      window.removeEventListener("mouseup", onWeekMouseUpFn);
    };
  }, [onWeekMouseMoveFn, onWeekMouseUpFn]);

  // Fix: process week draw-to-create commits in a useEffect, not during render.
  // weekCommitTick is incremented by onWeekMouseUpFn to trigger this effect.
  React.useEffect(() => {
    if (!weekCommitPending.current) return;
    const { colIdx, startMins, endMins, clientY, wDays } = weekCommitPending.current;
    weekCommitPending.current = null;
    if (!wDays) return;
    const WSNAP = 15;
    const wMinsToStr = (m) =>
      `${String(Math.floor(Math.max(0, Math.min(1440, m)) / 60)).padStart(2, "0")}:${String(Math.max(0, Math.min(1440, m)) % 60).padStart(2, "0")}`;
    const s  = Math.min(startMins, endMins);
    const e2 = Math.min(1440, Math.max(startMins, endMins) + WSNAP);
    const draggedMins = Math.abs(endMins - startMins);
    const finalStart = s;
    const finalEnd   = draggedMins < WSNAP ? s + 60 : e2;
    setSelectedDate(wDays[colIdx]);
    setQuickAddTime(wMinsToStr(toAbsoluteMinutes(finalStart, dayStartHour)));
    setCalPanel("add");
    setWeekDrawState(null);
    weekMouseDrawRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekCommitTick, dayStartHour]);

  // Global drag controls — must be unconditional (before calView early returns) to satisfy React hooks rules.
  // Matches Google Calendar behavior:
  //   • mouseup anywhere  → commit (drop in place at current ghost position)
  //   • Escape            → cancel (snap back, no change saved)
  //   • touch: touchend   → commit (handled on the grid container directly)
  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && isDraggingRef.current) {
        e.preventDefault();
        clearTimeout(longPressTimer.current);
        setDragItem(null); setDragPos(null); setDragSnap(null);
        isDraggingRef.current = false; setIsDragging(false);
        dragItemRef.current = null; dragSnapRef.current = null;
        setDragEndTick(t => t + 1);
      }
    };
    // Window-level mouseup: commits the drag wherever the ghost currently is.
    // The grid's own onMouseUp also handles this for drops inside the grid;
    // this catches releases outside the grid (e.g. user dragged off the edge).
    const onMouseUp = (e) => {
      mouseDragStartRef.current = null; // a press released anywhere can't start a drag later
      lastDragPointRef.current = null;
      // Nor can its long-press timer. Released off the block (pressed on it, then
      // flicked away), the block's own mouseup never ran, so the timer fired after
      // the release and started a drag with no button held: the block vanished.
      if (!isDraggingRef.current && longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
      if (!isDraggingRef.current) return;
      if (isResizingRef.current) return; // resize has its own window mouseup handler
      // dragSnap and dragItem are read via refs set during mousemove — we fire
      // a synthetic commit by dispatching through the existing commitDrag path.
      // We store latest snap in a ref (dragSnapRef) so we can read it here.
      if (dragSnapRef.current && dragItemRef.current) {
        const { snap, wDaysList } = dragSnapRef.current;
        clearTimeout(longPressTimer.current);
        // commitDrag reads dragItem from state — but we call setters directly here
        // to avoid stale closure. Mirror commitDrag logic inline:
        const item = dragItemRef.current.item;
        const type = dragItemRef.current.type;
        const newDate = snap.date;
        // Same maths as the touch drop (commitDrag); this one used to skip the
        // "day starts at" shift and squash a block dropped at the end of the day.
        const { time: newTime, endTime: newEndTime } = weekDropTimes(snap.mins, item, dayStartHourRef.current);
        if (type === "event") {
          const isRecurringEv = (item.frequencyType && item.frequencyType !== "once") || (item.repeat && item.repeat !== "None");
          const origDate = dragItemRef.current.origDate;
          if (isRecurringEv && origDate) {
            setCalEventOccurrenceOverrides(prev => {
              const key = `${item.id}:${origDate}`;
              const { date: _d, ...prevBase } = prev[key] || {};
              return { ...prev, [key]: { ...prevBase, ...(newDate !== origDate ? { date: newDate } : {}), time: newTime, endTime: newEndTime, updatedAt: new Date().toISOString() } };
            });
          } else {
            setCalEvents(p => p.map(ev => ev.id === item.id ? { ...ev, date: newDate, time: newTime, endTime: newEndTime, updatedAt: new Date().toISOString() } : ev));
          }
        }
        else if (type === "reminder") setCalReminders(p => p.map(r => r.id === item.id ? { ...r, date: newDate, time: newTime, endTime: newEndTime } : r));
        else if (type === "task") {
          const origDate = dragItemRef.current.origDate;
          const isRecurring = item.frequencyType && item.frequencyType !== "once";
          if (isRecurring && origDate) {
            setTaskOccurrenceOverrides(prev => {
              const key = `${item.id}:${origDate}`;
              const { date: _d, ...prevBase } = prev[key] || {};
              return { ...prev, [key]: { ...prevBase, ...(newDate !== origDate ? { date: newDate } : {}), time: newTime, endTime: newEndTime } };
            });
          } else {
            setTasks(p => p.map(t => t.id === item.id ? { ...t, startDate: newDate, dueDate: newDate, time: newTime, endTime: newEndTime } : t));
          }
        }
      }
      setDragItem(null); setDragPos(null); setDragSnap(null);
      isDraggingRef.current = false; setIsDragging(false);
      dragItemRef.current = null; dragSnapRef.current = null;
      setDragEndTick(t => t + 1);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dragging a week block (mouse or finger): holding it near the top or bottom
  // edge of the grid (or past it) scrolls the week, like Google Calendar (Jade,
  // 2026-10-20; touch the same day: a finger can't scroll the grid mid-drag, so
  // this is the only way to move a block past what's on screen). A touch drag
  // keeps the grid's own scrolling locked; setting scrollTop still works on it.
  // The grid's onScroll re-snaps the block to the pointer as it scrolls.
  const weekBlockDrag = isDragging && !!dragItem;
  React.useEffect(() => {
    if (!weekBlockDrag) return;
    const EDGE = 48;       // px from the edge where scrolling starts
    const MAX_STEP = 18;   // px per frame at (or past) the edge
    const onMove = (e) => { lastDragPointRef.current = { x: e.clientX, y: e.clientY }; };
    const onTouch = (e) => {
      const t = Array.from(e.touches).find(x => x.identifier === dragTouchId.current) || e.touches[0];
      if (t) lastDragPointRef.current = { x: t.clientX, y: t.clientY };
    };
    let raf = 0;
    const tick = () => {
      const grid = weekScrollRef.current;
      const p = lastDragPointRef.current;
      if (grid && p) {
        const r = grid.getBoundingClientRect();
        let step = 0;
        if (p.y < r.top + EDGE) step = -Math.min(MAX_STEP, Math.ceil((r.top + EDGE - p.y) / EDGE * MAX_STEP));
        else if (p.y > r.bottom - EDGE) step = Math.min(MAX_STEP, Math.ceil((p.y - (r.bottom - EDGE)) / EDGE * MAX_STEP));
        if (step) grid.scrollTop += step;
      }
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onTouch, { passive: true });
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onTouch);
      cancelAnimationFrame(raf);
      lastDragPointRef.current = null;
    };
  }, [weekBlockDrag]);

  // Resize commit/cleanup handlers — always attached. These are cheap (they no-op
  // unless a resize is pending) and fire only on pointer-up / cancel / Escape, so
  // they don't affect scrolling. Keeping them always-on guarantees the resize
  // state is cleaned up even if the move-listener effect below hasn't attached yet.
  React.useEffect(() => {
    const onUp = (ev) => {
      if (!resizeItemRef.current) return;
      if (!isResizingRef.current) {
        // Finger lifted without significant movement — treat as a tap; handlePointerUp
        // already opened the popup (or will via its own path). Just clean up pending state.
        resizeItemRef.current = null;
        setResizeItem(null);
        setResizeActive(false);
        return;
      }
      const curY = ev.changedTouches ? ev.changedTouches[0].clientY : ev.clientY;
      const { item, type, origEndMins, startY, occDate } = resizeItemRef.current;
      const deltaMins = ((curY - startY) / 56) * 60;
      const SNAP = 15;
      const startMin = parseInt(item.time.split(":")[0]) * 60 + parseInt(item.time.split(":")[1] || 0);
      const rawEnd = origEndMins + deltaMins;
      const endMins = Math.max(startMin + SNAP, Math.round(rawEnd / SNAP) * SNAP);
      const pad2 = (n) => String(Math.floor(n)).padStart(2, "0");
      const newEndTime = `${pad2(endMins / 60)}:${pad2(endMins % 60)}`;
      if (type === "event") {
        const isRecurringEv = (item.frequencyType && item.frequencyType !== "once") || (item.repeat && item.repeat !== "None");
        if (isRecurringEv && occDate) {
          setCalEventOccurrenceOverrides(prev => {
            const key = `${item.id}:${occDate}`;
            return { ...prev, [key]: { ...prev[key], endTime: newEndTime, updatedAt: new Date().toISOString() } };
          });
        } else {
          setCalEvents(p => p.map(e => e.id === item.id ? { ...e, endTime: newEndTime, updatedAt: new Date().toISOString() } : e));
        }
      }
      if (type === "reminder") setCalReminders(p => p.map(r => r.id === item.id ? { ...r, endTime: newEndTime } : r));
      if (type === "task") {
        const isRecurring = item.frequencyType && item.frequencyType !== "once";
        if (isRecurring && occDate) {
          setTaskOccurrenceOverrides(prev => {
            const key = `${item.id}:${occDate}`;
            return { ...prev, [key]: { ...prev[key], endTime: newEndTime } };
          });
        } else {
          setTasks(p => p.map(t => t.id === item.id ? { ...t, endTime: newEndTime } : t));
        }
      }
      isResizingRef.current = false;
      resizeItemRef.current = null;
      setResizeItem(null);
      setResizeActive(false);
    };
    const onCancel = () => {
      if (!resizeItemRef.current) return;
      isResizingRef.current = false;
      resizeItemRef.current = null;
      setResizeItem(null);
      setResizeActive(false);
    };
    const onKey = (ev) => {
      if (ev.key === "Escape" && isResizingRef.current) { ev.preventDefault(); onCancel(); }
    };
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onCancel);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onCancel);
      window.removeEventListener("keydown", onKey);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resize MOVE tracking — attached ONLY while a resize is actually in progress.
  // A non-passive (passive:false) touchmove listener on window disables the
  // browser's fast-scroll/compositor path for the entire page, which added touch
  // latency on mobile even when nothing was being resized. Gating it behind
  // resizeActive restores smooth scrolling everywhere except during a real resize.
  React.useEffect(() => {
    if (!resizeActive) return;
    const onMove = (ev) => {
      if (!resizeItemRef.current) return;
      const curY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const deltaMins = ((curY - resizeItemRef.current.startY) / 56) * 60;
      // Only commit to resize mode after the finger has moved at least 5 min worth of pixels.
      // This lets accidental taps on the resize handle fall through to the tap→popup path.
      if (!isResizingRef.current && Math.abs(deltaMins) < 5) return;
      if (!isResizingRef.current) isResizingRef.current = true;
      ev.preventDefault();
      setResizeItem(prev => prev ? { ...prev, deltaMins } : prev);
    };
    window.addEventListener("mousemove", onMove, { passive: false });
    window.addEventListener("touchmove", onMove, { passive: false });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
    };
  }, [resizeActive]);

  // Feature 1: centered modal for "Show Details" from inline list
  const [inlineModalItem, setInlineModalItem] = useState(null);
  const [inlineModalType, setInlineModalType] = useState(null);
  const [inlineModalDate, setInlineModalDate] = useState(null); // occurrence date this modal was opened for

  // Week view block tap popover (Google Calendar style floating card)
  // weekBlockPop lifted to ClarityApp — setWeekBlockPop comes in as prop
  const closeWeekBlockPop = () => setWeekBlockPop(null);

  // Week view block hover state
  const [hoveredBlockId, setHoveredBlockId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null); // which block's ⋮ menu is open
  const hoverLeaveTimer = React.useRef(null);
  const isMenuInteraction = React.useRef(false); // true while user is interacting with ⋮ menu
  const onBlockMouseEnter = (id) => {
    if (hoverLeaveTimer.current) clearTimeout(hoverLeaveTimer.current);
    setHoveredBlockId(id);
  };
  const onBlockMouseLeave = () => {
    hoverLeaveTimer.current = setTimeout(() => {
      setHoveredBlockId(null);
      setMenuOpenId(null);
    }, 180);
  };
  const closeBlockMenu = () => { setMenuOpenId(null); setHoveredBlockId(null); };

  // Inline slot-tap popover (Google Calendar style)
  const [slotPopover, setSlotPopover] = useState(null); // { top, time, endTime, date }
  const [slotPopTitle, setSlotPopTitle] = useState("");
  const [slotPopType, setSlotPopType] = useState("event"); // "event" | "task"
  const closeSlotPopover = () => { setSlotPopover(null); setSlotPopTitle(""); setSlotPopType("event"); };
  const saveSlotPopover = () => {
    if (!slotPopTitle.trim() || !slotPopover) return;
    if (slotPopType === "event") {
      setCalEvents(p => [...p, { id: uid(), title: slotPopTitle.trim(), date: slotPopover.date, time: slotPopover.time, endTime: slotPopover.endTime, allDay: false, reminders: [], color: "", categoryId: "", location: "", description: "", repeat: "None", repeatDays: [], repeatDates: [], notes: "", updatedAt: new Date().toISOString() }]);
      // Ensure "unlinked-events" bucket is visible if a filter is active
      if (visibleFilters !== null && !visibleFilters.has("unlinked-events")) {
        setVisibleFilters(prev => prev ? new Set([...prev, "unlinked-events"]) : null);
      }
    } else if (slotPopType === "task") {
      setTasks(p => [...p, { id: uid(), title: slotPopTitle.trim(), startDate: slotPopover.date, dueDate: slotPopover.date, time: slotPopover.time, endTime: slotPopover.endTime, done: false, subtasks: [], notes: "", createdAt: new Date().toISOString() }]);
      // Ensure "unlinked-tasks" bucket is visible if a filter is active
      if (visibleFilters !== null && !visibleFilters.has("unlinked-tasks")) {
        setVisibleFilters(prev => prev ? new Set([...prev, "unlinked-tasks"]) : null);
      }
    }
    closeSlotPopover();
  };

  // Feature 5: centered edit modal state
  const [editModalItem, setEditModalItem] = useState(null);
  const [editModalType, setEditModalType] = useState(null);
  const [editModalForm, setEditModalForm] = useState(null);

  const openEditModal = (item, type) => {
    // Safety net: force a clean resize state before the modal opens. A tap that
    // began on a block's resize handle can leave resizeActive stuck true (which
    // keeps a non-passive touchmove listener on window and makes the modal scroll
    // janky). handlePointerUp now clears it directly, but clearing here too means
    // the edit form ALWAYS opens on the browser's fast-scroll path no matter how
    // it was reached.
    isResizingRef.current = false;
    resizeItemRef.current = null;
    setResizeItem(null);
    setResizeActive(false);
    setEditModalItem(item);
    setEditModalType(type);
    setEditModalForm({ ...item });
  };
  const closeEditModal = () => { setEditModalItem(null); setEditModalType(null); setEditModalForm(null); };

  const saveEditModal = () => {
    if (!editModalForm || !editModalForm.title?.trim()) return;
    if (editModalForm.color) addSavedColor(editModalForm.color);
    if (editModalType === "task") {
      setTasks(p => p.map(t => t.id === editModalItem.id ? { ...t, ...editModalForm } : t));
    } else if (editModalType === "event") {
      setCalEvents(p => p.map(e => e.id === editModalItem.id ? { ...e, ...editModalForm, updatedAt: new Date().toISOString() } : e));
    } else if (editModalType === "reminder") {
      setCalReminders(p => p.map(r => r.id === editModalItem.id ? { ...r, ...editModalForm } : r));
    }
    closeEditModal();
  };

  const deleteTaskFromCal = (id) => {
    const item = tasks.find(t => t.id === id);
    if (item) softDeleteItems([{ id, type: "task", item }]);
    setTasks(p => p.filter(t => t.id !== id));
  };
  const toggleTaskDoneFromCal = React.useCallback((id, occDate) => {
    const task = tasks.find(t => t.id === id);
    if (task) toggleTaskDoneOn(task, occDate, setTasks, setTaskOccurrenceOverrides);
  }, [tasks, setTasks, setTaskOccurrenceOverrides]);

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); };

  const fmtSelected = (ds) => {
    if (!ds) return "";
    const [y, m, d] = ds.split("-");
    const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    return `${MONTHS[parseInt(m) - 1]} ${parseInt(d)}, ${DAYS_FULL[dateObj.getDay()]}`;
  };

  const startOffset = dowOffset(new Date(calYear, calMonth, 1).getDay());
  const today = todayStr();

  // ── MONTH VIEW ITEM INDEX ─────────────────────────────────────────────────
  // Month view used to re-scan the full tasks/calEvents/calReminders arrays
  // for every one of the ~30-42 visible day cells on every render — including
  // renders triggered by state changes unrelated to calendar data (e.g. any
  // button tap elsewhere in the app, since this split-view grid stays mounted
  // alongside the list view on desktop). Memoizing the per-day breakdown here
  // means it's only recomputed when the task/event/reminder arrays or the
  // visible month actually change. Covers the previous and next months too:
  // the swipe carousel keeps both mounted, and without them their event bars
  // only appeared after a swipe landed.
  const monthItemsByDate = useMemo(() => {
    const map = new Map();
    for (let off = -1; off <= 1; off++) {
      const y = calYear + Math.floor((calMonth + off) / 12);
      const m = (calMonth + off + 12) % 12;
      const n = daysInMonth(y, m);
      for (let i = 0; i < n; i++) {
        const ds = `${y}-${String(m + 1).padStart(2,"0")}-${String(i + 1).padStart(2,"0")}`;
        map.set(ds, {
          dayTasks: tasks.filter(t => isTaskOccurringOn(t, ds)),
          dayEvents: calEvents.filter(e => isOccurringOn(e, ds)),
          dayReminders: calReminders.filter(r => isOccurringOn(r, ds)),
        });
      }
    }
    return map;
  }, [tasks, calEvents, calReminders, calYear, calMonth]);

  // ── YEAR VIEW DAY-DOT INDEX ────────────────────────────────────────────────
  // Same problem as above but worse: the year grid checked .some() over the
  // full arrays for all 365 days x 12 months on every render. Memoize per
  // visible year so it's only recomputed when the underlying data changes.
  const yearDayDots = useMemo(() => {
    const map = new Map();
    const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    const daysIn = [31, isLeap(calYear) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    for (let m = 0; m < 12; m++) {
      for (let d = 1; d <= daysIn[m]; d++) {
        const ds = `${calYear}-${String(m + 1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
        const hasTasks = tasks.some(t => isTaskOccurringOn(t, ds));
        const hasEvents = calEvents.some(e => isOccurringOn(e, ds));
        const hasRemind = calReminders.some(r => isOccurringOn(r, ds));
        map.set(ds, { hasTasks, hasEvents, hasRemind, any: hasTasks || hasEvents || hasRemind });
      }
    }
    return map;
  }, [tasks, calEvents, calReminders, calYear]);

  const getHighestPriority = (dateStr) => {
    const dayTasks = tasks.filter(t => isTaskOccurringOn(t, dateStr));
    if (dayTasks.some(t => t.priority === "high")) return "high";
    if (dayTasks.some(t => t.priority === "medium")) return "medium";
    if (dayTasks.length) return "low";
    return null;
  };

  // Event form state
  const emptyEvent = { title: "", date: "", time: "", endTime: "", allDay: false, reminders: [], location: "", description: "", color: "", categoryId: "", repeat: "None", repeatDays: [], repeatDates: [], notes: "" };

  // Resolve effective color for an event: category color takes priority, then manual color
  const eventColor = (ev) => {
    if (ev.categoryId) {
      const cat = categories.find(c => c.id === ev.categoryId);
      if (cat?.color) return cat.color;
    }
    return ev.color || DEFAULT_EVENT_COLOR;
  };

  // Resolve effective color for a task: category color takes priority (same
  // rule as eventColor above), falling back to any manually-picked color,
  // then the shared pastel-blue task default.
  const taskColor = (t) => {
    if (t.goalId) {
      const cat = categories.find(c => c.id === t.goalId);
      if (cat?.color) return cat.color;
    }
    return t.color || DEFAULT_TASK_COLOR;
  };
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [eventSaved, setEventSaved] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [editEventForm, setEditEventForm] = useState(null);
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showEditReminderModal, setShowEditReminderModal] = useState(false);

  const saveEvent = () => {
    if (!eventForm.title.trim()) return;
    setCalEvents(p => [...p, { id: uid(), ...eventForm, updatedAt: new Date().toISOString() }]);
    setEventForm(emptyEvent);
    setEventSaved(true);
    setTimeout(() => setEventSaved(false), 1500);
  };

  const openEditEvent = (ev) => {
    setEditingEventId(ev.id);
    setEditEventForm({ ...emptyEvent, ...ev });
    setShowEditEventModal(true);
    setCalPanel(null);
  };

  const saveEditEvent = () => {
    if (!editEventForm.title.trim()) return;
    if (editEventForm.color) addSavedColor(editEventForm.color);
    setCalEvents(p => p.map(e => e.id === editingEventId ? { ...e, ...editEventForm, updatedAt: new Date().toISOString() } : e));
    setEditingEventId(null);
    setEditEventForm(null);
    setShowEditEventModal(false);
  };

  const deleteEvent = (id) => {
    const item = calEvents.find(e => e.id === id);
    if (item) softDeleteItems([{ id, type: "event", item }]);
    setCalEvents(p => removeEventCascade(p, id));
  };

  // Reminder form state
  const emptyReminder = { title: "", date: "", time: "", endTime: "", priority: "medium", repeat: "None", repeatDays: [], repeatDates: [], notes: "", categoryId: "" };
  const [reminderForm, setReminderForm] = useState(emptyReminder);
  const [reminderSaved, setReminderSaved] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [editReminderForm, setEditReminderForm] = useState(null);

  const saveReminder = () => {
    if (!reminderForm.title.trim()) return;
    setCalReminders(p => [...p, { id: uid(), ...reminderForm }]);
    setReminderForm(emptyReminder);
    setReminderSaved(true);
    setTimeout(() => setReminderSaved(false), 1500);
  };

  const openEditReminder = (r) => {
    setEditingReminderId(r.id);
    setEditReminderForm({ ...emptyReminder, ...r });
    setShowEditReminderModal(true);
    setCalPanel(null);
  };

  const saveEditReminder = () => {
    if (!editReminderForm.title.trim()) return;
    if (editReminderForm.color) addSavedColor(editReminderForm.color);
    setCalReminders(p => p.map(r => r.id === editingReminderId ? { ...r, ...editReminderForm } : r));
    setEditingReminderId(null);
    setEditReminderForm(null);
    setShowEditReminderModal(false);
  };

  const deleteReminder = (id) => {
    const item = calReminders.find(r => r.id === id);
    if (item) softDeleteItems([{ id, type: "reminder", item }]);
    setCalReminders(p => p.filter(r => r.id !== id));
  };

  // Mobile "+" opens the quick-add bar; its "More" leads to the full form.
  // primeKeyboard: start opening the keyboard with the tap itself (keyboardNow.js).
  const openQuickAdd = (date, type = "task") => { primeKeyboard(); setQuickBar({ date: date || todayStr(), type }); };
  const closeQuickAdd = useCallback(() => setQuickBar(null), []);
  const closeAllPanels = () => { setCalPanel(null); setDayDetailItem(null); setDayDetailType(null); setQuickAddTime(""); setQuickAddTitle(""); setQuickAddType("task"); setWeekBlockPop(null); };

  // Listen for edit events from App-level WeekBlockPopoverPortal
  React.useEffect(() => {
    const onEditModal = (e) => { openEditModal(e.detail.item, e.detail.type); };
    const onEditEvent = (e) => { openEditEvent(e.detail); };
    const onEditReminder = (e) => { openEditReminder(e.detail); };
    const onSkipFlow = (e) => {
      const d2 = new Date();
      setListSkipCalMonth({ y: d2.getFullYear(), m: d2.getMonth() });
      setListSkipFlow({ item: e.detail.item, type: e.detail.type, origDate: e.detail.date || null, step: "confirm", rescheduledTo: null });
    };
    window.addEventListener("clarity:openEditModal", onEditModal);
    window.addEventListener("clarity:openEditEvent", onEditEvent);
    window.addEventListener("clarity:openEditReminder", onEditReminder);
    window.addEventListener("clarity:openSkipFlow", onSkipFlow);
    return () => {
      window.removeEventListener("clarity:openEditModal", onEditModal);
      window.removeEventListener("clarity:openEditEvent", onEditEvent);
      window.removeEventListener("clarity:openEditReminder", onEditReminder);
      window.removeEventListener("clarity:openSkipFlow", onSkipFlow);
    };
  }, []);

  // Date click → open Day Detail Panel
  const openDate = (ds) => { setSelectedDate(ds); setWeekAnchor(ds); setCalPanel("day"); };

  // A date tapped on the Planner · Month home-screen widget opens that day here.
  // App.jsx leaves the date in a one-shot flag AND fires an event: a fresh mount
  // reads the flag, an instance that's already (or still, lingering) mounted
  // hears the event. Whichever gets there first clears the flag.
  useEffect(() => {
    const take = () => {
      let ds = null;
      try { ds = localStorage.getItem(PLANNER_OPEN_DATE_FLAG); localStorage.removeItem(PLANNER_OPEN_DATE_FLAG); } catch {}
      if (!ds || !/^\d{4}-\d{2}-\d{2}$/.test(ds)) return;
      setCalYear(+ds.slice(0, 4)); setCalMonth(+ds.slice(5, 7) - 1);
      setDayViewDate(ds);
      openDate(ds);
    };
    take();
    window.addEventListener(PLANNER_OPEN_DATE_EVENT, take);
    return () => window.removeEventListener(PLANNER_OPEN_DATE_EVENT, take);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── List View data derivation ──────────────────────────────────────────────
  // Memoised so it only recomputes when the underlying items / filter / scope
  // change — NOT when the user merely switches the Upcoming/Finished/Missed
  // sub-tab (which previously re-flattened & re-sorted every item on each tap).
  // On desktop split-view, list scope tracks the calendar's active sub-tab.
  const effectiveListScope = (isDesktop && plannerView === "list") ? calSubTab : listScope;
  const listViewData = useMemo(() => {
    const today_ = todayStr();

    // ── Scope date range ──────────────────────────────────────────────────
    const scopeEnd = (() => {
      const d = new Date(today_ + "T00:00:00");
      if (effectiveListScope === "week") {
        const day = dowOffset(d.getDay());
        d.setDate(d.getDate() + (6 - day));
      } else if (effectiveListScope === "calendar") {
        d.setMonth(d.getMonth() + 1, 0);
      } else {
        d.setFullYear(d.getFullYear(), 11, 31);
      }
      return fmt(d);
    })();
    const scopeStart = (() => {
      const d = new Date(today_ + "T00:00:00");
      if (effectiveListScope === "week") {
        d.setDate(d.getDate() - dowOffset(d.getDay()));
      } else if (effectiveListScope === "calendar") {
        d.setDate(1);
      } else {
        d.setMonth(0, 1);
      }
      return fmt(d);
    })();

    const getItemDate = (item, type) => {
      if (type === "task") return item.startDate || item.dueDate || null;
      return item.date || null;
    };

    const allItems = [
      ...tasks.map(t => ({ item: t, type: "task", date: getItemDate(t, "task") })),
      ...calEvents.map(e => ({ item: e, type: "event", date: getItemDate(e, "event") })),
      ...calReminders.map(r => ({ item: r, type: "reminder", date: getItemDate(r, "reminder") })),
    ].filter(x => x.date);

    // The List view's own category filter (listFilters), set from its
    // "All Items ▼" title — separate from the calendar views' filter.
    const filteredItems = applyFilter(allItems, listFilters);

    // Horizon for expanding recurring occurrences into the Upcoming list. A
    // recurring item shows ONE card per occurrence from today up to its repeatEnd
    // (so a Wed/Thu/Fri task ending Sep 30 yields a card for every such day until
    // then). Endless recurrences are capped far out (not truly infinite) so a
    // malformed recurrence can't send the occurrence scan into an endless loop.
    const upcomingHorizon = (() => {
      const d = new Date(today_ + "T00:00:00");
      d.setFullYear(d.getFullYear() + 10);
      return fmt(d);
    })();
    const isRecurringItem = (item, type) => {
      const ft = item.frequencyType;
      if (ft && ft !== "once") return true;
      if (type !== "task" && item.repeat && item.repeat !== "None") return true;
      return false;
    };
    const upcomingSort = (a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      const typeOrder = { task: 0, reminder: 1, event: 2 };
      if (typeOrder[a.type] !== typeOrder[b.type]) return typeOrder[a.type] - typeOrder[b.type];
      const ta = a.item.time || "99:99";
      const tb = b.item.time || "99:99";
      return ta.localeCompare(tb);
    };

    // Occurrence expansion over an arbitrary [rangeStart, rangeEnd] window —
    // shared by Pending (tasks) and Upcoming/Passed (events), which each need a
    // different window instead of the old fixed "today onward" horizon.
    //
    // Recurring items are COLLAPSED to a single representative row per series
    // instead of one card per day: a daily task used to explode into ~365 cards
    // over the 1-year horizon (thousands of DOM nodes → jank/crash). Now the list
    // shows only the nearest occurrence — `mode: "first"` picks the next one from
    // today onward (Pending/Upcoming), `mode: "last"` picks the most recent past
    // one (Passed). The week/month grid still renders every occurrence (it's
    // already bounded to the visible range, so it stays cheap). An optional `skip`
    // predicate lets a caller reject occurrences (e.g. events whose time already
    // passed) while scanning for the representative.
    const expandOccurrences = (items, rangeStart, rangeEnd, mode = "first", skip = null) => {
      const shouldSkip = (it, ds) => (skip ? skip(it, ds) : false);
      const out = [];
      items.forEach(({ item, type }) => {
        if (!isRecurringItem(item, type)) {
          const base = type === "task" ? (item.startDate || item.dueDate) : item.date;
          if (base && base >= rangeStart && base <= rangeEnd && !shouldSkip(item, base)) out.push({ item, type, date: base });
          return;
        }
        const occurs = type === "task" ? isTaskOccurringOn : isOccurringOn;
        const endStr = item.repeatEnd && item.repeatEnd < rangeEnd ? item.repeatEnd : rangeEnd;
        const overridesMap = type === "task" ? taskOccurrenceOverrides : calEventOccurrenceOverrides;
        const movedAwayDates = new Set();
        const movedToEntries = [];
        Object.entries(overridesMap).forEach(([key, ov]) => {
          const ci = key.indexOf(":");
          if (key.slice(0, ci) !== String(item.id)) return;
          const origDate = key.slice(ci + 1);
          if (ov.date && ov.date !== origDate) {
            movedAwayDates.add(origDate);
            if (ov.date >= rangeStart && ov.date <= rangeEnd && !shouldSkip(item, ov.date)) movedToEntries.push({ date: ov.date, item: { ...item, time: ov.time ?? item.time, endTime: ov.endTime ?? item.endTime } });
          }
          // A single occurrence skipped (not rescheduled) via the Skip modal —
          // same exclusion as a moved-away date, just with nowhere it moved to.
          if (ov.skipped) movedAwayDates.add(origDate);
        });
        const effAt = (ds) => {
          const ov = overridesMap[`${item.id}:${ds}`];
          return ov ? { ...item, time: ov.time ?? item.time, endTime: ov.endTime ?? item.endTime } : item;
        };
        // Scan for the single representative occurrence (breaks at the first hit).
        if (mode === "last") {
          const startD = new Date(rangeStart + "T00:00:00");
          for (let d = new Date(endStr + "T00:00:00"); d >= startD; d.setDate(d.getDate() - 1)) {
            const ds = fmt(d);
            const eff = effAt(ds);
            if (occurs(item, ds) && !movedAwayDates.has(ds) && !shouldSkip(eff, ds)) { out.push({ item: eff, type, date: ds }); break; }
          }
        } else {
          // "first": the next occurrence from today onward (never a past date).
          const scanStart = rangeStart > today_ ? rangeStart : today_;
          const endD = new Date(endStr + "T00:00:00");
          for (let d = new Date(scanStart + "T00:00:00"); d <= endD; d.setDate(d.getDate() + 1)) {
            const ds = fmt(d);
            const eff = effAt(ds);
            if (occurs(item, ds) && !movedAwayDates.has(ds) && !shouldSkip(eff, ds)) { out.push({ item: eff, type, date: ds }); break; }
          }
        }
        // Manually-moved single occurrences (drag overrides) stay as their own rows.
        movedToEntries.forEach(({ date, item: mi }) => out.push({ item: mi, type, date }));
      });
      return out;
    };

    // A Google Task synced in from Google Calendar is stored as an event
    // (src/google/googleTask.js) but listed under Tasks (Jade, 2026-10-20). One
    // dated day each (Google sends a repeating one as separate instances); its
    // done mark is the event's own `passed`, set by its checkbox / Mark as done.
    const isGTask = (x) => x.type === "event" && isGoogleTaskEvent(x.item);
    const taskItems = filteredItems.filter(x => x.type === "task");
    const gTaskItems = filteredItems.filter(isGTask);
    const eventItems = filteredItems.filter(x => x.type !== "task" && !isGTask(x)); // events + reminders, merged

    // ── Tasks: Overdue vs Pending vs Finished. A one-time (non-recurring) task
    // whose due date is strictly before today and isn't done/skipped is Overdue
    // — it's carved OUT of Pending (exclusive bucket) until it's either marked
    // done (→ Finished) or its due date moves to today/later (→ back to Pending).
    // Recurring tasks are excluded from Overdue entirely — a recurring series
    // doesn't really have a single due date to be "overdue" against.
    const pendingTasksAll = expandOccurrences(
      taskItems.filter(x => !x.item.done && !x.item.skipped),
      scopeStart, upcomingHorizon, "first"
    ).sort(upcomingSort);

    // Overdue is judged against the task's actual dueDate, not `x.date` (which
    // for a task is startDate||dueDate — its timeblock/scheduled day). A task
    // scheduled in the past but due in the future isn't overdue yet, and a
    // task with no dueDate at all is never overdue no matter how old its
    // scheduled day is.
    const isTaskOverdue = (x) => !isRecurringItem(x.item, x.type) && !!x.item.dueDate && x.item.dueDate < today_;
    // Google Tasks: due on their own day; an unticked one from an earlier day
    // (within the list's range, like Finished) is overdue.
    const gTaskOpen = gTaskItems.filter(x => !x.item.passed);
    const gTaskOverdue = gTaskOpen.filter(x => x.date < today_ && x.date >= scopeStart);
    const gTaskPending = gTaskOpen.filter(x => x.date >= today_);
    const gTaskDone = gTaskItems.filter(x => !!x.item.passed && x.date >= scopeStart && x.date <= scopeEnd);

    const overdueTasks = [...pendingTasksAll.filter(isTaskOverdue), ...gTaskOverdue].sort(upcomingSort);
    const pendingTasks = [...pendingTasksAll.filter(x => !isTaskOverdue(x)), ...gTaskPending].sort(upcomingSort);

    const finishedTasks = [
      ...taskItems.filter(x => x.date >= scopeStart && x.date <= scopeEnd && (!!x.item.done || !!x.item.skipped)),
      ...gTaskDone,
    ].sort((a, b) => b.date.localeCompare(a.date));

    // ── Events (+ reminders): Upcoming vs Passed. Purely date/time-derived — the
    // stored `passed` flag is no longer read here at all; whether a card is
    // upcoming or passed is computed fresh from its date+time vs. right now.
    const nowMinutes = now().getHours() * 60 + now().getMinutes();
    const isPastDateTime = (dateStr, timeStr) => {
      if (dateStr < today_) return true;
      if (dateStr > today_) return false;
      if (!timeStr) return false; // all-day / no-time item today counts as still upcoming
      const [hh, mm] = timeStr.split(":").map(Number);
      return (hh * 60 + mm) < nowMinutes;
    };

    // An event occurrence can also be put in Passed by hand ("Mark as passed") or
    // held in Upcoming after its time ("Keep in Upcoming"); see utils/eventStatus.js
    // (Jade, 2026-11-02). Reminders follow their date and time only.
    const occPassed = (it, ds, type) => type === "event"
      ? isEventPassed(it, ds, now())
      : isPastDateTime(ds, it.time);
    const evType = new Map(eventItems.map(x => [x.item.id, x.type]));
    const typeOf = (it) => evType.get(it.id);

    // Upcoming: next occurrence per series, skipping any that's passed.
    const upcomingEvents = expandOccurrences(
      eventItems, today_, upcomingHorizon, "first",
      (it, ds) => occPassed(it, ds, typeOf(it))
    );
    // Passed: most recent passed occurrence per series.
    const passedEvents = expandOccurrences(
      eventItems, scopeStart, today_, "last",
      (it, ds) => !occPassed(it, ds, typeOf(it))
    );
    // Marked occurrences the two scans above can't reach: one kept in Upcoming whose
    // day is already behind, and one marked passed whose day is still ahead.
    const seenOcc = new Set([...upcomingEvents, ...passedEvents].map(x => `${x.item.id}:${x.date}`));
    eventItems.forEach(({ item, type }) => {
      if (type !== "event") return;
      const add = (list, ds) => {
        const k = `${item.id}:${ds}`;
        if (seenOcc.has(k)) return;
        seenOcc.add(k);
        const ov = calEventOccurrenceOverrides[k];
        list.push({ item: ov ? { ...item, time: ov.time ?? item.time, endTime: ov.endTime ?? item.endTime } : item, type, date: ds });
      };
      Object.keys(item.keepUpcomingOn || {}).forEach(ds => { if (ds < today_ && ds >= scopeStart) add(upcomingEvents, ds); });
      Object.keys(item.passedOn || {}).forEach(ds => { if (ds > today_) add(passedEvents, ds); });
      if (!isRecurringEvent(item) && item.passed && item.date > today_) add(passedEvents, item.date);
    });
    upcomingEvents.sort(upcomingSort);
    passedEvents.sort((a, b) => b.date.localeCompare(a.date));

    const groupByDate = (items) => {
      const tomorrow = (() => {
        const d = new Date(today_ + "T00:00:00");
        d.setDate(d.getDate() + 1);
        return fmt(d);
      })();
      const getLabel = (date) => {
        if (date === today_) return "Today";
        if (date === tomorrow) return "Tomorrow";
        const d = new Date(date + "T00:00:00");
        return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
      };
      const map = new Map();
      items.forEach(x => {
        if (!map.has(x.date)) map.set(x.date, { date: x.date, label: getLabel(x.date), items: [] });
        map.get(x.date).items.push(x);
      });
      return [...map.values()];
    };

    return { overdueTasks, pendingTasks, finishedTasks, upcomingEvents, passedEvents, groupByDate };
  }, [tasks, calEvents, calReminders, effectiveListScope, taskOccurrenceOverrides, calEventOccurrenceOverrides, mondayFirst, listFilters, categories]);

  // Render task/event/reminder full-page forms (existing behaviour)
  if (calView === "task") {
    return <TasksTab tasks={tasks} setTasks={setTasks} categories={categories} initialDate={selectedDate} onBack={() => setCalView(null)} />;
  }

  if (calView === "event") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <button onClick={() => setCalView(null)} style={S.btn}><ArrowLeft size={14} style={{marginRight:4}} />Back to Calendar</button>
        <Card title="📅 New Event">
          <input placeholder="Event title *" value={eventForm.title} onChange={e => setEventForm(p => ({ ...p, title: e.target.value }))} style={S.input} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <div style={{ flex: 1 }}><p style={S.label}>DATE</p><NativePickerInput type="date" value={eventForm.date} onChange={e => setEventForm(p => ({ ...p, date: e.target.value }))} style={S.input} /></div>
            <div style={{ flex: 1 }}><p style={S.label}>START TIME</p><NativePickerInput type="time" value={eventForm.time} onChange={e => setEventForm(p => ({ ...p, time: e.target.value }))} style={S.input} /></div>
            <div style={{ flex: 1 }}><p style={S.label}>END TIME</p><NativePickerInput type="time" value={eventForm.endTime || ""} onChange={e => setEventForm(p => ({ ...p, endTime: e.target.value }))} style={S.input} /></div>
          </div>
          <p style={{ ...S.label, marginTop: 10 }}>LOCATION / PLACE</p>
          <input placeholder="Add a place..." value={eventForm.location} onChange={e => setEventForm(p => ({ ...p, location: e.target.value }))} style={S.input} />
          <p style={{ ...S.label, marginTop: 10 }}>DESCRIPTION</p>
          <textarea rows={3} value={eventForm.description} onChange={e => setEventForm(p => ({ ...p, description: e.target.value }))} style={{ ...S.input, resize: "none", height: 72 }} />
          <p style={{ ...S.label, marginTop: 10 }}>CATEGORY (AUTO-COLOR)</p>
          <select value={eventForm.categoryId} onChange={e => setEventForm(p => ({ ...p, categoryId: e.target.value }))} style={{ ...S.input, cursor: "pointer" }}>
            <option value="">No category</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.title}</option>)}
          </select>
          {/* Color preview + optional manual override */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: eventColor(eventForm), border: "2px solid #e0c8e0", flexShrink: 0 }} />
            <p style={{ ...S.label, margin: 0, flex: 1 }}>
              {eventForm.categoryId ? "COLOR FROM CATEGORY (tap swatch to override)" : "COLOR LABEL"}
            </p>
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            {eventForm.categoryId && (
              <button onClick={() => setEventForm(p => ({ ...p, categoryId: "" }))}
                style={{ ...S.btn, fontSize: 11, padding: "3px 8px", color: "var(--c-text-soft)" }}>reset</button>
            )}
            {EVENT_COLORS.map(c => (
              <button key={c} onClick={() => setEventForm(p => ({ ...p, color: c, categoryId: "" }))}
                style={{ width: 26, height: 26, borderRadius: "50%", background: c, border: eventForm.color === c && !eventForm.categoryId ? "2.5px solid #d4789a" : "2px solid transparent", cursor: "pointer", padding: 0 }} />
            ))}
          </div>
          <p style={{ ...S.label, marginTop: 10 }}>REPEAT</p>
                    <select value={eventForm.repeat} onChange={e => setEventForm(p => ({ ...p, repeat: e.target.value, repeatDays: [], repeatDates: [] }))} style={{ ...S.input, cursor: "pointer" }}>
            {["None","Daily","Weekly","Monthly"].map(r => <option key={r}>{r}</option>)}
          </select>
          {eventForm.repeat === "Weekly" && (
            <div style={{ marginTop: 8 }}>
              <p style={{ ...S.label, marginBottom: 10 }}>REPEAT ON DAYS</p>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                  <button key={d} onClick={() => setEventForm(p => ({ ...p, repeatDays: p.repeatDays?.includes(i) ? p.repeatDays.filter(x => x !== i) : [...(p.repeatDays||[]), i] }))}
                    style={{ ...S.btn, fontSize: 12, minWidth: 36, background: eventForm.repeatDays?.includes(i) ? "var(--c-bg-card)" : "transparent", color: eventForm.repeatDays?.includes(i) ? "#d4789a" : "#a07090", borderColor: eventForm.repeatDays?.includes(i) ? "#d4789a" : "#e0d0e8", fontWeight: eventForm.repeatDays?.includes(i) ? 700 : 400 }}>{d}</button>
                ))}
              </div>
            </div>
          )}
          {eventForm.repeat === "Monthly" && (
            <div style={{ marginTop: 8 }}>
              <p style={{ ...S.label, marginBottom: 10 }}>REPEAT ON DATES</p>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <button key={d} onClick={() => setEventForm(p => ({ ...p, repeatDates: p.repeatDates?.includes(d) ? p.repeatDates.filter(x => x !== d) : [...(p.repeatDates||[]), d] }))}
                    style={{ ...S.btn, fontSize: 11, minWidth: 30, padding: "3px 0", background: eventForm.repeatDates?.includes(d) ? "var(--c-bg-card)" : "transparent", color: eventForm.repeatDates?.includes(d) ? "#d4789a" : "#a07090", borderColor: eventForm.repeatDates?.includes(d) ? "#d4789a" : "#e0d0e8", fontWeight: eventForm.repeatDates?.includes(d) ? 700 : 400 }}>{d}</button>
                ))}
              </div>
            </div>
          )}
          <p style={{ ...S.label, marginTop: 10 }}>ADDITIONAL NOTES</p>
          <textarea rows={2} value={eventForm.notes} onChange={e => setEventForm(p => ({ ...p, notes: e.target.value }))} style={{ ...S.input, resize: "none", height: 56 }} />
          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button onClick={saveEvent} className="clarity-add-btn" style={S.addBtn}><Save size={14} style={{marginRight:4}} />Save event</button>
            {eventSaved && <span style={{ fontSize: 13, color: "#7bc67e" }}>✓ Saved!</span>}
          </div>
        </Card>
      </div>
    );
  }


  // ── Day items for selected date ──────────────────────────────────────────────
  const activeDateForPanel = selectedDate || today;
  const panelTasksRaw     = tasks.filter(t => isTaskOccurringOn(t, activeDateForPanel));
  const panelEventsRaw    = calEvents.filter(e => isOccurringOn(e, activeDateForPanel));
  const panelRemindersRaw = calReminders.filter(r => isOccurringOn(r, activeDateForPanel));
  const panelFiltered     = applyFilter([
    ...panelTasksRaw.map(t => ({ item: t, type: "task" })),
    ...panelEventsRaw.map(e => ({ item: e, type: "event" })),
    ...panelRemindersRaw.map(r => ({ item: r, type: "reminder" })),
  ]);
  const panelTasks     = panelFiltered.filter(p => p.type === "task").map(p => p.item);
  const panelEvents    = panelFiltered.filter(p => p.type === "event").map(p => p.item);
  const panelReminders = panelFiltered.filter(p => p.type === "reminder").map(p => p.item);

  // ── Bottom sheet shared style ────────────────────────────────────────────────
  const sheetStyle = {
    position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 206, paddingBottom: "var(--sab, 0px)",
    display: "flex", alignItems: "flex-end", justifyContent: "center",
  };
  const sheetInner = {
    position: "relative", background: "var(--c-bg-card)",
    borderRadius: "18px 18px 0 0", padding: "14px 16px 28px",
    width: "100%", maxWidth: 520,
    boxShadow: "0 -4px 32px rgba(212,120,154,0.20)", zIndex: 207,
    maxHeight: "80vh", overflowY: "auto",
  };
  const sheetHdr = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 };
  const closeBtn = (fn) => (
    <button onClick={fn} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--c-text-soft)", lineHeight: 1 }}><X size={16} /></button>
  );
  const arrowBtn = (fn) => (
    <button onClick={fn}
      style={{ background: "var(--c-bg-card)", border: "none", borderRadius: 8, padding: "4px 10px", cursor: "pointer", color: "var(--c-text-accent)", fontSize: 15, fontWeight: 700 }}><ArrowRight size={15} /></button>
  );

  // ── Full Detail View — closeDetail/openSkipDetail/deleteDetailItem are the
  // small helpers ItemDetailPanel needs from this component (panel-close +
  // skip-flow + delete-confirm all live here since they touch state that's
  // local to the calendar tab). The actual detail content is the shared
  // ItemDetailPanel component (see ./ItemDetailPanel.jsx) — also used by the
  // list view's chevron-opened detail panel, so both show identical info.
  const closeDetail = () => {
    setDayDetailItem(null);
    setDayDetailType(null);
    // Year view now shares Month view's day-detail panel, so closing an
    // item's detail returns to that same day list here too.
    setCalPanel("day");
  };

  const openSkipDetail = (item, type, date) => {
    closeDetail();
    const d2 = new Date();
    setListSkipCalMonth({ y: d2.getFullYear(), m: d2.getMonth() });
    setListSkipFlow({ item, type, origDate: date || null, step: "confirm", rescheduledTo: null });
  };

  const deleteDetailItem = (item, type) => {
    // The details panels show their own notice for this; any other route here gets it as an alert.
    if (type === "event" && isGoogleTaskEvent(item)) {
      window.alert("This task comes from Google Tasks, and Clarity can't delete it there. Delete it in Google Tasks and it will leave Clarity on the next sync.");
      return;
    }
    setDayPanelDeleteConfirm({ type, id: item.id, title: item.title || "this item", item });
  };


  // Build week strip (7 days starting from Sunday of selected week)
  const buildWeekDays = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    const sunday = new Date(d);
    sunday.setDate(d.getDate() - dowOffset(d.getDay()));
    return Array.from({ length: 7 }, (_, i) => {
      const dd = new Date(sunday);
      dd.setDate(sunday.getDate() + i);
      return fmt(dd);
    });
  };
  const weekDays = buildWeekDays(weekAnchor);

  // ── Drag helpers ─────────────────────────────────────────────────────────
  const DRAG_PX_PER_HOUR = 64;
  const DRAG_LABEL_W = 44;
  const SNAP_MINS = 15;

  const snapToGrid = (mins) => Math.round(mins / SNAP_MINS) * SNAP_MINS;

  const minsToTime = (mins) => {
    const m = Math.max(0, Math.min(1439, mins));
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  };

  // Given a touch/mouse clientX/Y, compute which date column + snapped time
  const posToSnap = (clientX, clientY, wDaysList) => {
    const grid = dragGridRef.current;
    if (!grid) return null;
    const rect = grid.getBoundingClientRect();
    const relY = clientY - rect.top + grid.scrollTop;
    const relX = clientX - rect.left;
    const colW = (rect.width - DRAG_LABEL_W) / 7;
    const colIdx = Math.min(6, Math.max(0, Math.floor((relX - DRAG_LABEL_W) / colW)));
    const rawMins = (relY / DRAG_PX_PER_HOUR) * 60;
    // Kept inside the day so the ghost block never hangs past the bottom row (or above the top).
    const snappedMins = clampGridStart(snapToGrid(rawMins), dragItemRef.current?.item);
    return { date: wDaysList[colIdx], colIdx, mins: snappedMins };
  };

  const commitDrag = (snap, wDaysList) => {
    if (!dragItem || !snap) return;
    const { item, type, origDate } = dragItem;
    // snap.mins is grid-relative (from posToSnap); weekDropTimes turns it into a
    // wall-clock start/end that keeps the block's length (src/utils/weekDrop.js).
    const { time: newTime, endTime: newEndTime } = weekDropTimes(snap.mins, item, dayStartHour);
    const newDate    = snap.date;
    if (type === "event") {
      const isRecurringEv = (item.frequencyType && item.frequencyType !== "once") || (item.repeat && item.repeat !== "None");
      if (isRecurringEv && origDate) {
        setCalEventOccurrenceOverrides(prev => {
          const key = `${item.id}:${origDate}`;
          const { date: _d, ...prevBase } = prev[key] || {};
          return { ...prev, [key]: { ...prevBase, ...(newDate !== origDate ? { date: newDate } : {}), time: newTime, endTime: newEndTime, updatedAt: new Date().toISOString() } };
        });
      } else {
        setCalEvents(p => p.map(e => e.id === item.id ? { ...e, date: newDate, time: newTime, endTime: newEndTime, updatedAt: new Date().toISOString() } : e));
      }
    } else if (type === "reminder") {
      setCalReminders(p => p.map(r => r.id === item.id ? { ...r, date: newDate, time: newTime, endTime: newEndTime } : r));
    } else if (type === "task") {
      const isRecurring = item.frequencyType && item.frequencyType !== "once";
      if (isRecurring && origDate) {
        setTaskOccurrenceOverrides(prev => {
          const key = `${item.id}:${origDate}`;
          const { date: _d, ...prevBase } = prev[key] || {};
          return { ...prev, [key]: { ...prevBase, ...(newDate !== origDate ? { date: newDate } : {}), time: newTime, endTime: newEndTime } };
        });
      } else {
        setTasks(p => p.map(t => t.id === item.id ? { ...t, startDate: newDate, dueDate: newDate, time: newTime, endTime: newEndTime } : t));
      }
    }
    setDragItem(null); setDragPos(null); setDragSnap(null); isDraggingRef.current = false; setIsDragging(false);
    dragItemRef.current = null; dragSnapRef.current = null;
    setDragEndTick(t => t + 1);
  };

  const cancelDrag = () => {
    clearTimeout(longPressTimer.current);
    setDragItem(null); setDragPos(null); setDragSnap(null); isDraggingRef.current = false; setIsDragging(false);
    dragItemRef.current = null; dragSnapRef.current = null;
    setDragEndTick(t => t + 1);
  };

  // Resize helper — called when dragging the bottom handle
  const commitResize = (item, type, newEndMins, occDate) => {
    const startMin = parseInt(item.time.split(":")[0]) * 60 + parseInt(item.time.split(":")[1] || 0);
    const endMins  = Math.max(startMin + SNAP_MINS, snapToGrid(newEndMins));
    const newEndTime = minsToTime(endMins);
    if (type === "event") {
      const isRecurringEv = (item.frequencyType && item.frequencyType !== "once") || (item.repeat && item.repeat !== "None");
      if (isRecurringEv && occDate) {
        setCalEventOccurrenceOverrides(prev => {
          const key = `${item.id}:${occDate}`;
          return { ...prev, [key]: { ...prev[key], endTime: newEndTime, updatedAt: new Date().toISOString() } };
        });
      } else {
        setCalEvents(p => p.map(e => e.id === item.id ? { ...e, endTime: newEndTime, updatedAt: new Date().toISOString() } : e));
      }
    }
    if (type === "reminder") setCalReminders(p => p.map(r => r.id === item.id ? { ...r, endTime: newEndTime } : r));
    if (type === "task") {
      const isRecurring = item.frequencyType && item.frequencyType !== "once";
      if (isRecurring && occDate) {
        setTaskOccurrenceOverrides(prev => {
          const key = `${item.id}:${occDate}`;
          return { ...prev, [key]: { ...prev[key], endTime: newEndTime } };
        });
      } else {
        setTasks(p => p.map(t => t.id === item.id ? { ...t, endTime: newEndTime } : t));
      }
    }
  };

  const goToPrevWeek = () => {
    const d = new Date(weekAnchor + "T00:00:00");
    d.setDate(d.getDate() - 7);
    setWeekAnchor(fmt(d));
  };
  const goToNextWeek = () => {
    const d = new Date(weekAnchor + "T00:00:00");
    d.setDate(d.getDate() + 7);
    setWeekAnchor(fmt(d));
  };

  // ── Google-Calendar-style Day View ────────────────────────────────────────
  const GCalDayView = () => {
    const HOURS = Array.from({ length: 24 }, (_, i) => i);
    const PX_PER_HOUR = 64;
    const LABEL_W = 52;
    const scrollRef = React.useRef(null);

    // h===0 is only ever "blank" because it's the topmost row (would overlap
    // the header above it) — that's a layout concern, not a fact about
    // midnight, so it's handled at the call site instead of in here. This
    // always formats the real hour correctly, including "12 AM" wherever
    // midnight lands once the grid start is rotated.
    const fmtHour = (h) => {
      if (h < 12) return `${h === 0 ? 12 : h} AM`;
      if (h === 12) return "12 PM";
      return `${h - 12} PM`;
    };
    const parseTime = (t) => {
      if (!t) return null;
      const [h, m] = t.split(":").map(Number);
      return h * 60 + (m || 0);
    };
    const minsToStr = (m) => `${String(Math.floor(m / 60)).padStart(2,"0")}:${String(m % 60).padStart(2,"0")}`;

    // Scroll to current time on mount — row 0 already IS the chosen day
    // start, so a non-today day just opens at the top rather than jumping
    // to a hardcoded "7 AM" that no longer means anything fixed once the
    // grid start is customizable.
    React.useEffect(() => {
      if (!scrollRef.current) return;
      const nowObj2 = now();
      const nowGridMins = toGridMinutes(nowObj2.getHours() * 60 + nowObj2.getMinutes(), dayStartHour);
      const scrollTo = isToday
        ? Math.max(0, (nowGridMins / 60 - 1) * PX_PER_HOUR)
        : 0;
      scrollRef.current.scrollTop = scrollTo;
    }, [dayViewDate]);

    const nowObj = now();
    const nowMins = toGridMinutes(nowObj.getHours() * 60 + nowObj.getMinutes(), dayStartHour);
    const nowTop = (nowMins / 60) * PX_PER_HOUR;
    const isToday = dayViewDate === todayStr();

    const timedEvents    = applyFilter(calEvents.filter(e => isOccurringOn(e, dayViewDate) && e.time).map(e => ({ item: e, type: "event" })));
    const timedReminders = applyFilter(calReminders.filter(r => isOccurringOn(r, dayViewDate) && r.time).map(r => ({ item: r, type: "reminder" })));
    const allDayItems    = applyFilter([
      ...tasks.filter(t => isTaskOccurringOn(t, dayViewDate)).map(t => ({ item: t, type: "task" })),
      ...calEvents.filter(e => isOccurringOn(e, dayViewDate) && !e.time).map(e => ({ item: e, type: "event" })),
      ...calReminders.filter(r => isOccurringOn(r, dayViewDate) && !r.time).map(r => ({ item: r, type: "reminder" })),
    ]);
    const timedItems = [...timedEvents, ...timedReminders];

    const itemBlockColor = (item, type) => {
      if (type === "event") return eventColor(item);
      if (type === "reminder") {
        const cat = item.categoryId ? categories.find(c => c.id === item.categoryId) : null;
        return cat?.color || "#5bbfb5";
      }
      return taskColor(item);
    };

    // Navigate days
    const goDay = (delta) => {
      const d = new Date(dayViewDate + "T00:00:00");
      d.setDate(d.getDate() + delta);
      const ds = fmt(d);
      setDayViewDate(ds); setWeekAnchor(ds); setSelectedDate(ds);
    };

    const [y, mo, d2] = dayViewDate.split("-");
    const dateObj2 = new Date(parseInt(y), parseInt(mo) - 1, parseInt(d2));
    const dateLabel = `${DAYS_FULL[dateObj2.getDay()]}, ${MONTHS[parseInt(mo) - 1]} ${parseInt(d2)}`;

    // ── Drag-to-draw state (local to GCalDayView) ──
    const [drawState, setDrawState] = React.useState(null);
    // Reset draw state when the selected day changes (no remount needed)
    React.useEffect(() => { setDrawState(null); }, [dayViewDate]);
    // drawState: { startMins, endMins, startClientY } | null
    const drawTouchId = React.useRef(null);
    const SNAP = 15; // minutes
    const snapMins = (raw) => Math.round(raw / SNAP) * SNAP;
    const clientYToMins = (clientY, scrollEl) => {
      if (!scrollEl) return 0;
      const rect = scrollEl.getBoundingClientRect();
      const relY = clientY - rect.top + scrollEl.scrollTop;
      return Math.max(0, Math.min(23 * 60 + 59, snapMins((relY / PX_PER_HOUR) * 60)));
    };

    const gridRef = React.useRef(null);

    // Ghost block derived values
    const ghostTop    = drawState ? (Math.min(drawState.startMins, drawState.endMins) / 60) * PX_PER_HOUR : 0;
    const ghostHeight = drawState ? Math.max(PX_PER_HOUR / 4, (Math.abs(drawState.endMins - drawState.startMins) / 60) * PX_PER_HOUR) : 0;
    // drawState's mins are grid-relative — convert to real wall-clock time
    // for these display labels (and at commit, below).
    const ghostStart  = drawState ? minsToStr(toAbsoluteMinutes(Math.min(drawState.startMins, drawState.endMins), dayStartHour)) : "";
    const ghostEnd    = drawState ? minsToStr(toAbsoluteMinutes(Math.min(1440, Math.max(drawState.startMins, drawState.endMins) + SNAP), dayStartHour)) : "";

    // Open popover after draw ends
    const commitDraw = (startMins, endMins, anchorClientY) => {
      const s = Math.min(startMins, endMins);
      const e2 = Math.max(startMins, endMins) + SNAP;
      setSelectedDate(dayViewDate);
      setQuickAddTime(minsToStr(toAbsoluteMinutes(s, dayStartHour)));
      setCalPanel("add");
      setDrawState(null);
    };

    // Touch handlers for draw gesture
    const onTouchStart = (e) => {
      // Only draw on bare grid — not on event blocks
      if (!e.target.dataset.slot) return;
      if (slotPopover) { closeSlotPopover(); return; }
      const touch = e.touches[0];
      drawTouchId.current = touch.identifier;
      const startMins = clientYToMins(touch.clientY, scrollRef.current);
      setDrawState({ startMins, endMins: startMins + SNAP, startClientY: touch.clientY });
      e.preventDefault(); // prevent scroll while drawing
    };

    const onTouchMove = (e) => {
      if (drawState === null) return;
      const touch = Array.from(e.touches).find(t => t.identifier === drawTouchId.current);
      if (!touch) return;
      const endMins = clientYToMins(touch.clientY, scrollRef.current);
      setDrawState(prev => ({ ...prev, endMins }));
      e.preventDefault();
    };

    const onTouchEnd = (e) => {
      if (drawState === null) return;
      const touch = Array.from(e.changedTouches).find(t => t.identifier === drawTouchId.current);
      const clientY = touch ? touch.clientY : drawState.startClientY;
      const draggedMins = Math.abs(drawState.endMins - drawState.startMins);
      if (draggedMins < SNAP) {
        // treat as tap → default 60 min block
        const s = drawState.startMins;
        commitDraw(s, s + 60, clientY);
      } else {
        commitDraw(drawState.startMins, drawState.endMins, clientY);
      }
      drawTouchId.current = null;
    };

    // Refs map for timed-event blocks — keyed by item.id.
    // Allows imperative opacity hide the instant drag fires (before React re-renders),
    // eliminating the white-flash seen when relying on setState alone.
    const dayItemRefsMap = React.useRef({});

    // Mouse handlers (desktop)
    const mouseDrawRef = React.useRef(null); // { startMins, startY }
    const onMouseDown = (e) => {
      if (!e.target.dataset.slot) return;
      if (slotPopover) { closeSlotPopover(); return; }
      const startMins = clientYToMins(e.clientY, scrollRef.current);
      mouseDrawRef.current = { startMins, startY: e.clientY };
      setDrawState({ startMins, endMins: startMins + SNAP, startClientY: e.clientY });
      e.preventDefault();
    };
    const onMouseMove = React.useCallback((e) => {
      if (!mouseDrawRef.current) return;
      const endMins = clientYToMins(e.clientY, scrollRef.current);
      setDrawState(prev => prev ? { ...prev, endMins } : null);
    }, []);
    const onMouseUp = React.useCallback((e) => {
      if (!mouseDrawRef.current) return;
      const draggedMins = drawState ? Math.abs(drawState.endMins - drawState.startMins) : 0;
      const s = mouseDrawRef.current.startMins;
      mouseDrawRef.current = null;
      if (draggedMins < SNAP) {
        commitDraw(s, s + 60, e.clientY);
      } else if (drawState) {
        commitDraw(drawState.startMins, drawState.endMins, e.clientY);
      }
    }, [drawState]);

    React.useEffect(() => {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
      return () => { window.removeEventListener("mousemove", onMouseMove); window.removeEventListener("mouseup", onMouseUp); };
    }, [onMouseMove, onMouseUp]);

    return (
      <div className="cal-subview" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)", overflow: "hidden", fontFamily: "'Quicksand','Nunito',sans-serif" }}>

        {/* ── Header: date nav + day label ── */}
        <div style={{ flexShrink: 0, padding: "0 0 6px" }}>
          {/* Week strip */}
          <div style={{ background: "var(--c-bg-card)", borderRadius: 16, padding: "10px 8px", marginBottom: 8, border: "1.5px solid #f5d8ec" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: "var(--c-text-accent)", fontWeight: 700 }}>
                {MONTHS[new Date(weekDays[0] + "T00:00:00").getMonth()].slice(0,3)} {new Date(weekDays[0] + "T00:00:00").getDate()} – {MONTHS[new Date(weekDays[6] + "T00:00:00").getMonth()].slice(0,3)} {new Date(weekDays[6] + "T00:00:00").getDate()}
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
              {weekDays.map(ds => {
                const [, wm, wd] = ds.split("-");
                const wdObj = new Date(ds + "T00:00:00");
                const dayLetter = DAYS_SHORT[wdObj.getDay()];
                const isTodayDs = ds === todayStr();
                const isSelected = ds === dayViewDate;
                const hasItems = calEvents.some(e => isOccurringOn(e, ds)) || calReminders.some(r => isOccurringOn(r, ds)) || tasks.some(t => isTaskOccurringOn(t, ds));
                return (
                  <div key={ds} onClick={() => { setDayViewDate(ds); setWeekAnchor(ds); setSelectedDate(ds); closeSlotPopover(); }}
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, cursor: "pointer" }}>
                    <span style={{ fontSize: 10, color: isSelected ? "#d4789a" : "#c0a0b0", fontWeight: 600 }}>{dayLetter}</span>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: isSelected ? "#d4789a" : isTodayDs ? "var(--c-bg-card)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: isSelected || isTodayDs ? 700 : 400, color: isSelected ? "#fff" : isTodayDs ? "#d4789a" : "#3a3040" }}>{parseInt(wd)}</div>
                    {hasItems && <div style={{ width: 5, height: 5, borderRadius: "50%", background: isSelected ? "#d4789a" : "#f4a7b9" }} />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Day label row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px 4px" }}>
            <button onClick={() => goDay(-1)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "var(--c-text-accent)", padding: "0 4px" }}><ChevronLeft size={16} /></button>
            <span style={{ fontSize: 13, fontWeight: 700, color: isToday ? "#d4789a" : "#3a3040" }}>{dateLabel}{isToday ? " · Today" : ""}</span>
            <button onClick={() => goDay(1)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "var(--c-text-accent)", padding: "0 4px" }}><ChevronRight size={16} /></button>
          </div>

          {/* All-day chips */}
          {allDayItems.length > 0 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", paddingLeft: LABEL_W, marginBottom: 10 }}>
              {allDayItems.map(({ item, type }) => (
                <div key={item.id} onClick={() => { setInlineModalItem(item); setInlineModalType(type); setInlineModalDate(dayViewDate); }}
                  style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 8, background: itemBlockColor(item, type), color: getContrastText(itemBlockColor(item, type)), cursor: "pointer", maxWidth: 140, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {item.title}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Scrollable timeline ── */}
        <div ref={scrollRef}
          style={{ overflowY: drawState || isDragging ? "hidden" : "auto", flex: 1, position: "relative", touchAction: drawState || isDragging ? "none" : "pan-y" }}
          onTouchStart={onTouchStart}
          onTouchMove={(e) => {
            // If dragging an event block, update dragPos to follow finger
            if (isDragging && dragItem) {
              const touch = Array.from(e.touches).find(t => t.identifier === dragTouchId.current) || e.touches[0];
              if (touch) setDragPos({ x: touch.clientX, y: touch.clientY });
              e.preventDefault();
              return;
            }
            onTouchMove(e);
          }}
          onTouchEnd={(e) => {
            // Commit event drag
            if (isDragging && dragItem) {
              const touch = Array.from(e.changedTouches)[0];
              if (touch && scrollRef.current) {
                const rect = scrollRef.current.getBoundingClientRect();
                const relY = touch.clientY - rect.top + scrollRef.current.scrollTop;
                const snappedMins = toAbsoluteMinutes(Math.round((relY / PX_PER_HOUR) * 60 / 15) * 15, dayStartHour);
                const origStart = parseTime(dragItem.item.time) || 0;
                const origEnd   = parseTime(dragItem.item.endTime) || origStart + 60;
                const dur = origEnd - origStart;
                const newEndMins = snappedMins + dur;
                const newTime = minsToStr(Math.max(0, snappedMins));
                const newEndTime = minsToStr(Math.min(1440, newEndMins));
                if (dragItem.type === "event") setCalEvents(p => p.map(ev => ev.id === dragItem.item.id ? { ...ev, time: newTime, endTime: newEndTime, updatedAt: new Date().toISOString() } : ev));
                if (dragItem.type === "reminder") setCalReminders(p => p.map(r => r.id === dragItem.item.id ? { ...r, time: newTime, endTime: newEndTime } : r));
                if (dragItem.type === "task") setTasks(p => p.map(t => t.id === dragItem.item.id ? { ...t, time: newTime, endTime: newEndTime } : t));
                cancelDrag();
                return;
              }
            }
            onTouchMove(e);
          }}
          onTouchCancel={() => { cancelDrag(); setDrawState(null); drawTouchId.current = null; }}
          onMouseDown={onMouseDown}
          onMouseMove={(e) => { if (isDragging && dragItem) setDragPos({ x: e.clientX, y: e.clientY }); }}
          onMouseUp={(e) => {
            if (isDragging && dragItem && scrollRef.current) {
              const rect = scrollRef.current.getBoundingClientRect();
              const relY = e.clientY - rect.top + scrollRef.current.scrollTop;
              const snappedMins = toAbsoluteMinutes(Math.round((relY / PX_PER_HOUR) * 60 / 15) * 15, dayStartHour);
              const origStart = parseTime(dragItem.item.time) || 0;
              const origEnd   = parseTime(dragItem.item.endTime) || origStart + 60;
              const dur = origEnd - origStart;
              const newTime = minsToStr(Math.max(0, snappedMins));
              const newEndTime = minsToStr(Math.min(1440, snappedMins + dur));
              if (dragItem.type === "event") setCalEvents(p => p.map(ev => ev.id === dragItem.item.id ? { ...ev, time: newTime, endTime: newEndTime, updatedAt: new Date().toISOString() } : ev));
              if (dragItem.type === "reminder") setCalReminders(p => p.map(r => r.id === dragItem.item.id ? { ...r, time: newTime, endTime: newEndTime } : r));
              if (dragItem.type === "task") setTasks(p => p.map(t => t.id === dragItem.item.id ? { ...t, time: newTime, endTime: newEndTime } : t));
              cancelDrag();
              return;
            }
          }}>
          <div ref={gridRef} style={{ position: "relative", height: 24 * PX_PER_HOUR, marginLeft: LABEL_W, minHeight: "100%", userSelect: "none" }}>

            {/* Hour rows */}
            {HOURS.map(h => (
              <div key={h} data-slot="1" style={{ position: "absolute", top: h * PX_PER_HOUR, left: -LABEL_W, right: 0, height: PX_PER_HOUR, display: "flex", alignItems: "flex-start", borderTop: h > 0 ? "1px solid #f0e4f0" : "none", boxSizing: "border-box" }}>
                <span style={{ fontSize: 10, color: "var(--c-text)", width: LABEL_W - 8, textAlign: "right", paddingRight: 8, paddingTop: 2, lineHeight: 1, flexShrink: 0, pointerEvents: "none", userSelect: "none" }}>{fmtHour(h)}</span>
                <div data-slot="1" style={{ flex: 1, height: "100%", cursor: "crosshair" }} />
              </div>
            ))}

            {/* Half-hour dashed lines */}
            {HOURS.map(h => (
              <div key={`hh${h}`} style={{ position: "absolute", top: h * PX_PER_HOUR + PX_PER_HOUR / 2, left: 0, right: 0, borderTop: "1px dashed #f8eef6", pointerEvents: "none" }} />
            ))}

            {/* Current time line */}
            {isToday && (
              <>
                <div style={{ position: "absolute", top: nowTop, left: -8, right: 0, height: 2.5, background: "linear-gradient(90deg, var(--c-primary), var(--c-primary-deep))", zIndex: 10, pointerEvents: "none", boxShadow: "0 0 6px var(--c-shadow)", borderRadius: "0 2px 2px 0" }} />
                <div style={{ position: "absolute", top: nowTop - 5, left: -14, width: 12, height: 12, borderRadius: "50%", background: "var(--c-primary)", zIndex: 10, pointerEvents: "none", boxShadow: "0 0 0 3px var(--c-shadow), 0 0 10px var(--c-shadow)", animation: editModalItem ? "none" : "pulse 1.5s ease-in-out infinite" }} />
              </>
            )}

            {/* Ghost block while drawing */}
            {drawState && (
              <div style={{
                position: "absolute", top: ghostTop, left: 2, right: 2,
                height: ghostHeight,
                background: "rgba(212,120,154,0.18)",
                border: "2px dashed #d4789a",
                borderRadius: 6,
                zIndex: 8,
                pointerEvents: "none",
                display: "flex", flexDirection: "column", justifyContent: "center",
                padding: "4px 8px", boxSizing: "border-box",
                backdropFilter: "blur(2px)",
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "var(--c-text-accent)" }}>
                  {ghostStart} – {ghostEnd}
                </span>
              </div>
            )}

            {/* Event + reminder blocks */}
            {timedItems.map(({ item, type }) => {
              const startMin = parseTime(item.time);
              if (startMin === null) return null;
              const endMin = parseTime(item.endTime);
              const durMin = endMin && endMin > startMin ? endMin - startMin : 60;
              // startMin itself stays a real wall-clock minute (resize math
              // below depends on it) — only its grid position is rotated.
              const topPx = (toGridMinutes(startMin, dayStartHour) / 60) * PX_PER_HOUR;
              const heightPx = Math.max(24, (durMin / 60) * PX_PER_HOUR - 2);
              const color = itemBlockColor(item, type);
              const isBeingDragged = isDragging && dragItem?.item?.id === item.id;

              // dayItemRef: plain object backed by dayItemRefsMap so we can imperatively
              // hide the block the instant drag fires — before React re-renders — eliminating
              // the white-flash. Same pattern as week view (useRef forbidden inside .map()).
              const dayItemRef = { current: dayItemRefsMap.current[item.id] ?? null };

              // Long-press to drag from Day view
              const handleDayBlockDown = (e) => {
                // Stop bubbling so the grid's onMouseDown (draw-to-create) never sees this
                e.stopPropagation();
                if (drawState) return;
                // Never start a new long-press timer if a drag is already active
                if (isDraggingRef.current) return;
                if (e.type === "touchstart") {
                  const touch = e.touches[0];
                  dragTouchId.current = touch.identifier;
                  longPressTimer.current = setTimeout(() => {
                    closeSlotPopover();
                    // ── Imperative hide: kill opacity BEFORE setState so there is no
                    //    flash between the JS callback and React's next paint ──
                    if (dayItemRef.current) {
                      dayItemRef.current.style.transition = "none";
                      dayItemRef.current.style.opacity = "0";
                    }
                    const di = { item, type, origDate: dayViewDate };
                    dragItemRef.current = di; setDragItem(di);
                    setDragPos({ x: touch.clientX, y: touch.clientY });
                    isDraggingRef.current = true; setIsDragging(true);
                    if (navigator.vibrate) navigator.vibrate(40);
                  }, 450);
                } else if (e.button === 0) {
                  longPressTimer.current = setTimeout(() => {
                    closeSlotPopover();
                    // ── Imperative hide: same trick for mouse ──
                    if (dayItemRef.current) {
                      dayItemRef.current.style.transition = "none";
                      dayItemRef.current.style.opacity = "0";
                    }
                    const di = { item, type, origDate: dayViewDate };
                    dragItemRef.current = di; setDragItem(di);
                    setDragPos({ x: e.clientX, y: e.clientY });
                    isDraggingRef.current = true; setIsDragging(true);
                  }, 450);
                }
              };
              const handleDayBlockUp = (e) => {
                if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
                // If a resize just completed, ignore this — don't open the detail modal
                if (isResizingRef.current) return;
                // Use ref (not state) to avoid stale closure
                if (!isDraggingRef.current) { closeSlotPopover(); setInlineModalItem(item); setInlineModalType(type); setInlineModalDate(dayViewDate); }
              };

              // Live height during resize
              const isBeingResized = resizeItem && resizeItem.item.id === item.id;
              const liveHeightPx = (() => {
                if (!isBeingResized) return Math.max(24, (durMin / 60) * PX_PER_HOUR - 2);
                const origEnd = resizeItem.origEndMins;
                const delta = resizeItem.deltaMins || 0;
                const SNAP = 15;
                const snap = (m) => Math.round(m / SNAP) * SNAP;
                const newEnd = Math.max(startMin + SNAP, snap(origEnd + delta));
                return Math.max(24, ((newEnd - startMin) / 60) * PX_PER_HOUR - 2);
              })();

              // Resize handle — plugs into the shared global resize useEffect
              const handleResizeDown = (e) => {
                e.stopPropagation();
                e.preventDefault();
                if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
                const startY = e.touches ? e.touches[0].clientY : e.clientY;
                const origEndMins = endMin || (startMin + 60);
                const ri = { item, type, origEndMins, startY, deltaMins: 0 };
                resizeItemRef.current = ri;
                isResizingRef.current = true;
                setResizeItem(ri);
                setResizeActive(true); // attach the passive:false move listeners for this drag
              };

              return (
                <div key={item.id}
                  id={`day-resize-${item.id}`}
                  ref={el => { dayItemRefsMap.current[item.id] = el; dayItemRef.current = el; }}
                  onMouseDown={handleDayBlockDown}
                  onMouseUp={handleDayBlockUp}
                  onTouchStart={handleDayBlockDown}
                  onTouchEnd={handleDayBlockUp}
                  style={{
                    position: "absolute", top: topPx + 1, left: 2, right: 2,
                    height: liveHeightPx,
                    background: color,
                    borderLeft: `3px solid rgba(0,0,0,0.18)`,
                    borderRadius: 6,
                    padding: 0,
                    boxSizing: "border-box",
                    overflow: "visible",
                    zIndex: isBeingResized ? 10 : 2,
                    cursor: isDragging ? "grabbing" : isBeingResized ? "ns-resize" : "pointer",
                    boxShadow: isBeingDragged ? "none" : "0 1px 4px rgba(0,0,0,0.15)",
                    opacity: isBeingDragged ? 0 : 1,
                    transition: "none",
                    userSelect: "none", WebkitUserSelect: "none", touchAction: "none",
                  }}>
                  {/* Ghost outline — shown in place of original while dragging, same as week view */}
                  {isBeingDragged && (
                    <div style={{
                      position: "absolute", inset: 0,
                      borderRadius: 6,
                      border: `2px dashed ${color}`,
                      background: color + "22",
                      pointerEvents: "none",
                      opacity: 1,
                    }} />
                  )}
                  {/* Inner content — overflow:hidden so text clips, not the resize handle */}
                  <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: 6, padding: "4px 8px", pointerEvents: "none", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: getContrastText(color), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title}</span>
                    <span style={{ fontSize: 9, color: getContrastText(color) === "#fff" ? "rgba(255,255,255,0.7)" : BLOCK_INK_SOFT }}>{item.time}{item.endTime ? ` – ${item.endTime}` : ""}</span>
                  </div>
                  {/* Resize handle at bottom — outside overflow:hidden so always clickable */}
                  <div
                    onMouseDown={handleResizeDown}
                    onTouchStart={handleResizeDown}
                    style={{
                      position: "absolute", bottom: -4, left: 0, right: 0,
                      height: 12, cursor: "ns-resize",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      touchAction: "none", zIndex: 20,
                    }}>
                    <div style={{ width: 24, height: 3, borderRadius: 2, background: "rgba(255,255,255,0.65)" }} />
                  </div>
                </div>
              );
            })}

            {/* Empty state hint */}
            {timedItems.length === 0 && allDayItems.length === 0 && (
              <div style={{ position: "absolute", top: "38%", left: 0, right: 0, textAlign: "center", pointerEvents: "none" }}>
                <p style={{ fontSize: 13, color: "var(--c-text-soft)", margin: 0 }}>Nothing scheduled</p>
                <p style={{ fontSize: 11, color: "#d0b0c8", margin: "4px 0 0" }}>Tap or drag to add a time block</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Floating drag preview (when dragging from Day view) ── */}
        {isDragging && dragItem && dragPos && (() => {
          const { item, type } = dragItem;
          const origStart = parseTime(item.time) || 0;
          const origEnd   = parseTime(item.endTime) || origStart + 60;
          const dur = origEnd - origStart;
          const color = itemBlockColor(item, type);
          const heightPx = Math.max(24, (dur / 60) * PX_PER_HOUR - 2);
          return (
            <div style={{
              position: "fixed",
              top: dragPos.y - heightPx / 2,
              left: "50%",
              transform: "translateX(-50%)",
              width: "calc(100% - 80px)",
              height: heightPx,
              background: color,
              borderRadius: 6,
              padding: "4px 8px",
              boxSizing: "border-box",
              zIndex: 40,
              pointerEvents: "none",
              border: "2px solid rgba(255,255,255,0.9)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.32)",
              transform: "translateX(-50%) scale(1.03)",
              display: "flex", flexDirection: "column", justifyContent: "center",
              opacity: 0.92,
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: getContrastText(color), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title}</span>
              <span style={{ fontSize: 9, color: getContrastText(color) === "#fff" ? "rgba(255,255,255,0.7)" : BLOCK_INK_SOFT }}>{item.time}{item.endTime ? ` – ${item.endTime}` : ""}</span>
            </div>
          );
        })()}

      </div>
    );
  };

  const DayViewPanel = () => {
    const HOURS = Array.from({ length: 24 }, (_, i) => i);
    const PX_PER_HOUR = 60;
    const LABEL_W = 60;

    const fmtHour = (h) => {
      if (h === 0) return "12 AM";
      if (h < 12) return `${h} AM`;
      if (h === 12) return "12 PM";
      return `${h - 12} PM`;
    };

    const parseTime = (t) => {
      if (!t) return null;
      const [h, m] = t.split(":").map(Number);
      return h * 60 + (m || 0);
    };

    const daySessions = focusSessions.filter(s =>
      s.date === dayViewDate && s.startedAt && s.completedAt
    );

    // Timed calendar items for this day
    const timedEvents    = applyFilter(calEvents.filter(e => isOccurringOn(e, dayViewDate) && e.time).map(e => ({ item: e, type: "event" })));
    const timedReminders = applyFilter(calReminders.filter(r => isOccurringOn(r, dayViewDate) && r.time).map(r => ({ item: r, type: "reminder" })));
    const allDayItems    = applyFilter([
      ...tasks.filter(t => isTaskOccurringOn(t, dayViewDate)).map(t => ({ item: t, type: "task" })),
      ...calEvents.filter(e => isOccurringOn(e, dayViewDate) && !e.time).map(e => ({ item: e, type: "event" })),
      ...calReminders.filter(r => isOccurringOn(r, dayViewDate) && !r.time).map(r => ({ item: r, type: "reminder" })),
    ]);

    const getTop = (iso) => {
      const d = new Date(iso);
      return (d.getHours() + d.getMinutes() / 60) * PX_PER_HOUR + 8;
    };
    const getHeight = (s) => {
      const startD = new Date(s.startedAt);
      const endD = new Date(s.completedAt);
      const diffMins = (endD - startD) / 60000;
      return Math.max(24, (diffMins / 60) * PX_PER_HOUR);
    };

    const itemBlockColor = (item, type) => {
      if (type === "event") return eventColor(item);
      if (type === "reminder") {
        const cat = item.categoryId ? categories.find(c => c.id === item.categoryId) : null;
        return cat?.color || "#5bbfb5";
      }
      return taskColor(item);
    };

    const nowObj = now();
    const nowMins = nowObj.getHours() * 60 + nowObj.getMinutes();
    const nowTop = (nowMins / 60) * PX_PER_HOUR + 8;
    const isToday = dayViewDate === todayStr();

    const [y, m, d2] = dayViewDate.split("-");
    const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d2));
    const dayLabel = `${DAYS_FULL[dateObj.getDay()]}, ${MONTHS[parseInt(m) - 1]} ${parseInt(d2)}`;

    return (
      <div className="cal-subview" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)", overflow: "hidden" }}>
        {/* Week strip */}
        <div style={{ background: "var(--c-bg-card)", borderRadius: 16, padding: "10px 8px", marginBottom: 8, border: "1.5px solid #f5d8ec", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "var(--c-text-accent)", fontWeight: 700 }}>
              {MONTHS[new Date(weekDays[0] + "T00:00:00").getMonth()].slice(0,3)} {new Date(weekDays[0] + "T00:00:00").getDate()} – {MONTHS[new Date(weekDays[6] + "T00:00:00").getMonth()].slice(0,3)} {new Date(weekDays[6] + "T00:00:00").getDate()}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
            {weekDays.map(ds => {
              const [, wm, wd] = ds.split("-");
              const wdObj = new Date(ds + "T00:00:00");
              const dayLetter = DAYS_SHORT[wdObj.getDay()];
              const isTodayDs = ds === todayStr();
              const isSelected = ds === dayViewDate;
              const hasSessions = focusSessions.some(s => s.date === ds);
              const hasItems = calEvents.some(e => isOccurringOn(e, ds)) || calReminders.some(r => isOccurringOn(r, ds)) || tasks.some(t => t.dueDate === ds);
              return (
                <div key={ds} onClick={() => { setDayViewDate(ds); setWeekAnchor(ds); setSelectedDate(ds); }}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, cursor: "pointer" }}>
                  <span style={{ fontSize: 10, color: isSelected ? "#d4789a" : "#c0a0b0", fontWeight: 600 }}>{dayLetter}</span>
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: isSelected ? "#d4789a" : isTodayDs ? "var(--c-bg-card)" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: isSelected || isTodayDs ? 700 : 400,
                    color: isSelected ? "#fff" : isTodayDs ? "#d4789a" : "#3a3040",
                  }}>{parseInt(wd)}</div>
                  {(hasSessions || hasItems) && <div style={{ width: 5, height: 5, borderRadius: "50%", background: isSelected ? "#d4789a" : "#f4a7b9" }} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Day label + all-day strip */}
        <div style={{ flexShrink: 0, marginBottom: 10 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text-accent)", margin: "0 0 6px" }}>{dayLabel}</p>
          {allDayItems.length > 0 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 6 }}>
              {allDayItems.map(({ item, type }) => (
                <div key={item.id}
                  onClick={() => { setInlineModalItem(item); setInlineModalType(type); setInlineModalDate(dayViewDate); }}
                  style={{
                    fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 8,
                    background: itemBlockColor(item, type),
                    color: getContrastText(itemBlockColor(item, type)),
                    cursor: "pointer", maxWidth: 140, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                  }}>
                  {item.title}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scrollable timeline */}
        <div style={{
          overflowY: "auto",
          flex: 1,
          position: "relative",
          background: "var(--c-bg-card)",
          transform: "translateZ(0)",
          WebkitTransform: "translateZ(0)",
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
          // NOTE: no permanent `will-change: transform` here. Kept on a tall,
          // always-mounted scroller it forces a big composited layer that the GPU
          // never releases; every list↔calendar remount stacked another, which is
          // what made re-opening the edit panel scroll progressively jankier.
          // translateZ(0) + contain already give this its own layer for smooth
          // scrolling without the permanent-promotion cost.
          contain: "layout paint style",
          isolation: "isolate",
        }}>
          <div style={{ 
            position: "relative", 
            height: 24 * PX_PER_HOUR + 8, 
            marginLeft: LABEL_W,
            background: "var(--c-bg-card)",
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
          }}>

            {/* Hour lines + labels + tappable slots */}
            {HOURS.map(h => (
              <div key={h} style={{ position: "absolute", top: h * PX_PER_HOUR + 8, left: -LABEL_W, right: 0, display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 10, color: "var(--c-text)", width: LABEL_W - 8, textAlign: "right", paddingRight: 8, lineHeight: "1", flexShrink: 0 }}>{fmtHour(h)}</span>
                <div
                  onClick={() => {
                    const hh = String(h).padStart(2, "0");
                    setSelectedDate(dayViewDate);
                    setQuickAddTime(`${hh}:00`);
                    setCalPanel("add");
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fdf0f6"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  style={{ flex: 1, height: PX_PER_HOUR, marginTop: `-${PX_PER_HOUR / 2}px`, borderTop: "1px solid #f5e8f0", cursor: "pointer" }}
                />
              </div>
            ))}

            {/* Half-hour lines */}
            {HOURS.map(h => (
              <div key={`hh${h}`} style={{ position: "absolute", top: h * PX_PER_HOUR + PX_PER_HOUR / 2 + 8, left: 0, right: 0, borderTop: "1px dashed #f8eef6", pointerEvents: "none" }} />
            ))}

            {/* Current time line */}
            {isToday && (
              <>
                <div style={{ position: "absolute", top: nowTop, left: -8, right: 0, height: 2.5, background: "linear-gradient(90deg, var(--c-primary), var(--c-primary-deep))", zIndex: 10, pointerEvents: "none", boxShadow: "0 0 6px var(--c-shadow)", borderRadius: "0 2px 2px 0" }} />
                <div style={{ position: "absolute", top: nowTop - 5, left: -14, width: 12, height: 12, borderRadius: "50%", background: "var(--c-primary)", zIndex: 10, pointerEvents: "none", boxShadow: "0 0 0 3px var(--c-shadow), 0 0 10px var(--c-shadow)", animation: editModalItem ? "none" : "pulse 1.5s ease-in-out infinite" }} />
              </>
            )}

            {/* Calendar event blocks */}
            {[...timedEvents, ...timedReminders].map(({ item, type }) => {
              const startMin = parseTime(item.time);
              if (startMin === null) return null;
              const endMin = parseTime(item.endTime);
              const durMin = endMin && endMin > startMin ? endMin - startMin : 60;
              const topPx = (startMin / 60) * PX_PER_HOUR + 8;
              const heightPx = Math.max(22, (durMin / 60) * PX_PER_HOUR - 2);
              const color = itemBlockColor(item, type);
              return (
                <div key={item.id}
                  onClick={() => { setInlineModalItem(item); setInlineModalType(type); setInlineModalDate(dayViewDate); }}
                  style={{
                    position: "absolute", top: topPx, left: 4, right: 4,
                    height: heightPx,
                    background: color + "22",
                    borderRadius: 12,
                    borderLeft: `4px solid ${color}`,
                    padding: "4px 8px 4px 10px", boxSizing: "border-box",
                    display: "flex", flexDirection: "column", justifyContent: "center",
                    overflow: "hidden", zIndex: 2, cursor: "pointer",
                    boxShadow: `0 2px 8px ${color}33`,
                  }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>
                    {item.title}
                  </span>
                  {heightPx > 32 && (
                    <span style={{ fontSize: 9, color: color + "bb", fontWeight: 500, marginTop: 1 }}>
                      {item.time}{item.endTime ? ` – ${item.endTime}` : ""}
                    </span>
                  )}
                </div>
              );
            })}

            {/* Clock-in / focus session blocks */}
            {daySessions.map((s, i) => {
              const allDayLabels = [...new Set(daySessions.map(x => x.label?.trim() || "Clock-in"))];
              const sessionColor = labelColor(s.label?.trim() || "Clock-in", allDayLabels);
              return (
                <div key={i} style={{
                  position: "absolute",
                  top: getTop(s.startedAt),
                  left: 4, right: 4,
                  height: getHeight(s),
                  background: sessionColor,
                  borderRadius: 8,
                  padding: "4px 10px",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  overflow: "hidden",
                  zIndex: 3,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--c-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    ⏱️ {s.label?.trim() || "Clock-in"} · {s.minutes}m
                  </span>
                  <button onClick={() => {
                    {
                      setFocusSessions(p => p.filter(x => !(x.startedAt === s.startedAt && x.completedAt === s.completedAt)));
                    }
                  }} style={{ background: "rgba(255,255,255,0.6)", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 11, color: "#e8647a", padding: "1px 5px", flexShrink: 0, marginLeft: 6 }}><X size={16} /></button>
                </div>
              );
            })}

            {daySessions.length === 0 && timedEvents.length === 0 && timedReminders.length === 0 && allDayItems.length === 0 && (
              <div style={{ position: "absolute", top: "35%", left: 0, right: 0, textAlign: "center" }}>
                <p style={{ fontSize: 13, color: "var(--c-text-soft)", margin: 0 }}>Nothing scheduled</p>
                <p style={{ fontSize: 11, color: "#d0b0c8", margin: "4px 0 0" }}>Tap a time slot to add</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Tasks/Events read as a proper iOS-style segmented control: one inset
  // "track" housing both segments, with the active segment filling in —
  // rather than two free-floating buttons. Bigger, bolder label per feedback
  // that the old 11px read too small to anchor the row. Shared by the desktop
  // list panel and the mobile topbar's Row 2, and the mobile Calendar/List
  // switcher reuses the same track look so the two sit side by side as a set.
  const MAIN_TABS = [
    { id: "tasks",  label: "Tasks" },
    { id: "events", label: "Events" },
  ];
  const mainTrackStyle = {
    display: "flex", gap: 3, flexShrink: 0,
    padding: 4, borderRadius: 999,
    background: "var(--c-bg-soft)",
    border: "1px solid var(--c-border-light)",
  };
  const tabBtnStyle = (active) => ({
    flex: 1,
    // height:100% + border-box, not padding alone, so this fills whatever
    // height the track actually has: auto on desktop (no explicit height on
    // mainTrackStyle there, so 100% itself resolves to auto and padding
    // drives the size same as before), but exactly MOBILE_ROW2_H on mobile
    // (renderListMainTabs passes that as an explicit height). Padding alone
    // made the button taller than the mobile track's fixed height, pushing
    // it past the bottom and making the label look bottom-anchored instead
    // of centered in the pill.
    // minHeight 0 overrides App.jsx's global `button { min-height: 36px }`:
    // inside the 36px mobile track only ~26px is left after its padding and
    // border, and the floor forced the button to 36 so it hung out the
    // bottom of the track.
    height: "100%", minHeight: 0, boxSizing: "border-box",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
    // Rounded rectangle, not a pill — Tasks/Events reads better that way at
    // its taller height, unlike the switcher/Week/All/Today which stay pills.
    padding: "9px 10px", borderRadius: 12, border: "none",
    background: active
      ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))"
      : "transparent",
    // No vertical offset — on a pill this short, the usual downward-offset
    // shadow (2px 8px, used elsewhere for taller elements) reads as visual
    // weight under the label and makes genuinely centered text look
    // bottom-anchored. Symmetric blur keeps the "active" lift without that.
    boxShadow: active ? "0 0 6px var(--c-shadow)" : "none",
    color: active ? "var(--c-on-primary)" : "var(--c-text-mid)",
    fontSize: 14.5, fontWeight: 800, letterSpacing: "0.01em",
    cursor: "pointer", transition: "all 0.15s",
    fontFamily: "inherit", whiteSpace: "nowrap",
  });
  // Same switcher as the habit form's To Do / Restricted (Jade, 2026-10-11):
  // a grey track with one pill that slides to the picked side. The pill keeps
  // the accent gradient and the labels keep their colours; only the track and
  // the slide are new. The buttons themselves are transparent and sit above
  // the pill.
  const renderListMainTabs = (trackExtra) => {
    const idx = Math.max(0, MAIN_TABS.findIndex(t => t.id === listMainTab));
    return (
      <div style={{ ...mainTrackStyle, gap: 0, position: "relative", borderRadius: 16, border: "none", background: "rgba(120,120,128,0.12)", ...trackExtra }}>
        <div aria-hidden style={{
          position: "absolute", top: 4, bottom: 4, left: 4, width: "calc(50% - 4px)", borderRadius: 12,
          background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))",
          boxShadow: "0 0 6px var(--c-shadow)",
          transform: `translateX(${idx * 100}%)`,
          transition: "transform 0.28s cubic-bezier(0.22,1,0.36,1)",
        }} />
        {MAIN_TABS.map(tab => (
          <button key={tab.id}
            onClick={() => { setListMainTab(tab.id); setListSubTab(tab.id === "tasks" ? "pending" : "upcoming"); }}
            style={{ ...tabBtnStyle(listMainTab === tab.id), position: "relative", zIndex: 1, background: "transparent", boxShadow: "none", transition: "color 0.2s",
              // Unpicked label: soft white on dark backgrounds, soft black on light
              // ones, instead of the theme's faint muted text (Jade, 2026-10-11).
              ...(listMainTab !== tab.id ? { color: isDarkBgTheme() ? "rgba(255,255,255,0.78)" : "rgba(0,0,0,0.6)" } : null) }}>
            {tab.label}
          </button>
        ))}
      </div>
    );
  };
  // Mobile Row 2 height: both the Tasks/Events track and the view switcher are
  // pinned to it (border-box), so the switcher is the same size and sits at the
  // same spot in calendar and list view instead of taking its siblings' height.
  // 36 exactly matches the app's own global `button { min-height: 36px }` tap-
  // target floor (App.jsx) — going any shorter needs an explicit inline
  // min-height override on every affected button (inline beats that
  // unqualified, non-!important rule) or they silently snap back up to 36 and
  // overflow the track, which is the bug the switcher buttons below work
  // around for their own smaller knob.
  const MOBILE_ROW2_H = 36;
  // Tasks/Events track: a touch taller than the switcher beside it (by request).
  // Row 2 is sized to this in BOTH views, so the 36px switcher stays centered
  // at the same spot whether the row holds Tasks/Events or Week/All/Today.
  const LIST_TABS_H = 40;
  const topbarInk = () => (isDarkBgTheme() ? "#ffffff" : "#1a1a1a");
  // The ☰ lines: the accent, or a light bright shade of it on dark backgrounds / dark mode,
  // same as the Week view lock (Jade, 2026-10-26).
  const menuLine = isDarkBgTheme() ? "color-mix(in srgb, var(--c-primary) 45%, #fff)" : "var(--c-primary)";
  // The selected option in the mobile topbar (calendar/list icons,
  // Week/Month/Year) is underlined rather than filled.
  const underlineStyle = (active) => ({
    position: "absolute", left: "50%", bottom: 0, height: 2.5, borderRadius: 2,
    width: active ? "70%" : 0, transform: "translateX(-50%)",
    background: "var(--c-primary)", transition: "width 0.2s",
  });
  // Calendar/List switcher (mobile, Row 1 upper-right, both views).
  const renderPlannerViewSwitch = () => (
    // gap/marginRight tuned so the Calendar icon lands directly above the
    // Week view lock icon and the List icon lands directly above the Today
    // button below, in Row 2 — see their measured centres in the commit that
    // introduced this comment. Retune both together if either row's icon
    // sizes change.
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0, marginRight: -3 }}>
      {[
        { id: "calendar", icon: faCalendarDaysSolid, label: "Show calendar" },
        { id: "list", icon: faListUl, label: "Show list" },
      ].map(v => {
        const active = plannerView === v.id;
        return (
          <button key={v.id} onClick={() => setPlannerView(v.id)} aria-label={v.label} aria-pressed={active} title={v.label}
            data-tour={v.id === "list" ? "planner-list-btn" : undefined}
            style={{
              position: "relative", width: 42, height: 40, minHeight: 0, flexShrink: 0,
              border: "none", background: "transparent", padding: 0, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
            {/* Explicit width/height, not fixedWidth: .fa-fw needs FontAwesome's
                core CSS, which this project never loads. */}
            <FontAwesomeIcon icon={v.icon} style={{ display: "block", width: 23, height: 23, color: topbarInk(), opacity: active ? 1 : 0.5, transition: "opacity 0.2s" }} />
            <span style={underlineStyle(active)} />
          </button>
        );
      })}
    </div>
  );
  // Week/Month/Year — mobile calendar Row 2, all three listed out (no
  // dropdown, no border). "calendar" is the Month view's id. The selected one
  // is a highlighted pill in the theme's accent (Jade, 2026-10-06 — was an
  // underline, which sat level with the row's other buttons while the labels
  // sat higher). The pill is vertically centred in the row, so the labels line
  // up with the filter / lock / Today buttons beside them.
  const renderCalSpanTabs = () => (
    <div data-tour="planner-view-toggle" role="tablist" style={{
      display: "flex", alignItems: "center", gap: 6, height: MOBILE_ROW2_H, flexShrink: 0,
    }}>
      {[["week", "Week"], ["calendar", "Month"], ["year", "Year"]].map(([id, label]) => {
        const active = calSubTab === id;
        return (
          <button key={id} role="tab" aria-selected={active} onClick={() => setCalSubTab(id)}
            style={{
              height: 30, minHeight: 0, padding: "0 13px", borderRadius: 15,
              border: "none", cursor: "pointer",
              background: active ? "color-mix(in srgb, var(--c-primary) 22%, transparent)" : "transparent",
              color: topbarInk(), opacity: active ? 1 : 0.6,
              fontSize: 15, fontWeight: 800, fontFamily: "'Nunito', sans-serif", whiteSpace: "nowrap",
              transition: "background-color 0.2s, opacity 0.2s",
            }}>
            {label}
          </button>
        );
      })}
    </div>
  );

  // ── Main calendar view ───────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: isDesktop && plannerView === "list" ? "row" : "column", gap: isDesktop && plannerView === "list" ? 20 : 0, position: "relative",
      // Only trimmed when reflections are expanded full-width — that's the
      // specific case whose gap read as bigger than Habits' equivalent
      // page. The narrow split view already had the right amount of gap
      // here, so leave its 68px reserve alone.
      paddingTop: isDesktop ? ((listReflectMode && reflectWide) ? 52 : 68) : (inlineTopbar ? 0 : 6),
      // Mobile list/reflect flows into the page's own scroll (see clarity-main),
      // so this wrapper must not lock height or clip — otherwise the inner
      // content can't grow past one screenful.
      paddingBottom: isDesktop ? 16 : 0,
      flex: (!isDesktop && plannerView === "list") ? "unset" : 1,
      overflow: (!isDesktop && plannerView === "list") ? "visible" : "hidden" }}>
      <style>{VIEW_SWITCH_CSS}</style>

      {/* View switcher now lives in the top bar — no floating button needed */}

      {/* ── Category pills moved into top bar (Row 3) ── */}

      {/* ── LIST VIEW (Step 4) ─────────────────────────────────────────────── */}
      {plannerView === "list" && (() => {
        const { overdueTasks, pendingTasks, finishedTasks, upcomingEvents, passedEvents, groupByDate } = listViewData;

        // ── Multi-select helpers (bulk delete) ─────────────────────────────
        const listKeyOf = (x) => `${x.type}:${String(x.item.id)}`;
        const allListPairs = [...overdueTasks, ...pendingTasks, ...finishedTasks, ...upcomingEvents, ...passedEvents];
        const allListKeys = allListPairs.map(listKeyOf);
        const selectedCount = listSelectedKeys.size;
        const allSelected = allListKeys.length > 0 && allListKeys.every(k => listSelectedKeys.has(k));
        const toggleSelectKey = (k) => setListSelectedKeys(prev => {
          const next = new Set(prev);
          if (next.has(k)) next.delete(k); else next.add(k);
          return next;
        });
        const exitSelectMode = () => { setListSelectMode(false); setListSelectedKeys(new Set()); setListDeleteConfirm(false); };
        const toggleSelectAll = () => setListSelectedKeys(allSelected ? new Set() : new Set(allListKeys));
        const performBulkDelete = () => {
          const byType = { task: new Set(), event: new Set(), reminder: new Set() };
          listSelectedKeys.forEach(k => {
            const i = k.indexOf(":");
            const t = k.slice(0, i), id = k.slice(i + 1);
            if (byType[t]) byType[t].add(id);
          });
          // Snapshot into Recently Deleted before removing from the live arrays.
          // Dedupe by type+id — recurring items can appear as multiple occurrence
          // entries in allListPairs that all share the same underlying item id.
          // Google Tasks can't be deleted from Clarity (see GoogleTaskLink.jsx), so
          // they're left out and the user is told where to delete them.
          const keptGTasks = new Set(allListPairs.filter(x => x.type === "event" && byType.event.has(String(x.item.id)) && isGoogleTaskEvent(x.item)).map(x => String(x.item.id)));
          keptGTasks.forEach(id => byType.event.delete(id));
          const seenSoftDelete = new Set();
          const toSoftDelete = [];
          allListPairs.forEach(x => {
            const id = String(x.item.id);
            if (!byType[x.type] || !byType[x.type].has(id)) return;
            const key = `${x.type}:${id}`;
            if (seenSoftDelete.has(key)) return;
            seenSoftDelete.add(key);
            toSoftDelete.push({ id, type: x.type, item: x.item });
          });
          softDeleteItems(toSoftDelete);
          if (byType.task.size)     setTasks(p => p.filter(t => !byType.task.has(String(t.id))));
          if (byType.event.size)    setCalEvents(p => removeEventsCascade(p, [...byType.event]));
          if (byType.reminder.size) setCalReminders(p => p.filter(r => !byType.reminder.has(String(r.id))));
          exitSelectMode();
          if (keptGTasks.size) {
            window.alert(keptGTasks.size === 1
              ? "1 task from Google Tasks wasn't deleted. Clarity can't delete it there. Delete it in Google Tasks and it will leave Clarity on the next sync."
              : `${keptGTasks.size} tasks from Google Tasks weren't deleted. Clarity can't delete them there. Delete them in Google Tasks and they will leave Clarity on the next sync.`);
          }
        };

        // ── Color helpers ──────────────────────────────────────────────────
        const getColor = (item, type) => {
          if (type === "event") return eventColor(item);
          if (type === "reminder") {
            const cat = item.categoryId ? categories.find(c => c.id === item.categoryId) : null;
            return cat?.color || themeTone("#5bbfb5");
          }
          // task
          return taskColor(item);
        };

        const TYPE_COLORS = { task: DEFAULT_TASK_COLOR, event: DEFAULT_EVENT_COLOR, reminder: "#5bbfb5" };
        const TYPE_LABELS = { task: "Task", event: "Event", reminder: "Reminder" };

        // ── Pill for category / type ───────────────────────────────────────
        const CategoryChip = ({ item, type }) => {
          const cat = (item.goalId || item.categoryId)
            ? categories.find(c => c.id === (item.goalId || item.categoryId))
            : null;
          if (!cat) return null;
          return (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: "0.04em",
              background: (cat.color || "#d4789a") + "22",
              color: cat.color || "#d4789a",
              borderRadius: 8, padding: "2px 7px",
              whiteSpace: "nowrap", flexShrink: 0,
            }}>
              {cat.emoji ? `${cat.emoji} ` : ""}{cat.title}
            </span>
          );
        };

        // ── Date group header ──────────────────────────────────────────────
        const DateHeader = ({ label, date }) => {
          const isToday = date === todayStr();
          return (
            <div style={{
              position: "sticky", top: 0, zIndex: 5,
              display: "flex", alignItems: "center", gap: 8,
              padding: "6px 2px",
              background: "transparent",
            }}>
              <span style={{
                fontSize: 11, fontWeight: 800,
                color: isToday ? "var(--c-text-accent)" : "var(--c-text)",
                letterSpacing: "0.06em", textTransform: "uppercase",
                background: "rgba(255,255,255,0.88)",
                borderRadius: 20,
                padding: "3px 10px",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
                flexShrink: 0,
              }}>{label}</span>
              <div style={{ flex: 1, height: 1, background: isToday ? "var(--c-primary-light)" : "var(--c-border-light)", borderRadius: 1 }} />
            </div>
          );
        };

        // ── Empty state ────────────────────────────────────────────────────
        const EmptyState = ({ Icon, text, sub }) => {
          // Desktop always reads on a light list panel, so keep this text black
          // there regardless of the active background theme; mobile still
          // switches to white on dark-gradient themes for contrast.
          const dark = !isDesktop && isDarkBgTheme();
          return (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", padding: "48px 20px", gap: 10,
            }}>
              <Icon size={44} style={{ color: dark ? "rgba(255,255,255,0.72)" : "var(--c-text-mid)" }} />
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: dark ? "#ffffff" : "#000000" }}>{text}</p>
              {sub && <p style={{ margin: 0, fontSize: 12, color: dark ? "rgba(255,255,255,0.6)" : "var(--c-text-mid)" }}>{sub}</p>}
            </div>
          );
        };

        const reflectExpanded = isDesktop && listReflectMode && reflectWide;
        return (
          <div className={viewEnterClass} style={{
            display: "flex", flexDirection: "column",
            flex: isDesktop ? (reflectExpanded ? "1 1 auto" : "0 0 37%") : "unset",
            minWidth: isDesktop ? 260 : undefined,
            maxWidth: isDesktop ? (reflectExpanded ? "none" : 420) : undefined,
            // Desktop keeps minHeight:0 so its inner scroll area can shrink below
            // its content; mobile flows into the page scroll instead, so it must
            // grow to its natural height (no minHeight:0, no flex lock).
            minHeight: isDesktop ? 0 : undefined,
            fontFamily: "'Quicksand','Nunito',sans-serif",
            position: "relative", zIndex: 1,
            isolation: "isolate",
            padding: isDesktop ? "0 0 0 0" : undefined,
          }}>
            {/* Rounded card container with white surface. minHeight:0 lets this flex
                child shrink below its content so the inner scroll area (and its sticky
                date headers) actually work; overflow:hidden clips the rounded corners.
                Dropped once reflections go full-width (reflectExpanded) — at that size
                the white panel just reads as a second, redundant background. */}
            <div ref={listPanelContainerRef} style={{
              position: "relative",
              flex: isDesktop ? 1 : "unset", display: "flex", flexDirection: "column",
              minHeight: isDesktop ? 0 : undefined,
              borderRadius: isDesktop && !reflectExpanded ? 22 : 0,
              overflow: isDesktop && !reflectExpanded ? "hidden" : "visible",
              background: isDesktop && !reflectExpanded ? "var(--c-bg-card)" : "none",
              border: isDesktop && !reflectExpanded ? "1.5px solid var(--c-border-light)" : "none",
              boxShadow: isDesktop && !reflectExpanded ? "0 8px 30px rgba(180,100,140,0.1)" : "none",
            }}>
            {/* ── Main tab row (Tasks / Events) + Select + Recently Deleted ── */}
            {(() => {
              const iconBtnStyle = {
                flexShrink: 0, width: 30, height: 30,
                display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: 9,
                border: "1.5px solid var(--c-border)", background: "#ffffff",
                color: "var(--c-text-accent)",
                cursor: "pointer",
              };
              const REFLECT_FILTER_LABELS = { both: "Both", tasks: "Tasks", events: "Events" };
              // Sub-tab pages for the active main tab — Pending/Finished for
              // Tasks, Upcoming/Passed for Events. Shared by the mobile swipe
              // strip's label indicator and the desktop eye-icon dropdown.
              const SUB_PAGES = listMainTab === "tasks"
                ? [{ id: "overdue", label: "Overdue" }, { id: "pending", label: "Pending" }, { id: "finished", label: "Finished" }]
                : [{ id: "upcoming", label: "Upcoming" }, { id: "passed", label: "Passed" }];
              return (
                <>
                  {!listReflectMode ? (
                    <>
                      {!listSelectMode ? (
                        <>
                          {/* Mobile renders this in the topbar's Row 2 instead,
                              beside the Calendar/List switcher. */}
                          {isDesktop && (
                            <div style={{ padding: "12px 12px 8px", display: "flex", alignItems: "center", gap: 8 }}>
                              {renderListMainTabs({ flex: 1 })}
                            </div>
                          )}
                          {/* Page-indicator row — doubles as the swipe strip's
                              active-page dot (mobile) and a tap shortcut on
                              both mobile and desktop, so this filter (and the
                              "More options" menu) sits right below the main
                              Tasks/Events tabs everywhere, instead of desktop
                              having its own separate eye-icon dropdown. */}
                          {(
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: isDesktop ? "0 12px 8px" : "0 14px 8px" }}>
                              <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
                              {SUB_PAGES.map(p => {
                                const active = listSubTab === p.id;
                                return (
                                  <button key={p.id} onClick={() => setListSubTab(p.id)}
                                    style={{
                                      display: "flex", alignItems: "center", gap: 6,
                                      background: "none", border: "none", padding: 0,
                                      cursor: "pointer", fontFamily: "inherit",
                                    }}>
                                    <span style={{
                                      width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                                      background: active ? (isDarkBgTheme() ? "#ffffff" : "var(--c-primary-deep)") : "transparent",
                                      // Unpicked: soft white on dark backgrounds, soft black on light (Jade, 2026-10-11).
                                      border: active ? "none" : `1.5px solid ${isDarkBgTheme() ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.38)"}`,
                                      transition: "background 0.15s",
                                    }} />
                                    <span style={{
                                      fontSize: 13, fontWeight: active ? 800 : 600,
                                      color: active ? (isDarkBgTheme() ? "#ffffff" : "#000000") : (isDarkBgTheme() ? "rgba(255,255,255,0.78)" : "rgba(0,0,0,0.6)"),
                                      transition: "color 0.15s",
                                    }}>{p.label}</span>
                                  </button>
                                );
                              })}
                              </div>
                              {/* More options (Select / Recently deleted) — relocated here from the
                                  topbar so it sits aligned with the Overdue/Pending/Finished row
                                  instead of leaving empty space next to the view dropdown above. */}
                              {!listSelectMode && (
                                <div style={{ position: "relative", flexShrink: 0 }}>
                                  <button onClick={() => setListDrawerOpen(v => !v)} title="More options" aria-label="More options"
                                    style={{
                                      width: 30, height: 30,
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      border: "none", background: "none", padding: 0,
                                      color: isDarkBgTheme() ? "#ffffff" : "#000000", cursor: "pointer",
                                    }}>
                                    <MoreHorizontal size={18} />
                                  </button>
                                  {listDrawerOpen && (
                                    <>
                                      <div onClick={() => setListDrawerOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
                                      {/* Sits just under the dots (Jade, 2026-10-11): the button is 36px
                                          tall (global button min-height) with the dots in its middle, so
                                          "100% + 6px" left a ~24px gap. */}
                                      <div style={{
                                        position: "absolute", top: "calc(100% - 8px)", right: 0,
                                        background: "var(--c-bg-card)", borderRadius: 14,
                                        boxShadow: "0 4px 20px var(--c-shadow)",
                                        border: "1.5px solid var(--c-border)",
                                        overflow: "hidden", zIndex: 201, minWidth: 160,
                                        fontFamily: "inherit",
                                      }}>
                                        <button onClick={() => { setListSelectMode(true); setListSelectedKeys(new Set()); setListDrawerOpen(false); }}
                                          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", borderBottom: "1px solid var(--c-border-light)", background: "transparent", color: "var(--c-text)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                                          <CheckSquare size={14} /> Select
                                        </button>
                                        <button onClick={() => { setShowRecentlyDeleted(true); setListDrawerOpen(false); }}
                                          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", background: "transparent", color: "var(--c-text)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                                          <Archive size={14} /> Recently deleted
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{
                          display: "flex", gap: 6, flexShrink: 0, alignItems: "center",
                          padding: isDesktop ? "12px 12px 8px" : "2px 2px 10px",
                        }}>
                          <button
                            onClick={exitSelectMode}
                            style={{
                              display: "flex", alignItems: "center", gap: 4,
                              padding: "5px 10px", borderRadius: 9,
                              border: "1.5px solid var(--c-border)", background: "var(--c-bg-card)",
                              color: "var(--c-text)", fontSize: 11, fontWeight: 800,
                              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                            }}>
                            <X size={12} /> Cancel
                          </button>
                          <button
                            onClick={toggleSelectAll}
                            style={{
                              padding: "5px 10px", borderRadius: 9,
                              border: "1.5px solid var(--c-primary-deep)", background: "var(--c-bg-active)",
                              color: "var(--c-text-accent)", fontSize: 11, fontWeight: 800,
                              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                            }}>
                            {allSelected ? "Deselect all" : "Select all"}
                          </button>
                          <div style={{ flex: 1 }} />
                          <button
                            onClick={() => setListDeleteConfirm(true)}
                            disabled={selectedCount === 0}
                            style={{
                              display: "flex", alignItems: "center", gap: 5,
                              padding: "5px 12px", borderRadius: 9, border: "none",
                              background: selectedCount === 0 ? "var(--c-bg-active)" : "linear-gradient(135deg, #f08aa0, #e05070)",
                              color: selectedCount === 0 ? "var(--c-text-soft)" : "#fff",
                              fontSize: 11, fontWeight: 800,
                              cursor: selectedCount === 0 ? "default" : "pointer",
                              fontFamily: "inherit", whiteSpace: "nowrap",
                              opacity: selectedCount === 0 ? 0.7 : 1,
                            }}>
                            <Trash2 size={13} /> Delete{selectedCount > 0 ? ` (${selectedCount})` : ""}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {!reflectSelectMode ? (
                        <>
                        {isDesktop ? (
                          <div style={{
                            display: "flex", gap: 10, flexShrink: 0, alignItems: "center",
                            // Matches the cards' own side padding below (list-panel-scroll)
                            // so the toolbar and the cards line up on both edges.
                            padding: `12px ${reflectExpanded ? 28 : 14}px 8px`,
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, padding: 4, borderRadius: 12, background: "rgba(255,255,255,0.5)", border: "1px solid var(--c-border)" }}>
                              {reflectionEntries.length > 0 && (
                                <button
                                  onClick={() => { setReflectSelectMode(true); setReflectSelectedKeys(new Set()); }}
                                  style={{
                                    flexShrink: 0,
                                    padding: "4px 10px", borderRadius: 8,
                                    border: "none", background: "#ffffff",
                                    color: "var(--c-text)", fontSize: 11, fontWeight: 800,
                                    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                  }}>
                                  Select
                                </button>
                              )}
                              <button onClick={() => setShowDeletedReflections(true)} title="Recently deleted reflections"
                                style={{
                                  ...iconBtnStyle, border: "none",
                                  background: showDeletedReflections ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "transparent",
                                  color: showDeletedReflections ? "var(--c-on-primary)" : "var(--c-text)",
                                }}>
                                <Archive size={14} />
                              </button>
                              <button
                                onClick={() => setReflectWide(v => !v)}
                                title={reflectWide ? "Restore calendar" : "Expand reflections"}
                                style={{
                                  ...iconBtnStyle, border: "none",
                                  background: reflectWide ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "transparent",
                                  color: reflectWide ? "var(--c-on-primary)" : "var(--c-text)",
                                }}>
                                {reflectWide ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                              </button>
                            </div>
                            <div style={{ position: "relative", flexShrink: 0 }}>
                              <button
                                onClick={() => setReflectFilterDdOpen(v => !v)}
                                style={{
                                  display: "flex", alignItems: "center", gap: 5,
                                  padding: "7px 12px", borderRadius: 9,
                                  border: "1.5px solid var(--c-border)", background: "#ffffff",
                                  color: "var(--c-text)", fontSize: 11, fontWeight: 800,
                                  cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                }}>
                                {REFLECT_FILTER_LABELS[reflectFilter]} <span style={{ fontSize: 9, opacity: 0.7 }}>▼</span>
                              </button>
                              {reflectFilterDdOpen && (
                                <>
                                  <div onClick={() => setReflectFilterDdOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
                                  <div style={{
                                    position: "absolute", top: "calc(100% + 6px)", left: 0,
                                    background: "var(--c-bg-card)", borderRadius: 12,
                                    boxShadow: "0 4px 20px var(--c-shadow)",
                                    border: "1.5px solid var(--c-border)",
                                    overflow: "hidden", zIndex: 201, minWidth: 110,
                                  }}>
                                    {["both", "tasks", "events"].map(id => (
                                      <button key={id}
                                        onClick={() => { setReflectFilter(id); setReflectFilterDdOpen(false); }}
                                        style={{
                                          display: "block", width: "100%", padding: "9px 14px",
                                          border: "none",
                                          background: reflectFilter === id ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "transparent",
                                          color: reflectFilter === id ? "var(--c-on-primary)" : "var(--c-text-soft)",
                                          fontSize: 12, fontWeight: reflectFilter === id ? 800 : 600,
                                          cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                                        }}>
                                        {REFLECT_FILTER_LABELS[id]}
                                      </button>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                            <div style={{ flex: 1 }} />
                          </div>
                        ) : (
                          // Mobile: the Both/Tasks/Events scope chip now lives in the
                          // category-pills strip above (idea C); exiting is now via
                          // the dedicated back arrow in the nav row above (item 3).
                          // Select+Recently Deleted move to a pull-tab pinned to the
                          // page's edge instead of a row of their own.
                          <PullTabDrawer open={reflectMobileDrawerOpen} onToggle={() => setReflectMobileDrawerOpen(v => !v)}
                            ariaLabel="Reflection options">
                            {reflectionEntries.length > 0 && (
                              <button onClick={() => { setReflectSelectMode(true); setReflectSelectedKeys(new Set()); setReflectMobileDrawerOpen(false); }}
                                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", borderBottom: "1px solid var(--c-border-light)", background: "transparent", color: "var(--c-text)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                                <CheckSquare size={14} /> Select
                              </button>
                            )}
                            <button onClick={() => { setShowDeletedReflections(true); setReflectMobileDrawerOpen(false); }}
                              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", background: "transparent", color: "var(--c-text)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                              <Archive size={14} /> Recently deleted
                            </button>
                          </PullTabDrawer>
                        )}
                        {/* Page title, centered — keeps the dropdown flush-left in
                            the action row above so its border lines up with the
                            reflection cards' left edge below. */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: isDesktop ? "0 12px 10px" : "0 14px 10px" }}>
                          <span style={{ fontSize: 18, fontWeight: 800, color: isDarkBgTheme() ? "#ffffff" : "#000000", fontFamily: "'Georgia','Palatino',serif" }}>
                            Reflections
                          </span>
                        </div>
                        </>
                      ) : (
                        <div style={{
                          display: "flex", gap: 6, flexShrink: 0, alignItems: "center",
                          // Matches the cards' own side padding below (list-panel-scroll)
                          // so this select-mode row lines up with them on both edges.
                          padding: isDesktop ? "12px 12px 8px" : "2px 14px 10px",
                        }}>
                          <button
                            onClick={exitReflectSelectMode}
                            style={{
                              display: "flex", alignItems: "center", gap: 4,
                              padding: "5px 10px", borderRadius: 9,
                              border: "1.5px solid var(--c-border)", background: "var(--c-bg-card)",
                              color: "var(--c-text)", fontSize: 11, fontWeight: 800,
                              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                            }}>
                            <X size={12} /> Cancel
                          </button>
                          <button
                            onClick={toggleReflectSelectAll}
                            style={{
                              padding: "5px 10px", borderRadius: 9,
                              border: "1.5px solid var(--c-primary-deep)", background: "var(--c-bg-active)",
                              color: "var(--c-text-accent)", fontSize: 11, fontWeight: 800,
                              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                            }}>
                            {reflectAllSelected ? "Deselect all" : "Select all"}
                          </button>
                          <div style={{ flex: 1 }} />
                          <button
                            onClick={() => setReflectDeleteConfirm(true)}
                            disabled={reflectSelectedCount === 0}
                            style={{
                              display: "flex", alignItems: "center", gap: 5,
                              padding: "5px 12px", borderRadius: 9, border: "none",
                              background: reflectSelectedCount === 0 ? "var(--c-bg-active)" : "linear-gradient(135deg, #f08aa0, #e05070)",
                              color: reflectSelectedCount === 0 ? "var(--c-text-soft)" : "#fff",
                              fontSize: 11, fontWeight: 800,
                              cursor: reflectSelectedCount === 0 ? "default" : "pointer",
                              fontFamily: "inherit", whiteSpace: "nowrap",
                              opacity: reflectSelectedCount === 0 ? 0.7 : 1,
                            }}>
                            <Trash2 size={13} /> Delete{reflectSelectedCount > 0 ? ` (${reflectSelectedCount})` : ""}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              );
            })()}
            {/* Scrollable list — desktop scrolls inside this box; mobile lets it
                grow and scroll with the page (clarity-main), with extra bottom
                padding so the last cards clear the floating bottom nav bar. */}
            <div className="list-panel-scroll" style={{
              flex: isDesktop ? 1 : "unset",
              minHeight: isDesktop ? 0 : undefined,
              overflowY: isDesktop ? "auto" : "visible",
              display: "block",
              paddingBottom: isDesktop ? 24 : 104,
              paddingTop: 4,
              // Reflections get wider side margins than the plain task/event
              // list — more breathing room between the cards and the panel's
              // own border, especially once it's expanded full-width. On mobile
              // the reflect cards align flush with the nav row above on both
              // edges (date/Both at 12px in, Week dropdown at 12px in) — the
              // panel box itself is a touch off-centre, so the L/R pad values
              // differ (4 vs 0) to land the cards symmetric on screen.
              paddingLeft: listReflectMode ? (reflectExpanded ? 28 : (isDesktop ? 14 : 4)) : 8,
              paddingRight: listReflectMode ? (reflectExpanded ? 28 : (isDesktop ? 14 : 0)) : 4,
              touchAction: "pan-y",
              WebkitOverflowScrolling: "touch",
              position: "relative",
              zIndex: 1,
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(180,120,160,0.45) transparent",
            }}>

              {!listReflectMode && (() => {
                // One section's worth of content for a given sub-page id —
                // shared by the desktop single-section view and each page
                // of the mobile swipe strip.
                const SUB_DATA = {
                  overdue:  { items: overdueTasks,  empty: { Icon: AlertTriangle, text: "Nothing overdue" } },
                  pending:  { items: pendingTasks,  empty: { Icon: CalendarDays, text: "Nothing pending" } },
                  finished: { items: finishedTasks, empty: { Icon: Sparkles,     text: "Nothing finished yet" } },
                  upcoming: { items: upcomingEvents, empty: { Icon: CalendarDays, text: "Nothing upcoming" } },
                  passed:   { items: passedEvents,   empty: { Icon: Trophy,      text: "Nothing passed" } },
                };
                const renderSubPage = (subId) => {
                  const { items, empty } = SUB_DATA[subId];
                  const groups = groupByDate(items);
                  if (groups.length === 0) return <EmptyState {...empty} />;
                  return groups.map(g => (
                    <div key={g.date} style={{ marginBottom: 8 }}>
                      <DateHeader label={g.label} date={g.date} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {g.items.map((x, i) => <ListItem key={x.item.id || i} x={x} categories={categories} setListDetailItem={setListDetailItem} setListDetailType={setListDetailType} setListDetailDate={setListDetailDate} setCalEvents={setCalEvents} setCalReminders={setCalReminders} setTasks={setTasks} isDesktop={isDesktop} calSubTab={calSubTab} selectMode={listSelectMode} selected={listSelectedKeys.has(listKeyOf(x))} onToggleSelect={() => toggleSelectKey(listKeyOf(x))} inkKey={isDarkBgTheme() ? "dark" : "light" /* re-renders the memoised card when the background flips, for its checkbox ink */} />)}
                      </div>
                    </div>
                  ));
                };

                if (isDesktop) {
                  // Desktop: single section, switched via the eye-icon dropdown.
                  return renderSubPage(listSubTab);
                }

                // Mobile: all pages of the current main tab sit side by side in
                // a pages.length*100%-wide strip; only one (100/pages.length)%
                // pane is visible at a time (outer wrapper clips the rest) and
                // the strip's transform is driven by drag / snapped to the
                // active page — same feel as paging between homescreens. The
                // Tasks/Events control and the fixed top bar above are outside
                // this wrapper, so they never move.
                const pages = listMainTab === "tasks" ? ["overdue", "pending", "finished"] : ["upcoming", "passed"];
                const pagePct = 100 / pages.length;
                return (
                  <div style={{ overflow: "hidden" }}>
                    <div
                      ref={listSwipeStripRef}
                      onTouchStart={handleListSwipeStart}
                      onTouchEnd={handleListSwipeEnd}
                      onTouchCancel={handleListSwipeEnd}
                      style={{ display: "flex", width: `${pages.length * 100}%`, touchAction: "pan-y", willChange: "transform" }}
                    >
                      {pages.map(subId => (
                        <div key={subId} style={{ width: `${pagePct}%`, flexShrink: 0, minWidth: 0, paddingRight: 8 }}>
                          {renderSubPage(subId)}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

            </div>

            {/* ── Bulk-delete confirmation ── */}
            {/* Portalled to body + very high z-index so the dim+blur covers EVERYTHING,
                including the portalled top filter bar (which lives in its own layer). */}
            {listDeleteConfirm && createPortal(
              <div
                onClick={() => setListDeleteConfirm(false)}
                style={{
                  position: "fixed", inset: 0, zIndex: 2147483600,
                  background: "rgba(35,18,38,0.62)",
                  backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
                }}>
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    background: "var(--c-bg-card)", borderRadius: 20, padding: "26px 22px",
                    maxWidth: 320, width: "100%", textAlign: "center",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.25)", fontFamily: "'Quicksand','Nunito',sans-serif",
                  }}>
                  <ConfirmHeader title={`Are you sure you want to delete ${selectedCount === 1 ? "this item" : `these ${selectedCount} items`}?`} />
                  <div style={CONFIRM_STACK}>
                    <button onClick={() => setListDeleteConfirm(false)} style={CONFIRM_CANCEL}>
                      Cancel
                    </button>
                    <button onClick={performBulkDelete} style={CONFIRM_DANGER}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* ── Done toast ── */}
            {listDoneToast && (
              <div style={{
                position: "fixed", bottom: "calc(80px + var(--sab, 0px))", left: "50%", transform: "translateX(-50%)",
                background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))",
                color: "#fff", borderRadius: 24, padding: "10px 20px",
                fontSize: 12, fontWeight: 700, zIndex: 9999,
                boxShadow: "0 4px 16px rgba(212,88,154,0.35)",
                display: "flex", alignItems: "center", gap: 7,
                animation: "listToastIn 0.25s ease",
                pointerEvents: "none",
              }}>
                <CheckCircle size={14} /> Moved to Finished ✨
              </div>
            )}

            {/* Keyframes */}
            <style>{`
              @keyframes listCheckPop {
                0%   { transform: scale(1); }
                40%  { transform: scale(1.35); }
                100% { transform: scale(1); }
              }
              @keyframes listToastIn {
                from { opacity: 0; transform: translateX(-50%) translateY(12px); }
                to   { opacity: 1; transform: translateX(-50%) translateY(0); }
              }
              @keyframes listSwipeOut {
                0%   { opacity: 1; transform: scaleY(1); max-height: 120px; margin-bottom: 6px; }
                60%  { opacity: 0; transform: scaleY(0.7); }
                100% { opacity: 0; transform: scaleY(0); max-height: 0; margin-bottom: 0; padding: 0; }
              }
            `}</style>

            </div>{/* close card container */}

            {/* ── List panel FAB — on mobile it matches the Habits-tab FAB exactly
                (portalled to body, truly fixed, 56px, just above the bottom nav).
                On desktop it stays anchored inside the split-view list panel. ── */}
            {!showListAddPanel && !quickBar && !listSkipFlow && !listSelectMode && !listReflectMode && !externalPanelOpen && (
              isDesktop ? (
                <button
                  data-tour="planner-add-btn"
                  onClick={() => setShowListAddPanel(true)}
                  title="New task or event"
                  style={{
                    position: "absolute", bottom: 20, right: 16,
                    zIndex: 10,
                    width: 52, height: 52, borderRadius: 16,
                    border: "none",
                    background: "linear-gradient(135deg, var(--c-primary-light) 0%, var(--c-primary-deep) 100%)",
                    color: "var(--c-on-primary)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 18px rgba(180,80,160,0.45)",
                    cursor: "pointer",
                    transition: "transform 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(180,80,160,0.55)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(180,80,160,0.45)"; }}
                >
                  <Plus size={24} strokeWidth={2.5} />
                </button>
              ) : createPortal(
                <button
                  className="cal-list-fab"
                  data-tour="planner-add-btn"
                  onClick={() => openQuickAdd(today, listMainTab === "tasks" ? "task" : "event")}
                  title="New task or event"
                  style={{
                    position: "fixed", bottom: "calc(90px + var(--sab, 0px))", right: 24, zIndex: 99999,
                    width: 56, height: 56, borderRadius: 18,
                    border: "none",
                    background: "linear-gradient(135deg, var(--c-primary-light) 0%, var(--c-primary-deep) 100%)",
                    color: "var(--c-on-primary)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 18px rgba(180,80,160,0.45)",
                    cursor: "pointer",
                    transition: "transform 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(180,80,160,0.55)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(180,80,160,0.45)"; }}
                >
                  <Plus size={26} strokeWidth={2.5} />
                </button>,
                document.body
              )
            )}
          </div>
        );
      })()}

      {/* ── List Detail Panel ── */}
      {listDetailItem && listDetailType && (() => {
        const closeListDetail = () => { setListDetailItem(null); setListDetailType(null); setListDetailDate(null); };
        return (
          <ItemDetailPanel
            item={listDetailItem} type={listDetailType} date={listDetailDate}
            categories={categories} tasks={tasks} calEvents={calEvents} calReminders={calReminders}
            setTasks={setTasks} setCalEvents={setCalEvents} setCalReminders={setCalReminders}
            taskOccurrenceOverrides={taskOccurrenceOverrides} setTaskOccurrenceOverrides={setTaskOccurrenceOverrides}
            calEventOccurrenceOverrides={calEventOccurrenceOverrides} setCalEventOccurrenceOverrides={setCalEventOccurrenceOverrides} weekStart={weekStart}
            onEdit={(it, ty) => { closeListDetail(); openEditModal(it, ty); }}
            onSkip={(it, ty, occDate) => {
              closeListDetail();
              const d2 = new Date();
              setListSkipCalMonth({ y: d2.getFullYear(), m: d2.getMonth() });
              setListSkipFlow({ item: it, type: ty, origDate: occDate || listDetailDate || null, step: "confirm", rescheduledTo: null });
            }}
            onDelete={(it, ty) => {
              if (!window.confirm(`Delete "${it.title || it.name || "this item"}"?`)) return;
              closeListDetail();
              softDeleteItems([{ id: it.id, type: ty, item: it }]);
              if (ty === "task") setTasks(p => p.filter(t => t.id !== it.id));
              else if (ty === "event") setCalEvents(p => removeEventCascade(p, it.id));
              else setCalReminders(p => p.filter(r => r.id !== it.id));
            }}
            onClose={closeListDetail}
            zIndexBase={99000}
          />
        );
      })()}

      {/* ── Recently Deleted panel ── */}
      {showRecentlyDeleted && createPortal(
        (() => {
          const TYPE_LABELS = { task: "Task", event: "Event", reminder: "Reminder" };
          const TYPE_EMOJI = { task: "✅", event: "📅", reminder: "🔔" };
          const daysAgo = (iso) => {
            const diff = Date.now() - new Date(iso).getTime();
            const d = Math.floor(diff / (24 * 60 * 60 * 1000));
            if (d <= 0) return "Today";
            if (d === 1) return "Yesterday";
            return `${d} days ago`;
          };
          return (
            <div>
              <div onClick={() => setShowRecentlyDeleted(false)} style={{
                position: "fixed", inset: 0, zIndex: 99000,
                background: "rgba(30,10,40,0.38)",
                backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                animation: "fadeInBg 0.2s ease both",
              }} />
              <div className="clarity-pop-c" style={{
                position: "fixed", top: "50%", left: "50%", zIndex: 99001,
                transform: "translate(-50%, -50%)",
                background: "var(--c-bg-card)",
                borderRadius: 24,
                boxShadow: "0 8px 48px rgba(100,40,120,0.22)",
                fontFamily: "'Quicksand','Nunito',sans-serif",
                width: "min(92vw, 420px)",
                maxHeight: "82vh",
                display: "flex", flexDirection: "column",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 20px 12px", borderBottom: "1.5px solid #f5eef8" }}>
                  <Archive size={18} style={{ color: "var(--c-text-accent)" }} />
                  <div style={{ flex: 1, fontSize: 16, fontWeight: 800, color: "var(--c-text)" }}>Recently Deleted</div>
                  <button onClick={() => setShowRecentlyDeleted(false)} style={{ background: "var(--c-bg-soft)", border: "none", borderRadius: 10, width: 30, height: 30, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#a070b8", fontSize: 16 }}>✕</button>
                </div>
                <p style={{ margin: "10px 20px 0", fontSize: 11, color: "var(--c-text-mid)" }}>Items are kept here for 30 days before being permanently removed.</p>
                <div style={{ flex: 1, overflowY: "auto", padding: "10px 16px 20px" }}>
                  {deletedItems.length === 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", gap: 10 }}>
                      <Archive size={40} style={{ color: "var(--c-text-mid)" }} />
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--c-text)" }}>Nothing here</p>
                      <p style={{ margin: 0, fontSize: 11, color: "var(--c-text-mid)" }}>Deleted tasks and events show up here for 30 days.</p>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {deletedItems.map(entry => {
                        const it = entry.item;
                        return (
                          <div key={`${entry.type}:${entry.id}:${entry.deletedAt}`} style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 12px", borderRadius: 14,
                            background: "var(--c-bg-soft)", border: "1px solid var(--c-border-light)",
                          }}>
                            <span style={{ fontSize: 16, flexShrink: 0 }}>{TYPE_EMOJI[entry.type]}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {it.title || it.name || "Untitled"}
                              </div>
                              <div style={{ fontSize: 10, color: "var(--c-text-mid)", marginTop: 2 }}>
                                {TYPE_LABELS[entry.type]} · Deleted {daysAgo(entry.deletedAt)}
                              </div>
                            </div>
                            <button onClick={() => restoreDeletedItem(entry)} title="Restore"
                              style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 10, border: "1.5px solid var(--c-border)", background: "#ffffff", color: "var(--c-text-accent)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <RotateCcw size={15} />
                            </button>
                            <button onClick={() => { if (window.confirm(`Permanently delete "${it.title || it.name || "this item"}"?`)) permanentlyDeleteItem(entry); }} title="Delete permanently"
                              style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 10, border: "1.5px solid #fde0e8", background: "#ffffff", color: "#e05070", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Trash2 size={15} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* ── Quick-add bar (mobile "+") — see QuickAddBar.jsx ── */}
      {quickBar && (
        <QuickAddBar
          defaultDate={quickBar.date}
          defaultType={quickBar.type}
          weekStart={weekStart}
          categories={categories} setCategories={setCategories} savedColors={savedColors} addSavedColor={addSavedColor}
          onAdd={(t, payload) => {
            if (t === "event") setCalEvents(p => [...p, payload]);
            else setTasks(p => [...p, payload]);
          }}
          onClose={closeQuickAdd}
          portal={node => createPortal(node, document.body)}
        />
      )}

      {/* ── List Add Panel modal ── */}
      {showListAddPanel && (() => {
        const closePanel = () => { setShowListAddPanel(false); resetListAddForm(); };
        return (
          // No pop-in (Jade, 2026-10-09): the list "+" panel just appears, like the calendar one.
          <Modal onClose={closePanel} blur={false} dim={0.5} animate={false}>
            <ItemPanel
              mode="add"
              initialType={listMainTab === "tasks" ? "task" : "event"}
              defaultDate={todayStr()}
              categories={categories} setCategories={setCategories} savedColors={savedColors} addSavedColor={addSavedColor}
              onClose={closePanel}
              onSubmit={(t, payload) => {
                if (t === "event") setCalEvents(p => [...p, payload]);
                else setTasks(p => [...p, payload]);
                closePanel();
              }}
            />
          </Modal>
        );
      })()}


      {/* Desktop: planner drawer ☰ button — fixed in the upper-left corner, aligned with the
          floating sidebar. While reflections are expanded full-width, that corner shows a
          "back to list" button instead — matching the Habits Reflections page's corner. */}
      {isDesktop && createPortal(
        (listReflectMode && reflectWide) ? (
          <button
            onClick={() => setListReflectMode(false)}
            aria-label="Back to list"
            style={{
              position: "fixed", top: "calc(12px + var(--sat, 0px))", left: 12, zIndex: 201,
              width: 44, height: 44, borderRadius: 14, border: "none", cursor: "pointer",
              background: "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
            <ArrowLeft size={20} color="var(--c-text-soft)" />
          </button>
        ) : (
          <button
            onClick={onOpenSettings}
            title="Settings"
            style={{
              position: "fixed", top: "calc(12px + var(--sat, 0px))", left: 12, zIndex: 201,
              width: 44, height: 44, borderRadius: 14, border: "none", cursor: "pointer",
              background: "transparent",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
            }}>
            <span style={{ display: "block", width: 18, height: 2, background: "var(--c-text-soft)", borderRadius: 2 }} />
            <span style={{ display: "block", width: 18, height: 2, background: "var(--c-text-soft)", borderRadius: 2 }} />
            <span style={{ display: "block", width: 18, height: 2, background: "var(--c-text-soft)", borderRadius: 2 }} />
          </button>
        ),
        document.body
      )}

      {/* ── Top Bar + Category Pills — always visible across all views ─────── */}
      {(() => {
        // Category filter pills — shared between Row 1 (desktop, inline between the
        // nav arrows and the view dropdown) and Row 2 (mobile, full-width strip).
        const renderCategoryPills = () => {
          const allActive = visibleFilters === null;
          const filterList = buildFilterList();
          return (
            <>
              <button
                key="all"
                onClick={() => {
                  if (allActive) {
                    setVisibleFilters(prevFiltersRef.current && prevFiltersRef.current.size > 0 ? new Set(prevFiltersRef.current) : new Set());
                  } else {
                    prevFiltersRef.current = visibleFilters ? new Set(visibleFilters) : null;
                    selectAllFilters();
                  }
                }}
                style={{
                  flexShrink: 0,
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "5px 14px",
                  borderRadius: 20,
                  // Pills float on the gradient (both mobile & desktop): give inactive
                  // pills a frosted white surface + a dark readable label.
                  border: !allActive ? "1.5px solid rgba(255,255,255,0.7)" : "none",
                  background: allActive ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "rgba(255,255,255,0.92)",
                  WebkitBackdropFilter: !allActive ? "blur(8px)" : undefined,
                  backdropFilter: !allActive ? "blur(8px)" : undefined,
                  color: allActive ? "var(--c-on-primary)" : "#1a1a1a",
                  fontSize: 13, fontWeight: 700,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  fontFamily: "inherit",
                  lineHeight: 1.4,
                }}>
                <CheckSquare size={12} style={{marginRight:3}} />All
              </button>
              {filterList.map(({ key, label, emoji, color, cat }) => {
                const active = isFilterVisible(key) && visibleFilters !== null;
                const c = themeTone(color);   // theme-matched category accent
                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (visibleFilters === null) {
                        setVisibleFilters(new Set([key]));
                      } else {
                        toggleFilter(key);
                      }
                    }}
                    style={{
                      flexShrink: 0,
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "5px 12px 5px 8px",
                      borderRadius: 20,
                      // Pills float on the gradient (mobile & desktop): frosted white
                      // surface, category accent on the icon + border, and a dark
                      // readable label so even light categories stay legible.
                      // Dark mode (Jade, 2026-10-20): an unpicked pill was white with its
                      // label turned white too. It takes the dark raised surface, light
                      // text, and the category's vivid ink on the icon and border.
                      border: `1.5px solid ${active ? c : isDarkMode() ? categoryInk(c) + "99" : c + "66"}`,
                      // Active pills fill with the category's own colour (the same
                      // accent used on its card in the Spaces tab) and switch the label
                      // to a contrast-safe colour — white on saturated/dark colours,
                      // near-black only on very light ones — so it's always readable.
                      background: active ? c : isDarkMode() ? "var(--c-bg-raised)" : "rgba(255,255,255,0.92)",
                      WebkitBackdropFilter: "blur(8px)",
                      backdropFilter: "blur(8px)",
                      color: active ? getContrastText(c) : isDarkMode() ? "var(--c-text)" : "#1a1a1a",
                      fontSize: 13, fontWeight: active ? 700 : 600,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      fontFamily: "inherit",
                      lineHeight: 1.4,
                    }}>
                    {cat
                      ? <CategoryIcon cat={cat} size={16} color={active ? getContrastText(c) : isDarkMode() ? categoryInk(c) : c} />
                      : <span style={{ fontSize: 14, lineHeight: 1 }}>{emoji}</span>}
                    {label}
                  </button>
                );
              })}
            </>
          );
        };
        // Category filter dropdown ("All ▾") — sits beside the hamburger. Replaces
        // the old drawer's "My Calendars" + "Categories" sections with the exact
        // same data source (buildFilterList/isFilterVisible/toggleFilter) the
        // pills strip already used, just presented as a compact panel instead of
        // a full-height side drawer.
        // `bare`: when true, skip the trigger's own frosted-pill chrome — used
        // when this renders inside another shared frosted pill (mobile Row 2)
        // so we don't nest a pill inside a pill.
        // `panelAlign`: which edge of the trigger the panel hangs from —
        // "right" (default) keeps the panel's right edge flush with the
        // trigger's, growing leftward (fine when the trigger sits near the
        // screen's right edge); "left" grows rightward instead, needed when
        // the trigger sits near the left edge or the panel would run off
        // the edge of the screen.
        // `title`: the List view's "All Items ▼" heading as the trigger (Jade,
        // 2026-10-10). Same panel, but it edits the List view's OWN filter
        // (listFilters), not the calendar's.
        const renderCategoryDropdown = ({ bare, title, panelAlign = "right" } = {}) => {
          const fv = title ? listFilters : visibleFilters;
          const setFv = title ? setListFilters : setVisibleFilters;
          const prevRef = title ? prevListFiltersRef : prevFiltersRef;
          const allActive = fv === null;
          const filterLabel = allActive ? "All" : fv.size === 0 ? "None" : "Filtered";
          const filterList = buildFilterList();
          const titleLabel = allActive ? "All Items" : "Filtered";
          return (
            <div style={{ position: "relative", flexShrink: title ? 1 : 0, minWidth: 0 }}>
              {title ? (
                <button
                  onClick={(e) => {
                    if (!catFilterOpen) setCatFilterRect(e.currentTarget.getBoundingClientRect());
                    setCatFilterOpen(v => !v);
                  }}
                  aria-label={`Filter categories (${titleLabel})`}
                  style={{
                    display: "flex", alignItems: "center", gap: 5, maxWidth: "100%",
                    padding: 0, border: "none", background: "none", cursor: "pointer",
                    fontSize: 17, fontWeight: 800,
                    color: isDarkBgTheme() ? "#ffffff" : "#1a1a1a",
                    fontFamily: "'Nunito', sans-serif", whiteSpace: "nowrap",
                  }}>
                  {titleLabel} <span style={{ fontSize: 11, opacity: 0.85 }}>▼</span>
                </button>
              ) : bare ? (
                <button
                  onClick={() => setCatFilterOpen(v => !v)}
                  aria-label={`Filter categories (${filterLabel})`}
                  title={`Filter: ${filterLabel}`}
                  style={{
                    position: "relative", width: MOBILE_ROW2_H, height: MOBILE_ROW2_H, minHeight: 0, padding: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "none", background: "none", color: topbarInk(), cursor: "pointer",
                  }}>
                  <SlidersVertical size={21} strokeWidth={2.4} />
                  {/* Dot = some categories are hidden (Filtered or None). */}
                  {!allActive && <span style={{ position: "absolute", top: 5, right: 4, width: 8, height: 8, borderRadius: "50%", background: "var(--c-primary)" }} />}
                </button>
              ) : (
              <button
                onClick={() => setCatFilterOpen(v => !v)}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "6px 12px", borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.4)",
                  background: "rgba(255,255,255,0.35)",
                  backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
                  boxShadow: "0 1px 6px var(--c-shadow)",
                  color: isDarkBgTheme() ? "#ffffff" : "#1a1a1a",
                  fontSize: 14, fontWeight: 800, cursor: "pointer",
                  fontFamily: "inherit", lineHeight: 1.4,
                }}>
                {filterLabel} <span style={{ fontSize: 11, opacity: 0.85 }}>▼</span>
              </button>
              )}
              {catFilterOpen && (() => {
                // The list title sits in the inline (scrolling) topbar, inside
                // .clarity-tab-content's transform, where position:fixed is only
                // "relative to it" — so, like the Week/Month/Year menu, portal the
                // panel to <body> and anchor it to the title's measured rect.
                const portalled = title && catFilterRect;
                const menu = (
                <>
                  <div onClick={() => setCatFilterOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
                  <div style={{
                    ...(portalled ? {
                      position: "fixed", top: catFilterRect.bottom + 6, left: catFilterRect.left,
                      fontFamily: "'Quicksand','Nunito',sans-serif",
                    } : {
                      position: "absolute", top: "calc(100% + 6px)", ...(panelAlign === "left" ? { left: 0 } : { right: 0 }),
                      fontFamily: "inherit",
                    }),
                    background: "var(--c-bg-card)", borderRadius: 14,
                    boxShadow: "0 4px 20px var(--c-shadow)",
                    border: "1.5px solid var(--c-border)",
                    overflow: "hidden", zIndex: 201, minWidth: 200, maxWidth: "calc(100vw - 32px)",
                    // Mobile: a short panel (about 8 rows) that scrolls; desktop keeps the tall one.
                    maxHeight: isDesktop ? "70vh" : 290, overflowY: "auto",
                  }}>
                    {/* "All" — a row like the categories below (checkbox + label), just bold.
                        Ticked while nothing is filtered; unticking it hides everything
                        (or restores the previous selection), ticking it shows everything. */}
                    <button
                      onClick={() => {
                        if (allActive) {
                          setFv(prevRef.current && prevRef.current.size > 0 ? new Set(prevRef.current) : new Set());
                        } else {
                          prevRef.current = fv ? new Set(fv) : null;
                          setFv(null);
                        }
                      }}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 8,
                        padding: "8px 12px", background: "transparent", border: "none",
                        cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                      }}>
                      <div style={{ width: 16, height: 16, borderRadius: 5, flexShrink: 0, background: allActive ? "var(--c-primary)" : "transparent", border: "2px solid var(--c-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {allActive && <span style={{ fontSize: 9, color: "var(--c-on-primary)", fontWeight: 800 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 12.5, color: "var(--c-text)", fontWeight: 800, flex: 1 }}>All</span>
                    </button>
                    {filterList.map(({ key, label, emoji, color, cat }) => {
                      const active = fv === null || fv.has(key);
                      const c = themeTone(color);
                      return (
                        <button key={key} onClick={() => toggleFilter(key, fv, setFv)}
                          style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 8,
                            padding: "8px 12px", background: "transparent", border: "none",
                            borderTop: "1px solid var(--c-border-light)",
                            cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                          }}>
                          <div style={{ width: 16, height: 16, borderRadius: 5, flexShrink: 0, background: active ? c : "transparent", border: `2px solid ${c}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {active && <span style={{ fontSize: 9, color: "#fff", fontWeight: 800 }}>✓</span>}
                          </div>
                          {cat ? <CategoryIcon cat={cat} size={14} color={c} /> : <span style={{ fontSize: 13 }}>{emoji}</span>}
                          <span style={{ fontSize: 12.5, color: "var(--c-text)", fontWeight: active ? 700 : 500, opacity: active ? 1 : 0.6, flex: 1 }}>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
                );
                return portalled ? createPortal(menu, document.body) : menu;
              })()}
            </div>
          );
        };
        // View switcher (Week/Month/Year) — extracted so it can render either
        // inline beside the nav arrows (mobile list+reflect, per idea A: the
        // switcher belongs next to the date it controls, not pinned to the
        // opposite edge from a bare hamburger) or in its usual trailing spot
        // everywhere else, without duplicating the dropdown markup.
        const renderViewDropdown = ({ bare, panelAlign = "right" } = {}) => {
          const viewLabels = { week: "Week", calendar: "Month", year: "Year" };
          const viewOrder = ["week", "calendar", "year"];
          const activeId = listReflectMode ? reflectSpan : ((plannerView === "list" && !isDesktop) ? listScope : calSubTab);
          return (
            <div style={{ position: "relative", flexShrink: 0 }}>
              <button
                data-tour="planner-view-toggle"
                onClick={(e) => {
                  if (!viewPanelOpen && inlineTopbar) setViewPanelRect(e.currentTarget.getBoundingClientRect());
                  setViewPanelOpen(v => !v);
                }}
                style={bare ? {
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: "none",
                  background: "none",
                  color: isDarkBgTheme() ? "#ffffff" : "#1a1a1a",
                  fontSize: 14, fontWeight: 800,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  lineHeight: 1.4,
                } : {
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "6px 12px",
                  borderRadius: 999,
                  ...FROSTED_SURFACE,
                  color: isDarkBgTheme() ? "#ffffff" : "#1a1a1a",
                  fontSize: 14, fontWeight: 800,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  lineHeight: 1.4,
                }}>
                {viewLabels[activeId]} <span style={{ fontSize: 11, opacity: 0.85 }}>▼</span>
              </button>
              {viewPanelOpen && (() => {
                // The inline (scrolling) topbar sits inside .clarity-tab-content,
                // whose transform turns position:fixed into "relative to it" — the
                // tap-outside backdrop then only covered the list's height and
                // missed taps below it. So there, portal the menu to <body> and
                // anchor it to the trigger's measured screen rect instead.
                const portalled = inlineTopbar && viewPanelRect;
                const menu = (
                <>
                  <div onClick={() => setViewPanelOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
                  <div style={{
                    ...(portalled ? {
                      position: "fixed", top: viewPanelRect.bottom + 6,
                      ...(panelAlign === "left" ? { left: viewPanelRect.left } : { right: window.innerWidth - viewPanelRect.right }),
                      fontFamily: "'Quicksand','Nunito',sans-serif",
                    } : {
                      position: "absolute", top: "calc(100% + 6px)", ...(panelAlign === "left" ? { left: 0 } : { right: 0 }),
                      fontFamily: "inherit",
                    }),
                    background: "var(--c-bg-card)", borderRadius: 14,
                    boxShadow: "0 4px 20px var(--c-shadow)",
                    border: "1.5px solid var(--c-border)",
                    overflow: "hidden", zIndex: 201, minWidth: 110,
                  }}>
                    {viewOrder.map(id => (
                      <button key={id}
                        onClick={() => {
                          if (listReflectMode) setReflectSpan(id);
                          else if (plannerView === "list" && !isDesktop) setListScope(id);
                          else setCalSubTab(id);
                          setViewPanelOpen(false);
                        }}
                        style={{
                          display: "block", width: "100%",
                          padding: "9px 16px",
                          border: "none",
                          background: activeId === id ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "transparent",
                          color: activeId === id ? "var(--c-on-primary)" : "var(--c-text-soft)",
                          fontSize: 12, fontWeight: activeId === id ? 800 : 600,
                          cursor: "pointer", textAlign: "left",
                          fontFamily: "inherit",
                          borderBottom: id !== "year" ? "1px solid var(--c-border-light)" : "none",
                        }}>
                        {viewLabels[id]}
                      </button>
                    ))}
                  </div>
                </>
                );
                return portalled ? createPortal(menu, document.body) : menu;
              })()}
            </div>
          );
        };
        const mobileListReflect = !isDesktop && plannerView === "list" && listReflectMode;
        // Mobile calendar view (not desktop split-view, not list/reflect) — gets
        // the extra calendar-only row below Row 1 (the date-range/month/year
        // label with prev/next). Desktop and list/reflect keep their existing
        // (already-approved) layouts untouched.
        const mobileCal = !isDesktop && plannerView === "calendar" && !listReflectMode;
        // Mobile, non-reflect, EITHER calendar or list view — the shared,
        // unified Row 1 button layout: ☰ date label … arrows list-icon, with
        // Filter/Week/Today grouped into one frosted pill on Row 2 (plus a
        // separate ＋ beside it), per the mobile mockup.
        const mobileTopbarGrouped = !isDesktop && !listReflectMode;
        // Date label for the mobile calendar view's Row 1, beside the
        // hamburger. There are no arrows: the grid itself swipes between periods.
        const mobileCalNav = mobileCal ? (() => {
          // Width left for the label between the hamburger and the
          // calendar/list icons; ~0.53em per character in Quicksand 800.
          const avail = (typeof window !== "undefined" ? window.innerWidth : 393) - 170;
          const fit = (len) => Math.min(16.5, avail / (len * 0.53));
          if (calSubTab === "week") {
            const sd = new Date(weekDays[0] + "T00:00:00");
            const ed = new Date(weekDays[6] + "T00:00:00");
            const sameMonth = sd.getMonth() === ed.getMonth();
            const build = (name) => {
              const startLabel = `${name(sd.getMonth())} ${sd.getDate()}`;
              const endLabel = sameMonth ? `${ed.getDate()}` : `${name(ed.getMonth())} ${ed.getDate()}`;
              return { startLabel, endLabel, len: `${startLabel} – ${endLabel}, ${ed.getFullYear()}`.length };
            };
            // Full month names unless they'd have to shrink below 14px — then
            // "Sep 27 – Oct 3, 2026", which never shoves the icons off-screen.
            let parts = build(i => MONTHS[i]);
            if (fit(parts.len) < 14) parts = build(i => MONTHS[i].slice(0, 3));
            return { label: <>{parts.startLabel} – {parts.endLabel}, <span style={{ fontStyle: "italic" }}>{ed.getFullYear()}</span></>, fontSize: fit(parts.len) };
          }
          if (calSubTab === "year") return { label: <span style={{ fontStyle: "italic" }}>{calYear}</span>, fontSize: 16.5 };
          return { label: <>{MONTHS[calMonth]} <span style={{ fontStyle: "italic" }}>{calYear}</span></>, fontSize: fit(MONTHS[calMonth].length + 5) };
        })() : null;
        const topbar = (
        <div style={{
          // Offset by --sat rather than padding by it. Padding would grow the
          // topbar's measured height (--cal-topbar-h), which the content below
          // is offset by — and the app shell has ALREADY pushed that content
          // down past the status bar, so the padding version counted the status
          // bar twice and left a gap above the grid. Content still can't slide
          // under the clock: .clarity-main starts below it and clips its own
          // scrolling children.
          ...(inlineTopbar ? {
            // Scrolls away with the list rather than floating over it (the
            // transparent pinned bar showed tasks through its labels). order:-1
            // lifts it above the list, since it's rendered after it in this
            // flex column; the negative margin cancels .clarity-tab-content's
            // mobile side padding so every button lands on exactly the same
            // pixel as in the pinned calendar-view bar.
            position: "relative", order: -1, margin: "0 -10px", zIndex: 200,
          } : {
            position: "fixed", top: "calc(var(--in-app-banner-h, 0px) + var(--sat, 0px))", left: 96, right: 0, zIndex: 200,
          }),
          // Topbar has no background of its own (mobile Row 1's pastel band
          // was removed too) — content behind shows through.
          background: "transparent",
          backdropFilter: "none", WebkitBackdropFilter: "none",
          borderBottom: "none",
          boxShadow: "none",
          display: "flex", flexDirection: "column",
          paddingLeft: 12,
          paddingRight: 12,
          fontFamily: "'Quicksand','Nunito',sans-serif",
        }}
        ref={topbarRef}
        className="cal-topbar"
        >
          {/* ── Row 1 (unified): hamburger · nav arrows · serif title · view switcher · Today · ＋ ──
              No background band — the topbar is fully transparent now; the
              category/view dropdown triggers get their own frosted pill
              instead so they still read as tappable. */}
          <div style={{
            position: "relative", display: "flex", alignItems: "center", gap: 6,
            paddingTop: 10, paddingBottom: 0,
            ...(mobileTopbarGrouped ? { marginLeft: -12, marginRight: -12, paddingLeft: 12, paddingRight: 12 } : {}),
          }}>
            {/* Left: ☰ hamburger — hidden on desktop (relocated to upper-left
                corner), and hidden while browsing reflections on mobile too
                (only the back arrow leads there, matching the Habits
                Reflections page). */}
            {!listReflectMode && (
              <button onClick={onOpenSettings}
                className="cal-drawer-btn"
                aria-label="Settings"
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: "8px 9px 8px 4px",
                  display: "flex", flexDirection: "column", gap: 4, alignItems: "center", justifyContent: "center",
                  flexShrink: 0, borderRadius: 10,
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#ffffff"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}>
                {/* Same 3-bar mark as every other tab's hamburger (App.jsx), instead
                    of the thinner "☰" glyph this used to render. */}
                <span className="keep-white" style={{ display: "block", width: 20, height: 2.5, background: menuLine, borderRadius: 2 }} />
                <span className="keep-white" style={{ display: "block", width: 20, height: 2.5, background: menuLine, borderRadius: 2 }} />
                <span className="keep-white" style={{ display: "block", width: 20, height: 2.5, background: menuLine, borderRadius: 2 }} />
              </button>
            )}

            {/* Date label — sits beside the hamburger now (calendar view only;
                list view has no date to navigate). Filter/Week/Today moved
                down to the Row 2 frosted pill; the nav arrows moved to the
                upper-right, beside the list/calendar toggle. */}
            {mobileTopbarGrouped && mobileCal && (
              <span style={{
                // Sized to the space left, so a long cross-month week label
                // never shoves the calendar/list icons off the right edge.
                fontSize: mobileCalNav.fontSize, fontWeight: 800,
                color: isDarkBgTheme() ? "#ffffff" : "#1a1a1a",
                fontFamily: "'Quicksand','Nunito',sans-serif", whiteSpace: "nowrap",
                minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
                paddingRight: 3, // room for the italic year's overhang
              }}>
                {mobileCalNav.label}
              </span>
            )}

            {/* "All Items" title — list view's equivalent of the date label
                above, sitting beside the hamburger the same way (has to be
                BEFORE the flex:1 spacer below, or the spacer shoves it all
                the way to the right edge instead). Nunito Bold, not italic. */}
            {/* It's the category filter's trigger too (Jade, 2026-10-10). */}
            {mobileTopbarGrouped && !mobileCal && renderCategoryDropdown({ title: true, panelAlign: "left" })}
            {mobileTopbarGrouped && <div style={{ flex: 1 }} />}

            {/* Back arrow — only while browsing reflections, leads back to the
                list view instead of relying on re-tapping the toolbar's
                reflections toggle. Nudged so its left edge lines up with the
                action row's dropdown / the cards below (both use 14px, or
                28px expanded) instead of this topbar's own 12px — matches
                how the Habits Reflections page keeps all three flush. */}
            {/* Desktop expanded view: back arrow lives in the fixed upper-left
                corner instead (swapped in for the planner drawer button
                there) — keeping it here too would duplicate it. */}
            {listReflectMode && !(isDesktop && reflectWide) && (
              <button onClick={() => setListReflectMode(false)} aria-label="Back to list"
                style={{ flexShrink: 0, width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent", padding: 0, color: isDarkBgTheme() ? "#ffffff" : "#000000", cursor: "pointer", marginLeft: reflectWide ? 16 : 2 }}>
                <ArrowLeft size={18} />
              </button>
            )}

            {/* Nav title with arrows — context-aware per view; always shown on desktop
                (split-view), and also on mobile list-view while browsing reflections
                (where they page through reflection history instead of the calendar
                grid, which isn't shown in that mode). While browsing reflections on
                mobile it's centered on the FULL row via absolute positioning (not
                flex:1) so its center doesn't skew toward whichever side has a
                narrower sibling. */}
            {(plannerView === "calendar" || isDesktop || (plannerView === "list" && !isDesktop && listReflectMode)) && !mobileCal && <div style={{
              display: "flex", alignItems: "center", gap: 4, userSelect: "none",
              ...((!isDesktop && plannerView === "list" && listReflectMode) ? { position: "absolute", left: "50%", top: 0, bottom: 0, transform: "translateX(-50%)" } : {}),
            }}>
              <button
                onClick={() => {
                  if (listReflectMode) {
                    if (reflectSpan === "week") reflectGoPrevWeek();
                    else if (reflectSpan === "year") setReflectYear(y => y - 1);
                    else reflectPrevMonth();
                  } else if (calSubTab === "week") goToPrevWeek();
                  else if (calSubTab === "year") setCalYear(y => y - 1);
                  else prevMonth();
                }}
                style={{ background: "#ffffff", border: "1.5px solid var(--c-border)", cursor: "pointer", color: "var(--c-text-accent)", fontSize: 15, padding: "4px 9px", lineHeight: 1, fontWeight: 700, borderRadius: 99, flexShrink: 0, display: "flex", alignItems: "center", boxShadow: "0 1px 4px var(--c-shadow)", ...(isDesktop ? { ...FROSTED_SURFACE, color: isDarkBgTheme() ? "#ffffff" : "#1a1a1a" } : null) }}
              ><ChevronLeft size={15} /></button>
              <span style={{
                fontWeight: 700,
                // Long month names (February, September, November, December) widened this
                // label enough to shove the whole trailing group — view dropdown, Today,
                // ＋ — off the right edge (Today got clipped). Two things keep every
                // top-bar button at a FIXED position regardless of the month:
                //   1) a fixed label width, so the nav block never changes size, and
                //   2) a smaller font on the long month names so they still fit that width.
                // Applies to BOTH week and month views (the visible label is month+year
                // in each). Year view shows just the year, so it stays at the base size.
                fontSize: listReflectMode
                  ? (reflectSpan === "week" ? 14 : 17)
                  : (() => {
                      if (calSubTab === "year") return 16;
                      const mName = calSubTab === "week"
                        ? MONTHS[new Date(weekAnchor + "T00:00:00").getMonth()]
                        : MONTHS[calMonth];
                      return mName.length >= 8 ? 13.5 : 16;
                    })(),
                // Neutral label (black on light themes, white on dark) instead of the
                // accent-tinted colour, per request.
                color: isDarkBgTheme() ? "#ffffff" : "#000000",
                // Fixed width in calendar mode pins the arrows + trailing buttons in
                // place; reflect mode keeps a min-width since its labels vary in length.
                ...(listReflectMode ? { minWidth: 118 } : { width: 116 }),
                textAlign: "center", letterSpacing: "-0.01em",
                fontFamily: "'Georgia','Palatino',serif", whiteSpace: "nowrap",
              }}>
                {listReflectMode ? (
                  reflectSpan === "year"
                    ? <span style={{ fontStyle: "italic" }}>{reflectYear}</span>
                    : reflectSpan === "week"
                      ? (() => {
                          const { start, end } = reflectDateWindow;
                          const sd = new Date(start + "T00:00:00"), ed = new Date(end + "T00:00:00");
                          const sameMonth = sd.getMonth() === ed.getMonth();
                          const startLabel = `${MONTHS[sd.getMonth()].slice(0, 3)} ${sd.getDate()}`;
                          const endLabel = sameMonth ? `${ed.getDate()}` : `${MONTHS[ed.getMonth()].slice(0, 3)} ${ed.getDate()}`;
                          return <>{startLabel}–{endLabel}, <span style={{ fontStyle: "italic" }}>{ed.getFullYear()}</span></>;
                        })()
                      : <>{MONTHS[reflectMonth]} <span style={{ fontStyle: "italic" }}>{reflectYear}</span></>
                ) : (
                  calSubTab === "year"
                    ? <span style={{ fontStyle: "italic" }}>{calYear}</span>
                    : calSubTab === "week"
                      ? (() => { const wa = new Date(weekAnchor + "T00:00:00"); return <>{MONTHS[wa.getMonth()]} <span style={{ fontStyle: "italic" }}>{wa.getFullYear()}</span></>; })()
                      : <>{MONTHS[calMonth]} <span style={{ fontStyle: "italic" }}>{calYear}</span></>
                )}
              </span>
              <button
                onClick={() => {
                  if (listReflectMode) {
                    if (reflectSpan === "week") reflectGoNextWeek();
                    else if (reflectSpan === "year") setReflectYear(y => y + 1);
                    else reflectNextMonth();
                  } else if (calSubTab === "week") goToNextWeek();
                  else if (calSubTab === "year") setCalYear(y => y + 1);
                  else nextMonth();
                }}
                style={{ background: "#ffffff", border: "1.5px solid var(--c-border)", cursor: "pointer", color: "var(--c-text-accent)", fontSize: 15, padding: "4px 9px", lineHeight: 1, fontWeight: 700, borderRadius: 99, flexShrink: 0, display: "flex", alignItems: "center", boxShadow: "0 1px 4px var(--c-shadow)", marginLeft: 6, ...(isDesktop ? { ...FROSTED_SURFACE, color: isDarkBgTheme() ? "#ffffff" : "#1a1a1a" } : null) }}
              ><ChevronRight size={15} /></button>
            </div>}

            {/* View switcher — pinned to the page's right edge while browsing
                reflections on mobile, matching the Habits Reflections page,
                instead of sitting inline beside the date. */}
            {mobileListReflect && (
              <div style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}>
                {renderViewDropdown()}
              </div>
            )}

            {/* Week/Month/Year — single dropdown for mobile list view, sitting
                right before the list/calendar toggle. No pill chrome (per
                request) — plain black/white text like the rest of the
                topbar. Calendar view uses the separate inline segmented
                control in Row 2 instead. */}
            {mobileTopbarGrouped && !mobileCal && (
              <div style={{ marginRight: 8 }}>
                {renderViewDropdown({ bare: true })}
              </div>
            )}

            {/* Center: on desktop the category pills live inline here (between the nav
                arrows and the view dropdown), scrollable so they never overlap. On mobile
                this is just a spacer and the pills stay in Row 2 below. */}
            {isDesktop ? (
              <div className="cal-pills-inline" style={{
                flex: 1, minWidth: 0,
                display: "flex", alignItems: "center", gap: 5,
                overflowX: "auto",
                scrollbarWidth: "none", msOverflowStyle: "none",
                margin: "0 10px", padding: "2px 0",
              }}>
                {renderCategoryPills()}
              </div>
            ) : (
              // No flex:1 here while the nav-arrows block itself is centering (it takes
              // flex:1 in that case) — otherwise the two flex:1 siblings would split the
              // row and the arrows would center within only their half, not the whole row.
              // mobileTopbarGrouped already placed its own trailing spacer earlier in the row.
              !(plannerView === "list" && listReflectMode) && !mobileTopbarGrouped && <div style={{ flex: 1 }} />
            )}

            {/* View switcher — trailing position everywhere except mobile
                list+reflect, where it now renders inline above instead.
                While browsing reflections, nudged left so its right edge
                lines up with the cards below (14px/28px expanded) instead
                of this topbar's own 12px inset. */}
            {/* Week/Month/Year dropdown — the desktop "More options" button that
                used to sit here (Select / Recently deleted) moved down into the
                Overdue/Pending/Finished row below the Tasks/Events tabs, same
                placement as mobile, so this dropdown now sits right next to
                the "+" add button in the Tasks/Events list view. */}
            {!mobileListReflect && !mobileTopbarGrouped && (
              <div style={{ marginRight: (isDesktop && listReflectMode) ? (reflectWide ? 16 : 2) : 3 }}>
                {renderViewDropdown()}
              </div>
            )}

            {/* Week view lock — disables resizing task/event blocks so a scroll
                gesture that brushes past one can't be hijacked into a resize
                drag. Desktop/non-grouped-mobile copy; mobileCal gets its own
                in Row 2 below. */}
            {/* Phones only (Jade, 2026-10-21): with a mouse, a scroll can't turn
                into a resize by accident, so desktop has no lock. */}
            {plannerView === "calendar" && !mobileCal && !isDesktop && calSubTab === "week" && (
              <button
                onClick={() => setWeekResizeLocked(v => !v)}
                data-tour="planner-week-lock"
                aria-label={weekResizeLocked ? "Unlock task/event resizing" : "Lock task/event resizing (prevents accidental resize while scrolling)"}
                title={weekResizeLocked ? "Unlock resizing" : "Lock resizing"}
                style={{
                  width: 30, height: 30, borderRadius: "50%", flexShrink: 0, marginRight: 6,
                  display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                  background: weekResizeLocked ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "#ffffff",
                  border: weekResizeLocked ? "none" : "1.5px solid var(--c-border)",
                  color: weekResizeLocked ? "var(--c-on-primary)" : "var(--c-text-accent)",
                  boxShadow: "0 1px 4px var(--c-shadow)",
                }}>
                {weekResizeLocked ? <Lock size={15} /> : <LockOpen size={15} />}
              </button>
            )}

            {/* Today pill — hidden in list view (and skipped here for mobileCal,
                which renders its own copy right after the view dropdown above) */}
            {plannerView === "calendar" && !mobileCal && <button
              onClick={() => { setCalMonth(now().getMonth()); setCalYear(now().getFullYear()); setSelectedDate(today); setWeekAnchor(today); }}
              style={{
                fontSize: 13, fontWeight: 800, fontFamily: "'Quicksand', sans-serif", flexShrink: 0,
                color: (calSubTab === "week" ? !weekDays.includes(today) : calSubTab === "year" ? calYear !== now().getFullYear() : (calMonth !== now().getMonth() || calYear !== now().getFullYear())) ? "var(--c-on-primary)" : "var(--c-text-accent)",
                background: (calSubTab === "week" ? !weekDays.includes(today) : calSubTab === "year" ? calYear !== now().getFullYear() : (calMonth !== now().getMonth() || calYear !== now().getFullYear())) ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "#ffffff",
                border: (calSubTab === "week" ? !weekDays.includes(today) : calSubTab === "year" ? calYear !== now().getFullYear() : (calMonth !== now().getMonth() || calYear !== now().getFullYear())) ? "none" : "1.5px solid var(--c-border)",
                borderRadius: 20, padding: "5px 12px", cursor: "pointer", transition: "all 0.2s", lineHeight: 1,
              }}>
              Today
            </button>}

            {calSubTab !== "year" && !listReflectMode && !mobileTopbarGrouped && (
            <button
              className="cal-topbar-add-btn"
              data-tour="planner-add-btn"
              onClick={() => { if (plannerView === "list") { if (isDesktop) setShowListAddPanel(true); else openQuickAdd(today, listMainTab === "tasks" ? "task" : "event"); } else { setSelectedDate(today); setCalPanel("add"); } }}
              style={{
                width: 30, height: 30, borderRadius: "50%",
                background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))",
                border: "none", color: "#fff", fontSize: 20,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", fontWeight: 300, lineHeight: 1,
                boxShadow: "0 2px 8px rgba(212,88,154,0.3)", flexShrink: 0,
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(212,88,154,0.45)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(212,88,154,0.3)"; }}>
              ＋
            </button>
            )}

            {/* Calendar/List icons — upper-right, level with the date label. */}
            {mobileTopbarGrouped && renderPlannerViewSwitch()}
          </div>

          {/* ── Row 2 (mobile). Calendar view: filter icon (left edge),
              Week/Month/Year (centre), Today icon (right edge). List view:
              the Tasks/Events tabs. ── */}
          {mobileTopbarGrouped && (
            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 6, paddingBottom: 10, paddingTop: mobileCal ? 0 : 16, height: LIST_TABS_H, boxSizing: "content-box" }}>
              {mobileCal ? (
                <>
                  {/* Filter icon stays at its natural size on the left (-4px puts
                      its centre under the hamburger's bars); Week/Month/Year is
                      pinned to an explicit left offset matching the date label's
                      own left edge in Row 1 (12px hamburger inset + 36px hamburger
                      width + 6px row gap = 54px from the screen edge — this row
                      itself starts 12px in, so the offset here is 54-12=42),
                      instead of the old two flex:1 sides which centred it and let
                      its position drift with whatever the filter/Today icons
                      happened to need. */}
                  {/* top -3 (not a transform: the filter panel's position:fixed backdrop
                      would break inside one): the filter icon sat a little low next to the Week/Month/
                      Year pill (Jade, 2026-10-06) — only this button moves; lock and
                      Today stay where they are. */}
                  <div style={{ flexShrink: 0, display: "flex", justifyContent: "flex-start", marginLeft: -4, position: "relative", top: -3 }}>
                    {renderCategoryDropdown({ bare: true, panelAlign: "left" })}
                  </div>
                  {/* -7px: the active tab's underline sits at the bottom of the
                      button's own box, a few px below where the filter/lock/Today
                      icons' glyphs actually end (measured: icon glyphs bottom out
                      around y=80-82.5, the underline at y=88) — nudging the whole
                      group up brings the underline level with those icons' bottom
                      edges instead of hanging visibly lower than them. */}
                  <div style={{ position: "absolute", left: 42, top: "50%", transform: "translateY(calc(-50% - 7px))" }}>
                    {renderCalSpanTabs()}
                  </div>
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                    {calSubTab === "week" && (
                      <button
                        onClick={() => setWeekResizeLocked(v => !v)}
                data-tour="planner-week-lock"
                        aria-label={weekResizeLocked ? "Unlock task/event resizing" : "Lock task/event resizing (prevents accidental resize while scrolling)"}
                        title={weekResizeLocked ? "Unlock resizing" : "Lock resizing"}
                        style={{
                          width: 40, height: 40, minHeight: 0, borderRadius: "50%", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                          background: "none", border: "none", padding: 0,
                          // Locked on a dark background / dark mode: a light, bright shade of
                          // the accent (the deep one vanished there; Jade, 2026-10-26).
                          color: weekResizeLocked ? (isDarkBgTheme() ? "color-mix(in srgb, var(--c-primary) 45%, #fff)" : "var(--c-primary-deep)") : topbarInk(),
                          touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
                        }}>
                        {weekResizeLocked ? <Lock size={19} /> : <LockOpen size={19} />}
                      </button>
                    )}
                    <button
                      onClick={() => { setCalMonth(now().getMonth()); setCalYear(now().getFullYear()); setSelectedDate(today); setWeekAnchor(today); setTodayBump(n => n + 1); }}
                      aria-label="Go to today" title="Today"
                      // Tap target is 60×52 — much bigger than the 25px icon, which
                      // stays exactly where it was (the negative margins cancel the
                      // extra size), so a slightly-off finger still lands on it.
                      style={{
                        width: 60, height: 52, margin: "-8px -12px -8px 0", minHeight: 0,
                        padding: "0 17.5px 0 0", flexShrink: 0, boxSizing: "border-box",
                        display: "flex", alignItems: "center", justifyContent: "flex-end",
                        border: "none", background: "none", cursor: "pointer",
                        touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
                      }}>
                      <style>{`@keyframes calTodayBump { 0% { transform: scale(1); } 35% { transform: scale(0.82); } 70% { transform: scale(1.14); } 100% { transform: scale(1); } }`}</style>
                      {/* Rounded-square "today" mark with today's date inside. */}
                      <span key={todayBump} style={{
                        width: 25, height: 25, boxSizing: "border-box", borderRadius: 7,
                        border: `2.2px solid ${topbarInk()}`, color: topbarInk(),
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12.5, fontWeight: 800, fontFamily: "'Nunito', sans-serif", lineHeight: 1,
                        animation: todayBump ? "calTodayBump 0.4s ease-out" : "none",
                      }}>
                        {now().getDate()}
                      </span>
                    </button>
                  </div>
                </>
              ) : (
                // Flush with the task/event cards below. Cards sit inset from
                // this row's own edges by list-panel-scroll's 8px/4px L/R padding
                // plus the swipe-page wrapper's own 8px right padding (measured:
                // cards land 6px further right and 10px short of this row's full
                // width) — matched here instead of a bare 100%, which is what
                // left the pill reading slightly wider than the cards.
                renderListMainTabs({ width: "calc(100% - 16px)", marginLeft: 6, height: LIST_TABS_H + 4, boxSizing: "border-box" })
              )}
            </div>
          )}

          {/* Row 2 list sub-tabs (Upcoming/Finished/Missed) moved into the list
              card container itself — see the list-view block above. */}

          {/* ── Row 2: Category filter pills (mobile only) — now shown ONLY in
              list-view while browsing reflections (they filter reflections by
              category there). Plain calendar-mode category filtering moved into
              the "All ▾" dropdown beside the hamburger in Row 1. ── */}
          {!isDesktop && mobileListReflect && (() => {
              const REFLECT_FILTER_LABELS = { both: "Both", tasks: "Tasks", events: "Events" };
              return (
                <div style={{ display: "flex", gap: 5, alignItems: "center", paddingBottom: 10, paddingTop: 0 }}>
                  <style>{`.cal-topbar ::-webkit-scrollbar { display: none; }`}</style>
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <button
                      onClick={() => setReflectFilterDdOpen(v => !v)}
                      style={{
                        display: "flex", alignItems: "center", gap: 5,
                        padding: "6px 12px", borderRadius: 20,
                        border: "1.5px solid rgba(255,255,255,0.7)", background: "rgba(255,255,255,0.92)",
                        WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
                        color: "#1a1a1a", fontSize: 11, fontWeight: 700,
                        cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                      }}>
                      {REFLECT_FILTER_LABELS[reflectFilter]} <span style={{ fontSize: 9, opacity: 0.7 }}>▼</span>
                    </button>
                    {reflectFilterDdOpen && (
                      <>
                        <div onClick={() => setReflectFilterDdOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
                        <div style={{
                          position: "absolute", top: "calc(100% + 6px)", left: 0,
                          background: "var(--c-bg-card)", borderRadius: 12,
                          boxShadow: "0 4px 20px var(--c-shadow)",
                          border: "1.5px solid var(--c-border)",
                          overflow: "hidden", zIndex: 201, minWidth: 110,
                        }}>
                          {["both", "tasks", "events"].map(id => (
                            <button key={id}
                              onClick={() => { setReflectFilter(id); setReflectFilterDdOpen(false); }}
                              style={{
                                display: "block", width: "100%", padding: "9px 14px",
                                border: "none",
                                background: reflectFilter === id ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "transparent",
                                color: reflectFilter === id ? "var(--c-on-primary)" : "var(--c-text-soft)",
                                fontSize: 12, fontWeight: reflectFilter === id ? 800 : 600,
                                cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                              }}>
                              {REFLECT_FILTER_LABELS[id]}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.5)", flexShrink: 0 }} />
                  <div style={{
                    display: "flex", gap: 5, overflowX: "auto", alignItems: "center",
                    scrollbarWidth: "none", msOverflowStyle: "none",
                    WebkitOverflowScrolling: "touch", touchAction: "pan-x",
                    flex: 1, minWidth: 0,
                  }}>
                    {renderCategoryPills()}
                  </div>
                </div>
              );
          })()}

        </div>
        );
        return inlineTopbar ? topbar : createPortal(topbar, (document.getElementById("portal-root") || document.body));
      })()}
      {/* ── CALENDAR VIEWS — always shown on desktop (split-view), calendar-only mode on
          mobile. Hidden entirely when the list view's reflections panel is expanded wide
          (desktop only) — that's the one case where the calendar grid steps aside. ─ */}
      {(plannerView === "calendar" || isDesktop) && !(isDesktop && plannerView === "list" && listReflectMode && reflectWide) && <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}><>


      {/* ── YEAR VIEW sub-tab (placeholder — implemented in Fix 5) ── */}
      {calSubTab === "year" && (() => {
        const yearNow = now().getFullYear();
        const todayStr_ = todayStr();

        // Helpers: does a date-string have any items? (memoized in yearDayDots
        // above — falls back to a live scan for dates outside the visible year)
        const getDayDots = (ds) => {
          const cached = ds.startsWith(`${calYear}-`) ? yearDayDots.get(ds) : null;
          if (cached) return cached;
          const hasTasks    = tasks.some(t => isTaskOccurringOn(t, ds));
          const hasEvents   = calEvents.some(e => isOccurringOn(e, ds));
          const hasRemind   = calReminders.some(r => isOccurringOn(r, ds));
          return { hasTasks, hasEvents, hasRemind, any: hasTasks || hasEvents || hasRemind };
        };

        // One year's mini-month grid, parameterised by year so the pager can
        // render prev/current/next side by side during a swipe.
        const renderPane = (calYear, role) => (
            <>
            {/* ── mini-month grid (4 cols on desktop, 2 on mobile) — the grid itself
                 scrolls within the card on mobile (12 months don't fit at once),
                 instead of getting silently clipped by the card's overflow:hidden. ── */}
            <div className={isDesktop ? undefined : "clarity-no-scrollbar"}
              // Every pane (live and mid-swipe) opens at the same scroll depth,
              // so neighbouring years line up while sliding.
              data-sync-scroll="1"
              ref={el => { if (el && !el._scrolled) { el._scrolled = true; el.scrollTop = yearScrollPosRef.current; } }}
              onScroll={role === "live" ? (e => { yearScrollPosRef.current = e.currentTarget.scrollTop; syncCalScroll(e.currentTarget); }) : undefined}
              style={{
              display: "grid", gridTemplateColumns: isDesktop ? "repeat(4, 1fr)" : "repeat(2, 1fr)",
              gap: 0,
              flex: 1,
              minHeight: 0,
              gridAutoRows: isDesktop ? "minmax(0, 1fr)" : undefined,
              overflowY: isDesktop ? undefined : "auto",
              WebkitOverflowScrolling: "touch",
            }}>
              {Array.from({ length: 12 }, (_, mi) => {
                const mDayCount  = daysInMonth(calYear, mi);
                const mOffset    = dowOffset(new Date(calYear, mi, 1).getDay());
                const isCurrentM = calYear === yearNow && mi === now().getMonth();
                const cols = isDesktop ? 4 : 2;

                return (
                  <div key={mi} style={{
                    background: "var(--c-cal-surface)",
                    borderRight: (mi + 1) % cols === 0 ? "none" : "var(--c-cal-line-w) solid var(--c-cal-line)",
                    borderBottom: mi >= 12 - cols ? "none" : "var(--c-cal-line-w) solid var(--c-cal-line)",
                    padding: isDesktop ? "12px 18px 12px" : "14px 14px 14px",
                    display: "flex", flexDirection: "column", gap: 0,
                  }}>

                    {/* Month label + current month dot */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <div
                        onClick={() => { setCalMonth(mi); setCalSubTab("calendar"); }}
                        style={{
                          fontSize: 13, fontWeight: 700,
                          color: isCurrentM ? "var(--c-text-accent)" : "var(--c-cal-ink-strong)",
                          letterSpacing: "0.05em",
                          cursor: "pointer",
                          textTransform: "uppercase",
                        }}>
                        {MONTHS[mi].slice(0, 3)}
                      </div>
                      {isCurrentM && (
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--c-text-accent)", flexShrink: 0 }} />
                      )}
                    </div>

                    {/* Day-of-week headers */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 10 }}>
                      {weekdayHeaderLetter.map((d, di) => (
                        <div key={di} style={{ textAlign: "center", fontSize: 10, color: "var(--c-cal-ink-strong)", fontWeight: 600, lineHeight: "16px" }}>{d}</div>
                      ))}
                    </div>

                    {/* Day cells */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", rowGap: 3 }}>
                      {Array.from({ length: mOffset }, (_, ei) => <div key={`e${ei}`} />)}
                      {Array.from({ length: mDayCount }, (_, di) => {
                        const dayNum = di + 1;
                        const ds = `${calYear}-${String(mi+1).padStart(2,"0")}-${String(dayNum).padStart(2,"0")}`;
                        const isTod = ds === todayStr_;
                        const isSel = ds === selectedDate;
                        const dots  = getDayDots(ds);

                        return (
                          <div
                            key={ds}
                            onClick={() => {
                              // Same day-detail panel as Month view (Tasks/Events/
                              // Reminders grouped card) — Year view used to open its
                              // own differently-styled flat-list panel here, which
                              // looked inconsistent with tapping the same date from
                              // Month view.
                              setSelectedDate(ds);
                              setCalPanel("day");
                            }}
                            style={{
                              display: "flex", flexDirection: "column",
                              alignItems: "center", cursor: "pointer",
                            }}>
                            <div style={{
                              width: 24, height: 24, borderRadius: "50%",
                              background: isTod ? "var(--c-primary)" : isSel ? "var(--c-bg-card)" : "transparent",
                              border: isSel && !isTod ? "1.5px solid var(--c-text-accent)" : "none",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 11,
                              fontWeight: isTod || isSel ? 700 : dots.any ? 600 : 400,
                              color: isTod ? "var(--c-on-primary)" : isSel ? "var(--c-text-accent)" : "var(--c-cal-ink-strong)",
                              lineHeight: 1,
                            }}>
                              {dayNum}
                            </div>
                            {/* Event dots */}
                            {dots.any && !isTod && (
                              <div style={{ display: "flex", gap: 1, marginTop: 1 }}>
                                {dots.hasEvents  && <div style={{ width: 3, height: 3, borderRadius: "50%", background: "#c07be8" }} />}
                                {dots.hasTasks   && <div style={{ width: 3, height: 3, borderRadius: "50%", background: "#f5b800" }} />}
                                {dots.hasRemind  && <div style={{ width: 3, height: 3, borderRadius: "50%", background: "#3bbfb5" }} />}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
        );
        // Same treatment as week/month: unmount the 12 mini-month grid while an
        // add/edit panel is open so the panel scrolls over nothing heavy.
        // Was: hide/unmount the grid while an add/edit panel is open (a scroll-jank
        // workaround). Jade, 2026-10-05: that teardown made the add panel open late and the
        // grid vanishing behind the dim read as a second fade — so the grid now stays put.
        const yearPanelOpen = false;
        return (
          <div className="cal-subview" {...calSwipeProps} style={{ display: yearPanelOpen ? "none" : "flex", flexDirection: "column",
            // flex:1 fills the parent .clarity-tab-content, which is ALREADY a
            // flex-bounded column sized to the real visible area (address-bar-safe).
            // minHeight:0 is what actually lets this flex child shrink below its
            // tall content so the mini-month grid's own overflowY:auto (below)
            // has something to scroll. This container itself doesn't scroll —
            // the grid is the single scroll surface, avoiding a nested
            // double-scrollbar. No extra bottom padding here either: mobile's
            // bottom-nav clearance is already reserved once, up at .clarity-main
            // (padding-bottom:140px) — stacking more here just shrank the
            // available height for no reason.
            flex: 1, minHeight: 0,
            paddingTop: 0, overflow: "hidden" }}>
            {!yearPanelOpen && (
              /* The card stays put; only the months inside slide. Always clips
                 to the rounded border so the grid's square corners don't poke out. */
              <div style={{ border: "2px solid var(--c-cal-line-outer)", borderRadius: 14, overflow: "hidden", flexShrink: 0, background: "var(--c-cal-surface)", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {renderCalCarousel(renderPane, calYear - 1, calYear, calYear + 1, { seam: "var(--c-cal-line)" })}
              </div>
            )}
          </div>
        );
      })()}


      {/* ── WEEK VIEW sub-tab ── */}
      {calSubTab === "week" && (() => {
        // The whole week body is parameterised by its anchor date so the pager
        // can render prev/current/next weeks side by side during a swipe. When
        // idle only the current (fully interactive) pane is produced.
        // Holiday names sit on a line under the dates. Every pane reserves it
        // when this week or either neighbour has one, so the three swipe panes
        // keep the same header height (a mismatch would jolt the grid mid-swipe).
        // ── All-day row under the dates (Jade, 2026-10-10, from Google Calendar) ──
        // A day's holiday first, then every item with no time (= "All day" in the
        // forms; the hour grid only shows timed items). Same occurrence rules as
        // the Month grid.
        // Only items whose "All day" switch is on (Jade, 2026-10-11): a task with no
        // time that wasn't switched to All day doesn't belong here. Tasks store
        // allDay only when it's on; events always store it, so an event without
        // the field at all (saved before the switch existed, or from Google sync)
        // still counts as all-day.
        const isAllDayItem = (it, type) => !it.time && (it.allDay === true || (type === "event" && it.allDay === undefined));
        const allDayEntries = (ds) => {
          const list = applyFilter([
            ...calEvents.filter(e => isOccurringOn(e, ds) && isAllDayItem(e, "event")).map(e => ({ item: e, type: "event" })),
            ...tasks.filter(t => isTaskOccurringOn(t, ds) && isAllDayItem(t, "task")).map(t => ({ item: t, type: "task" })),
            ...calReminders.filter(r => isOccurringOn(r, ds) && isAllDayItem(r, "reminder")).map(r => ({ item: r, type: "reminder" })),
          ]);
          return holidays[ds] ? [{ holiday: holidays[ds] }, ...list] : list;
        };
        const ALLDAY_COLLAPSED = 3;   // lines per day while collapsed
        // The row is as tall as the week on screen needs — no empty lines (Jade):
        // the most any of its days has, up to 3 while collapsed. The swipe
        // neighbours use the same height so the panes stay level mid-swipe; a
        // neighbour day with more shows "+N", and the row re-fits once a swipe
        // lands on that week.
        const weekAllDayMax = (anchor) => Math.max(0, ...buildWeekDays(anchor).map(ds => allDayEntries(ds).length));
        const linesFor = (max) => weekAllDayOpen ? max : Math.min(max, ALLDAY_COLLAPSED);
        const allDayMax = weekAllDayMax(weekAnchor);
        const allDayOverflows = allDayMax > ALLDAY_COLLAPSED;
        const allDayLines = linesFor(allDayMax);
        const ALLDAY_LINE_H = 20;     // 18px pill + 2px gap
        // Swipes (Jade, 2026-10-10, like Google Calendar): the row's height is CSS,
        // not a re-render. Idle it fits this week; the moment a swipe starts it
        // eases open to fit the week sliding in too (.cal-dragging + .cal-dir-*
        // on the host, toggled in handleCalTouchMove), so that week's items are
        // already showing as it arrives — no wait for the swipe to land. When it
        // lands, the row eases to the new week's own height.
        const adPrev = (() => { const d = new Date(weekAnchor + "T00:00:00"); d.setDate(d.getDate() - 7); return linesFor(weekAllDayMax(fmt(d))); })();
        const adNext = (() => { const d = new Date(weekAnchor + "T00:00:00"); d.setDate(d.getDate() + 7); return linesFor(weekAllDayMax(fmt(d))); })();
        // Mid-swipe the height follows the finger (--cal-drag-p, 0→1, set in
        // applyCalDrag): from this week's height to the incoming week's, growing
        // or shrinking, so it is already right when the swipe lands.
        const rowH = (lines) => lines * ALLDAY_LINE_H + (lines > 0 ? 3 : 0); // + the 3px gap above it
        const adRowCss = (lines) => lines > 0 ? `height:${lines * ALLDAY_LINE_H}px;margin-top:3px;` : "height:0;margin-top:0;";
        const adDragCss = (to) => {
          const a = rowH(allDayLines), b = rowH(to);
          return `height:calc(${a}px + ${b - a}px * var(--cal-drag-p, 0));margin-top:0;box-sizing:border-box;border-top:${a || b ? 3 : 0}px solid transparent;transition:none;`;
        };
        const ALLDAY_CSS = `.wk-adrow{transition:height .22s ease,margin-top .22s ease;${adRowCss(allDayLines)}}`
          + `.cal-dragging.cal-dir-prev .wk-adrow{${adDragCss(adPrev)}}`
          + `.cal-dragging.cal-dir-next .wk-adrow{${adDragCss(adNext)}}`
          + `@media (prefers-reduced-motion: reduce){.wk-adrow{transition:none}}`;
        const anyAllDay = Math.max(allDayLines, adPrev, adNext) > 0;
        // Only a swipe toward a week with a different row height animates it.
        calAdRowDragRef.current = adPrev !== allDayLines || adNext !== allDayLines;
        const renderPane = (weekAnchor, role = "live") => {
        const wDays = buildWeekDays(weekAnchor);
        const PX_PER_HOUR = 64;
        const HOURS = Array.from({ length: 24 }, (_, i) => i);
        const LABEL_W = 54;

        const startLabel = (() => { const d = new Date(wDays[0] + "T00:00:00"); return `${MONTHS[d.getMonth()].slice(0,3)} ${d.getDate()}`; })();
        const endLabel   = (() => { const d = new Date(wDays[6] + "T00:00:00"); return `${MONTHS[d.getMonth()].slice(0,3)} ${d.getDate()}, ${d.getFullYear()}`; })();

        // Get current time for the red line
        const nowObj = now();
        const nowMinutes = toGridMinutes(nowObj.getHours() * 60 + nowObj.getMinutes(), dayStartHour);
        const nowTop = (nowMinutes / 60) * PX_PER_HOUR;
        const todayColIdx = wDays.indexOf(today);

        // Parse time string "HH:MM" → total minutes
        const parseTime = (t) => {
          if (!t) return null;
          const [h, m] = t.split(":").map(Number);
          return h * 60 + (m || 0);
        };

        // Build timed events per day (events/reminders/tasks with a time field)
        const getTimedItems = (ds) => {
          // Recurring events that naturally occur on ds but haven't been date-moved away
          const evs = calEvents.filter(e => isOccurringOn(e, ds) && e.time).filter(e => {
            const isRecurring = (e.frequencyType && e.frequencyType !== "once") || (e.repeat && e.repeat !== "None");
            if (!isRecurring) return true;
            const ov = calEventOccurrenceOverrides[`${e.id}:${ds}`];
            return !ov || (!ov.skipped && (!ov.date || ov.date === ds));
          });

          // Events moved TO ds from another occurrence
          const movedInEvs = [];
          Object.entries(calEventOccurrenceOverrides).forEach(([key, ov]) => {
            if (ov.date !== ds) return;
            const colonIdx = key.indexOf(":");
            const evId = key.slice(0, colonIdx);
            const origOccDate = key.slice(colonIdx + 1);
            if (origOccDate === ds) return;
            const e = calEvents.find(ev => ev.id === evId);
            if (!e || !e.time) return;
            const isRecurring = (e.frequencyType && e.frequencyType !== "once") || (e.repeat && e.repeat !== "None");
            if (!isRecurring) return;
            const effectiveItem = { ...e, time: ov.time ?? e.time, endTime: ov.endTime ?? e.endTime };
            movedInEvs.push({ item: effectiveItem, type: "event", occDate: origOccDate });
          });

          const rems = calReminders.filter(r => isOccurringOn(r, ds) && r.time);

          // Recurring tasks that naturally occur on ds but haven't been date-moved away
          const tks = tasks.filter(t => isTaskOccurringOn(t, ds) && t.time).filter(t => {
            const isRecurring = t.frequencyType && t.frequencyType !== "once";
            if (!isRecurring) return true;
            const ov = taskOccurrenceOverrides[`${t.id}:${ds}`];
            return !ov || (!ov.skipped && (!ov.date || ov.date === ds));
          });

          // Tasks whose occurrence was moved TO ds from a different date
          const movedIn = [];
          Object.entries(taskOccurrenceOverrides).forEach(([key, ov]) => {
            if (ov.date !== ds) return;
            const colonIdx = key.indexOf(":");
            const taskId = key.slice(0, colonIdx);
            const origOccDate = key.slice(colonIdx + 1);
            if (origOccDate === ds) return;
            const t = tasks.find(tk => tk.id === taskId);
            if (!t || !t.time) return;
            const isRecurring = t.frequencyType && t.frequencyType !== "once";
            if (!isRecurring) return;
            const effectiveItem = { ...t, time: ov.time ?? t.time, endTime: ov.endTime ?? t.endTime };
            movedIn.push({ item: effectiveItem, type: "task", occDate: origOccDate });
          });

          return applyFilter([
            ...evs.map(e => {
              const isRecurring = (e.frequencyType && e.frequencyType !== "once") || (e.repeat && e.repeat !== "None");
              if (!isRecurring) return { item: e, type: "event" };
              const ov = calEventOccurrenceOverrides[`${e.id}:${ds}`];
              const effectiveItem = ov ? { ...e, time: ov.time ?? e.time, endTime: ov.endTime ?? e.endTime } : e;
              return { item: effectiveItem, type: "event" };
            }),
            ...movedInEvs,
            ...rems.map(r => ({ item: r, type: "reminder" })),
            ...tks.map(t => {
              const isRecurring = t.frequencyType && t.frequencyType !== "once";
              if (!isRecurring) return { item: t, type: "task" };
              const ov = taskOccurrenceOverrides[`${t.id}:${ds}`];
              const effectiveItem = ov ? { ...t, time: ov.time ?? t.time, endTime: ov.endTime ?? t.endTime } : t;
              return { item: effectiveItem, type: "task" };
            }),
            ...movedIn,
          ]);
        };

        // All-day items (tasks + untimed events/reminders)
        const getAllDayItems = (ds) => applyFilter([
          ...tasks.filter(t => isTaskOccurringOn(t, ds)).map(t => ({ item: t, type: "task" })),
          ...calEvents.filter(e => isOccurringOn(e, ds) && !e.time).map(e => ({ item: e, type: "event" })),
          ...calReminders.filter(r => isOccurringOn(r, ds) && !r.time).map(r => ({ item: r, type: "reminder" })),
        ]);

        const fmtHour = (h) => {
          if (h === 0) return "12 AM";
          if (h < 12) return `${h} AM`;
          if (h === 12) return "12 PM";
          return `${h - 12} PM`;
        };

        // Returns "#fff" or "#1a1a1a" based on background luminance (WCAG formula) — defined at CalendarTab scope
        // occDate lets a single occurrence of a recurring task/event carry its
        // own color override without touching the shared series color.
        const itemColor = (item, type, occDate) => {
          if (type === "event") {
            const ov = occDate ? calEventOccurrenceOverrides[`${item.id}:${occDate}`] : null;
            if (ov?.color) return ov.color;
            return eventColor(item);
          }
          if (type === "reminder") {
            const cat = item.categoryId ? categories.find(c => c.id === item.categoryId) : null;
            return cat?.color || themeTone("#5bbfb5");
          }
          // task — occurrence override, then category, then explicit color, then theme-matched default
          const ov = occDate ? taskOccurrenceOverrides[`${item.id}:${occDate}`] : null;
          if (ov?.color) return ov.color;
          return taskColor(item);
        };

        const itemTextColor = (item, type, occDate) => {
          return getContrastText(itemColor(item, type, occDate));
        };

        // ── Week draw-to-create helpers (state/refs live in parent component) ─
        const WSNAP = 15; // minutes snap grid

        const wSnapMins = (raw) => Math.round(raw / WSNAP) * WSNAP;
        const wMinsToStr = (m) => `${String(Math.floor(Math.max(0, Math.min(1440, m)) / 60)).padStart(2, "0")}:${String(Math.max(0, Math.min(1440, m)) % 60).padStart(2, "0")}`;

        // Convert clientY + scroll container → snapped minutes
        const wClientYToMins = (clientY, scrollEl) => {
          if (!scrollEl) return 0;
          const rect = scrollEl.getBoundingClientRect();
          const relY = clientY - rect.top + scrollEl.scrollTop;
          return Math.max(0, Math.min(23 * 60 + 45, wSnapMins((relY / PX_PER_HOUR) * 60)));
        };

        // Detect which column was touched by clientX
        const wClientXToColIdx = (clientX, scrollEl) => {
          if (!scrollEl) return null;
          const rect = scrollEl.getBoundingClientRect();
          const relX = clientX - rect.left - LABEL_W;
          if (relX < 0) return null;
          const gridW = rect.width - LABEL_W;
          const colIdx = Math.floor((relX / gridW) * 7);
          return colIdx >= 0 && colIdx < 7 ? colIdx : null;
        };

        // Commit draw → open slot popover with correct date (also called from touch path)
        const commitWeekDraw = (colIdx, startMins, endMins, anchorClientY) => {
          const s = Math.min(startMins, endMins);
          const e2 = Math.min(1440, Math.max(startMins, endMins) + WSNAP);
          setSelectedDate(wDays[colIdx]);
          setQuickAddTime(wMinsToStr(toAbsoluteMinutes(s, dayStartHour)));
          setCalPanel("add");
          setWeekDrawState(null);
          weekMouseDrawRef.current = null;
        };

        // NOTE: mouse-up commits are now handled by the weekCommitTick useEffect in the parent
        // component (no render-phase side effects). Touch commits still call commitWeekDraw directly.

        // Ghost block derived values
        const wGhostTop    = weekDrawState ? (Math.min(weekDrawState.startMins, weekDrawState.endMins) / 60) * PX_PER_HOUR : 0;
        const wGhostHeight = weekDrawState ? Math.max(PX_PER_HOUR / 4, (Math.abs(weekDrawState.endMins - weekDrawState.startMins) / 60) * PX_PER_HOUR) : 0;
        const wGhostStart  = weekDrawState ? wMinsToStr(toAbsoluteMinutes(Math.min(weekDrawState.startMins, weekDrawState.endMins), dayStartHour)) : "";
        const wGhostEnd    = weekDrawState ? wMinsToStr(toAbsoluteMinutes(Math.min(1440, Math.max(weekDrawState.startMins, weekDrawState.endMins) + WSNAP), dayStartHour)) : "";

        // Touch handlers
        const SCROLL_THRESHOLD = 8; // px finger must move before we commit to a draw gesture
        const onWeekTouchStart = (e) => {
          if (isDragging) return;
          if (!e.target.dataset.wslot) return;
          if (slotPopover) { closeSlotPopover(); return; }
          const touch = e.touches[0];
          weekDrawTouchId.current = touch.identifier;
          // Record start position but don't start a draw yet — wait to see if it's a scroll
          weekDrawPendingRef.current = { startX: touch.clientX, startY: touch.clientY };
          // Do NOT call e.preventDefault() here — let the browser handle scroll normally until
          // we're sure this is a draw gesture (finger moved enough on touchmove)
        };

        const onWeekTouchMove = (e) => {
          const touch = Array.from(e.touches).find(t => t.identifier === weekDrawTouchId.current);
          if (!touch) return;

          // If a draw is already active, update it
          if (weekDrawState) {
            const endMins = wClientYToMins(touch.clientY, weekScrollRef.current);
            setWeekDrawState(prev => prev ? { ...prev, endMins } : null);
            e.preventDefault();
            return;
          }

          // Pending: check if finger has moved enough to be a draw gesture
          const pending = weekDrawPendingRef.current;
          if (!pending) return;
          const dy = Math.abs(touch.clientY - pending.startY);
          const dx = Math.abs(touch.clientX - pending.startX);
          // Only start a draw if the movement is more vertical than horizontal and exceeds threshold
          if (dy > SCROLL_THRESHOLD && dy > dx) {
            const colIdx = wClientXToColIdx(pending.startX, weekScrollRef.current);
            if (colIdx === null) { weekDrawPendingRef.current = null; return; }
            const startMins = wClientYToMins(pending.startY, weekScrollRef.current);
            setWeekDrawState({ colIdx, startMins, endMins: startMins + WSNAP, startClientY: pending.startY });
            weekDrawPendingRef.current = null;
            e.preventDefault();
          } else if (dx > SCROLL_THRESHOLD) {
            // Horizontal swipe — definitely a scroll, cancel pending
            weekDrawPendingRef.current = null;
          }
        };

        const onWeekTouchEnd = (e) => {
          // A plain tap (finger never moved past the draw/scroll threshold) opens
          // the add panel straight away. It used to fall through to the browser's
          // emulated mousedown/mouseup, which drew a ghost block (one full week-
          // grid render) and committed it via weekCommitTick (another) before the
          // panel's own render — the "mid-second delay" Jade saw (2026-10-06).
          // preventDefault on touchend stops those emulated mouse events.
          const tap = weekDrawPendingRef.current;
          weekDrawPendingRef.current = null;
          if (tap && !weekDrawState && !isDragging) {
            // Only a real tap: a swipe whose touchmoves went elsewhere (the
            // week carousel) must not open the panel where it started.
            const end = Array.from(e.changedTouches).find(t => t.identifier === weekDrawTouchId.current) || e.changedTouches[0];
            if (!end || Math.abs(end.clientX - tap.startX) > SCROLL_THRESHOLD || Math.abs(end.clientY - tap.startY) > SCROLL_THRESHOLD) return;
            const colIdx = wClientXToColIdx(tap.startX, weekScrollRef.current);
            if (colIdx !== null) {
              if (e.cancelable) e.preventDefault();
              // Whole hour of the tapped row — what a tap always gave (the slot's
              // own onClick, which the emulated click used to land on last).
              const startMins = Math.floor(wClientYToMins(tap.startY, weekScrollRef.current) / 60) * 60;
              commitWeekDraw(colIdx, startMins, startMins + 60, tap.startY);
            }
            return;
          }
          if (!weekDrawState) return;
          const touch = Array.from(e.changedTouches).find(t => t.identifier === weekDrawTouchId.current);
          const clientY = touch ? touch.clientY : weekDrawState.startClientY;
          const draggedMins = Math.abs(weekDrawState.endMins - weekDrawState.startMins);
          const { colIdx, startMins, endMins } = weekDrawState;
          if (draggedMins < WSNAP) {
            commitWeekDraw(colIdx, startMins, startMins + 60, clientY);
          } else {
            commitWeekDraw(colIdx, startMins, endMins, clientY);
          }
          weekDrawTouchId.current = null;
        };

        // Mouse handlers
        const onWeekMouseDown = (e) => {
          if (isDragging) return;
          if (isResizingRef.current) return; // resize just finished — ignore this mousedown
          if (!e.target.dataset.wslot) return;
          if (slotPopover) { closeSlotPopover(); return; }
          const colIdx = wClientXToColIdx(e.clientX, weekScrollRef.current);
          if (colIdx === null) return;
          const startMins = wClientYToMins(e.clientY, weekScrollRef.current);
          weekMouseDrawRef.current = { colIdx, startMins, startY: e.clientY };
          setWeekDrawState({ colIdx, startMins, endMins: startMins + WSNAP, startClientY: e.clientY });
          e.preventDefault();
        };

        // Week grid surface is now ALWAYS white (per redesign), regardless of the
        // app's selected bgTheme — grid lines no longer need the dark-theme
        // white-based variant this used to switch to for contrast against a
        // transparent, theme-colored grid.
        const wkGridLine     = "var(--c-cal-line)";
        const wkGridLineSoft = "var(--c-cal-line-soft)";

        return (
            /* ── Unified card: day headers + grid ─────────────────────────────── */
            <div className="cal-week-card" style={{
              display: "flex", flexDirection: "column",
              flex: 1, minHeight: 0,
              background: "var(--c-cal-surface)",
              border: "2px solid var(--c-cal-line-outer)",
              borderRadius: 14,
              boxShadow: "var(--c-cal-shadow)",
              overflow: "hidden",
              // Cache this heavy grid as its own GPU layer so that scrolling an
              // add/edit panel opened above it composites the cached texture
              // instead of repainting all 168 slot cells + event blocks each
              // frame — that repaint was the source of the janky panel scroll.
              transform: "translateZ(0)",
            }}>

            {/* ── Day header row (inside card) — back to the classic single-row
                layout (pre-redesign), just forced to a white background always
                (instead of theme-swapped var(--c-bg-card)) so it matches the
                always-white grid. Each day cell keeps its own borderLeft, so
                the grid's vertical column lines run continuously from the
                header straight down through the whole grid, same as before. ── */}
            {role === "live" && <style>{ALLDAY_CSS}</style>}
            <div className="wk-header" style={{
              flexShrink: 0,
              background: "var(--c-cal-surface)",
              borderBottom: "var(--c-cal-line-w) solid var(--c-cal-line)",
              paddingTop: 4,
            }}>
              <div style={{ display: "flex", alignItems: "stretch", position: "relative" }}>
                {/* Time-label spacer (same as grid). Holds the chevron that opens /
                    closes the all-day row when some day has more than fits. */}
                <div className="wk-corner" style={{ width: LABEL_W, flexShrink: 0, position: "relative" }}>
                  {role === "live" && (allDayOverflows || weekAllDayOpen) && allDayLines > 0 && (
                    <button aria-label={weekAllDayOpen ? "Show fewer all-day items" : "Show all all-day items"}
                      onClick={() => setWeekAllDayOpen(o => !o)}
                      style={{ position: "absolute", bottom: 4, left: 0, right: 0, margin: "0 auto", width: 32, height: 28, border: "none", background: "none", cursor: "pointer", color: "var(--c-cal-ink-soft)", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                      {weekAllDayOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  )}
                </div>
                {wDays.map((ds, i) => {
                  const dObj = new Date(ds + "T00:00:00");
                  const isToday = ds === today;
                  const dayName = ["SUN","MON","TUE","WED","THU","FRI","SAT"][dObj.getDay()];
                  const entries = allDayEntries(ds);
                  // A neighbour fits its own week (the row grows to it mid-swipe).
                  const paneLines = role === "side" ? linesFor(weekAllDayMax(weekAnchor)) : allDayLines;
                  const overflow = entries.length > paneLines;
                  const shown = overflow ? entries.slice(0, Math.max(0, paneLines - 1)) : entries;
                  // Any tap on the day's header opens its day panel (Jade).
                  const openDay = () => { setSelectedDate(ds); setWeekDayPanel(true); setCalPanel("day"); };
                  const pill = { width: "100%", boxSizing: "border-box", height: 18, borderRadius: 5, padding: "0 4px", fontSize: 11, fontWeight: 800, lineHeight: "18px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "clip", cursor: "pointer" };
                  return (
                    <div key={ds} className="wk-hday" onClick={openDay} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", padding: "4px 0 6px", borderLeft: "var(--c-cal-line-w) solid var(--c-cal-line)", background: "transparent", position: "relative", cursor: "pointer" }}>
                      {/* Fixed light-theme colors — this header is always white now,
                          regardless of bgTheme, so it can't use theme-swapped text tokens
                          (those go near-white under dark themes and vanish). */}
                      <span style={{ fontSize: 11, fontWeight: 700, color: isToday ? "var(--c-cal-today-name)" : "var(--c-cal-ink-soft)", letterSpacing: "0.08em" }}>{dayName}</span>
                      <div style={{
                        width: 32, height: 32, borderRadius: "50%", marginTop: 3,
                        background: isToday ? "var(--c-primary)" : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 18, fontWeight: isToday ? 700 : 500,
                        color: isToday ? "var(--c-on-primary)" : "var(--c-cal-ink)",
                        transition: "background 0.15s",
                        boxShadow: isToday ? "0 2px 10px var(--c-shadow-btn)" : "none",
                      }}>{dObj.getDate()}</div>
                      {anyAllDay && (
                        <div className="wk-adrow" style={{ alignSelf: "stretch", marginLeft: 2, marginRight: 2, display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}>
                          {shown.map((en, k) => {
                            if (en.holiday) {
                              return <div key="holiday" title={en.holiday} style={{ ...pill, background: HOLIDAY_BG, color: HOLIDAY_TEXT }}>{en.holiday}</div>;
                            }
                            const { item, type } = en;
                            const done = type === "task" && isTaskDoneOn(item, ds, taskOccurrenceOverrides);
                            const bg = done ? "#c8d8b0" : itemColor(item, type, ds);
                            return (
                              <div key={`${type}-${item.id}`} title={item.title}
                                onClick={e => { e.stopPropagation(); closeSlotPopover(); setCalPanel(null); setWeekBlockPop({ item, type, centered: true, occDate: ds }); }}
                                style={{ ...pill, background: bg, color: getContrastText(bg), textDecoration: done ? "line-through" : "none" }}>
                                {item.title}
                              </div>
                            );
                          })}
                          {overflow && (
                            <div onClick={e => { e.stopPropagation(); setWeekAllDayOpen(true); }}
                              style={{ ...pill, textAlign: "center", color: "var(--c-cal-ink-soft)", fontWeight: 700 }}>
                              +{entries.length - shown.length}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* ── Scrollable time grid ─────────────────────────────────────────── */}
            <div
              ref={el => {
                // Only the interactive pane owns the hit-testing refs; neighbours
                // and the label overlay just mirror its scroll depth.
                if (role === "live") { dragGridRef.current = el; weekScrollRef.current = el; }
                if (el && !el._scrolled) {
                  el._scrolled = true;
                  // Restore wherever the user had scrolled to before the add/edit
                  // modal covered (and unmounted) this pane — falls back to
                  // scrolling near "now" only on a genuinely fresh mount.
                  // A fresh mount opens on a whole hour, so no sliver of the hour
                  // above peeks out under the header (its line read as a doubled
                  // header line — Jade, 2026-10-15). +2 tucks that hour's own top
                  // border under the header too.
                  const hourTop = Math.floor(Math.max(0, nowTop - 80) / PX_PER_HOUR) * PX_PER_HOUR;
                  el.scrollTop = weekScrollPosRef.current != null ? weekScrollPosRef.current : (hourTop ? hourTop + 2 : 0);
                }
              }}
              data-sync-scroll="1"
              onScroll={role === "live" ? (e => {
                weekScrollPosRef.current = e.currentTarget.scrollTop; syncCalScroll(e.currentTarget);
                // Wheel or edge scrolling during a drag: the pointer hasn't moved,
                // but the grid under it has, so re-snap the block to it.
                const p = lastDragPointRef.current;
                if (isDraggingRef.current && dragItemRef.current && p) {
                  const snap = posToSnap(p.x, p.y, wDays);
                  if (snap) { dragSnapRef.current = { snap: { date: wDays[snap.colIdx], mins: snap.mins }, wDaysList: wDays }; setDragSnap({ date: wDays[snap.colIdx], mins: snap.mins }); }
                }
              }) : undefined}
              onMouseDown={onWeekMouseDown}
              onMouseMove={e => {
                if (weekMouseDrawRef.current) return; // handled by window listener
                if (!isDragging) return;
                lastDragPointRef.current = { x: e.clientX, y: e.clientY };
                setDragPos({ x: e.clientX, y: e.clientY });
                const snap = posToSnap(e.clientX, e.clientY, wDays);
                if (snap) { dragSnapRef.current = { snap: { date: wDays[snap.colIdx], mins: snap.mins }, wDaysList: wDays }; setDragSnap({ date: wDays[snap.colIdx], mins: snap.mins }); }
              }}
              onMouseUp={e => {
                if (weekMouseDrawRef.current) return; // handled by window listener
                // The window mouseup listener handles the actual commit so both
                // in-grid and off-grid releases go through the same path.
                // Nothing extra needed here.
              }}
              onTouchStart={onWeekTouchStart}
              onTouchMove={e => {
                if (weekDrawState) { onWeekTouchMove(e); return; }
                if (!isDragging) return; // do NOT cancel longPressTimer here — event block's own touchend clears it
                e.preventDefault();
                const touch = Array.from(e.touches).find(t => t.identifier === dragTouchId.current) || e.touches[0];
                setDragPos({ x: touch.clientX, y: touch.clientY });
                const snap = posToSnap(touch.clientX, touch.clientY, wDays);
                if (snap) { dragSnapRef.current = { snap: { date: wDays[snap.colIdx], mins: snap.mins }, wDaysList: wDays }; setDragSnap({ date: wDays[snap.colIdx], mins: snap.mins }); }
              }}
              onTouchEnd={e => {
                if (weekDrawState || (weekDrawPendingRef.current && !isDragging)) { onWeekTouchEnd(e); return; }
                clearTimeout(longPressTimer.current);
                if (!isDragging) return;
                const touch = Array.from(e.changedTouches).find(t => t.identifier === dragTouchId.current) || e.changedTouches[0];
                const snap = posToSnap(touch.clientX, touch.clientY, wDays);
                if (snap) commitDrag({ date: wDays[snap.colIdx], mins: snap.mins }, wDays);
                else cancelDrag();
              }}
              onTouchCancel={() => { setWeekDrawState(null); weekDrawTouchId.current = null; weekMouseDrawRef.current = null; if (isDragging) cancelDrag(); }}
              onDragOver={e => {
                if (!listDragState.current) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                const colIdx = wClientXToColIdx(e.clientX, weekScrollRef.current);
                if (colIdx === null) { if (listDragPreview) setListDragPreview(null); return; }
                const snapped = Math.max(0, Math.min(23 * 60 + 45, wSnapMins(wClientYToMins(e.clientY, weekScrollRef.current))));
                const prev = listDragPreviewRef.current;
                if (!prev || prev.colIdx !== colIdx || prev.mins !== snapped) {
                  const next = { colIdx, mins: snapped, endMins: snapped + 15, color: listDragState.current.color, title: listDragState.current.title };
                  listDragPreviewRef.current = next;
                  setListDragPreview(next);
                }
              }}
              onDragLeave={e => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  listDragPreviewRef.current = null;
                  setListDragPreview(null);
                }
              }}
              onDrop={e => {
                e.preventDefault();
                listDragPreviewRef.current = null;
                setListDragPreview(null);
                const raw = e.dataTransfer.getData("clarity-list-item");
                if (!raw) return;
                const { id, type: itemType } = JSON.parse(raw);
                const colIdx = wClientXToColIdx(e.clientX, weekScrollRef.current);
                if (colIdx === null) return;
                const targetDate = wDays[colIdx];
                // wClientYToMins is grid-relative — convert to real wall-clock
                // time before saving.
                const snapped = toAbsoluteMinutes(Math.max(0, Math.min(23 * 60 + 45, wSnapMins(wClientYToMins(e.clientY, weekScrollRef.current)))), dayStartHour);
                const endSnapped = Math.min(24 * 60, snapped + 15);
                const toTimeStr = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
                if (itemType === "task") {
                  // Always reassign dueDate to the dropped-on day (not just when
                  // unset) — otherwise dragging an overdue task onto today/a
                  // future day here would leave its stale past due date in
                  // place and it'd stay stuck in the Overdue bucket.
                  setTasks(prev => prev.map(t => t.id === id ? { ...t, time: toTimeStr(snapped), endTime: toTimeStr(endSnapped), dueDate: targetDate } : t));
                } else if (itemType === "event") {
                  setCalEvents(prev => prev.map(ev => ev.id === id ? { ...ev, time: toTimeStr(snapped), date: targetDate } : ev));
                } else if (itemType === "reminder") {
                  setCalReminders(prev => prev.map(r => r.id === id ? { ...r, time: toTimeStr(snapped), date: r.date || targetDate } : r));
                }
                listDragState.current = null;
              }}
              className="wk-grid-scroll"
              style={{
                // A touch drag locks scrolling (the finger moves the block); a mouse
                // drag doesn't, so the wheel / edges can scroll the week under it.
                overflowY: (isDragging && !dragItem?.mouse) || weekDrawState ? "hidden" : "auto", position: "relative", flex: 1, minHeight: 0,
                touchAction: isDragging || weekDrawState || isResizingRef.current ? "none" : "pan-y",
                overflow: (isDragging && !dragItem?.mouse) || weekDrawState ? "hidden" : "auto",
                // Give the scroll container itself its own GPU layer + paint
                // containment (matching the day-agenda scroll view) so scrolling
                // doesn't repaint the 168 slot cells + event blocks each frame —
                // the parent .cal-week-card layer alone wasn't enough since this
                // inner element is the one actually scrolling.
                transform: "translateZ(0)",
                WebkitTransform: "translateZ(0)",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                willChange: "transform",
                contain: "layout paint style",
                isolation: "isolate",
              }}>
              <div style={{ position: "relative", height: PX_PER_HOUR * 24, borderBottom: `var(--c-cal-line-w) solid ${wkGridLine}` }}>

                {/* Hour rows — h is the row index (position); realHour is the
                    actual wall-clock hour that row represents once rotated
                    by dayStartHour, used for the label and for click-to-add. */}
                {HOURS.map(h => {
                  const realHour = (dayStartHour + h) % 24;
                  return (
                  <div key={h} className="wk-hrow" style={{
                    position: "absolute", top: h * PX_PER_HOUR, left: 0, right: 0,
                    height: PX_PER_HOUR,
                    display: "flex",
                    borderTop: h === 0 ? "none" : `var(--c-cal-line-w) solid ${wkGridLine}`,
                  }}>
                    {/* Time label */}
                    <div className="wk-label" style={{
                      width: LABEL_W, flexShrink: 0, textAlign: "right",
                      paddingRight: 6, paddingTop: 2,
                    }}>
                      {/* Grid is always white now, so a fixed dark label reads fine
                          without the old bgTheme-conditional background-tint hack. */}
                      <span style={{ fontSize: 11, color: "var(--c-cal-ink)", fontWeight: 600 }}>{fmtHour(realHour)}</span>
                    </div>
                    {/* 7 day columns */}
                    {wDays.map((ds, i) => (
                      <div key={ds}
                        className="wk-slot"
                        data-wslot="1"
                        onClick={(e) => {
                          if (isDragging || weekDrawState) return;
                          const hh = String(realHour).padStart(2, "0");
                          setSelectedDate(ds);
                          setQuickAddTime(`${hh}:00`);
                          setCalPanel("add");
                        }}
                        style={{
                          flex: 1,
                          borderLeft: `var(--c-cal-line-w) solid ${wkGridLine}`,
                          cursor: weekDrawState ? "crosshair" : "crosshair",
                          position: "relative",
                          background: "transparent",
                        }}
                        onMouseEnter={e => { if (!isDragging && !weekDrawState) e.currentTarget.style.background = "rgba(212,120,154,0.06)"; }}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      />
                    ))}
                  </div>
                  );
                })}

                {/* Half-hour dotted lines */}
                {HOURS.map(h => (
                  <div key={`h${h}`} style={{
                    position: "absolute",
                    top: h * PX_PER_HOUR + PX_PER_HOUR / 2,
                    left: LABEL_W,
                    right: 0,
                    borderTop: `1px dashed ${wkGridLineSoft}`,
                    pointerEvents: "none",
                    opacity: 0.7,
                  }} />
                ))}

                {/* Current time red line */}
                {todayColIdx >= 0 && (() => {
                  const colW = `calc((100% - ${LABEL_W}px) / 7)`;
                  const colLeft = `calc(${LABEL_W}px + ${todayColIdx} * (100% - ${LABEL_W}px) / 7)`;
                  return (
                    <>
                      <div style={{
                        position: "absolute",
                        top: nowTop,
                        left: colLeft,
                        width: colW,
                        height: 2.5,
                        background: "linear-gradient(90deg, var(--c-primary), var(--c-primary-deep))",
                        zIndex: 10,
                        pointerEvents: "none",
                        borderRadius: "0 2px 2px 0",
                        boxShadow: "0 0 6px var(--c-shadow-btn)",
                      }} />
                      <div style={{
                        position: "absolute",
                        top: nowTop - 5,
                        left: `calc(${LABEL_W}px + ${todayColIdx} * (100% - ${LABEL_W}px) / 7 - 6px)`,
                        width: 12, height: 12, borderRadius: "50%",
                        background: "var(--c-primary)",
                        zIndex: 10,
                        pointerEvents: "none",
                        boxShadow: "0 0 0 3px var(--c-shadow-btn), 0 0 10px var(--c-shadow-btn)",
                        animation: editModalItem ? "none" : "pulse 1.5s ease-in-out infinite",
                      }} />
                    </>
                  );
                })()}

                {/* Ghost block preview while dragging list item onto week grid */}
                {listDragPreview && (() => {
                  const { colIdx, mins, endMins, color, title } = listDragPreview;
                  const colW = `calc((100% - ${LABEL_W}px) / 7)`;
                  const colLeft = `calc(${LABEL_W}px + ${colIdx} * (100% - ${LABEL_W}px) / 7 + 1px)`;
                  const topPx = (mins / 60) * PX_PER_HOUR;
                  const heightPx = Math.max(16, ((endMins - mins) / 60) * PX_PER_HOUR - 2);
                  const toT = (m) => `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;
                  return (
                    <div style={{
                      position: "absolute", top: topPx, left: colLeft,
                      width: `calc(${colW} - 10px)`, height: heightPx,
                      background: color, borderRadius: 5,
                      padding: "3px 5px", zIndex: 20,
                      opacity: 0.75, filter: "saturate(0.85) brightness(1.05)",
                      pointerEvents: "none",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                      transition: "top 0.05s, left 0.05s",
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: "#fff", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", lineHeight: 1.2 }}>{title}</div>
                      {heightPx >= 26 && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.85)" }}>{toT(toAbsoluteMinutes(mins, dayStartHour))} – {toT(toAbsoluteMinutes(endMins, dayStartHour))}</div>}
                    </div>
                  );
                })()}

                {/* Timed event blocks */}
                {wDays.map((ds, colIdx) => {
                  const timedItems = getTimedItems(ds);
                  return timedItems.map(({ item, type, occDate }) => {
                    const blockKey = `${item.id}:${occDate ?? ds}`;
                    const startMin = parseTime(item.time);
                    if (startMin === null) return null;
                    const endMin = parseTime(item.endTime);
                    const durMin = endMin && endMin > startMin ? endMin - startMin : 60;
                    // startMin itself stays real wall-clock (resize math below
                    // depends on it) — only its grid position is rotated.
                    const topPx = (toGridMinutes(startMin, dayStartHour) / 60) * PX_PER_HOUR;
                    // During resize, use live deltaMins from resizeItem state so React controls height
                    const isBeingResized = resizeItem && resizeItem.item.id === item.id && (resizeItem.occDate === (occDate ?? ds) || !resizeItem.occDate);
                    const liveHeightPx = (() => {
                      if (!isBeingResized) return Math.max(14, (durMin / 60) * PX_PER_HOUR - 2);
                      const origEnd = resizeItem.origEndMins;
                      const delta = resizeItem.deltaMins || 0;
                      const newEnd = Math.max(startMin + SNAP_MINS, snapToGrid(origEnd + delta));
                      return Math.max(14, ((newEnd - startMin) / 60) * PX_PER_HOUR - 2);
                    })();
                    const heightPx = liveHeightPx;
                    const color = itemColor(item, type, occDate ?? ds);
                    const colW = `calc((100% - ${LABEL_W}px) / 7)`;
                    const colLeft = `calc(${LABEL_W}px + ${colIdx} * (100% - ${LABEL_W}px) / 7 + 1px)`;
                    const isBeingDragged = isDragging && dragItem?.item?.id === item.id && (dragItem?.origDate === (occDate ?? ds) || !dragItem?.origDate);

                    // ── Long-press to drag (mobile + desktop) ──
                    // weekItemRef: used to imperatively hide the element the instant drag
                    // starts — bypassing React's render cycle so there is zero white-flash
                    // between the long-press firing and the next React paint.
                    // NOTE: useRef cannot be called inside a .map() callback (Rules of Hooks).
                    // Instead we use a shared weekItemRefsMap (keyed by item.id:date for
                    // recurring tasks) declared at the CalendarTab level.
                    const weekItemRef = { current: weekItemRefsMap.current[blockKey] ?? null };
                    const handlePointerDown = (e) => {
                      // Touch fires both touch* and synthesized mouse events; ignore the
                      // synthesized mouse ones so the tap isn't handled twice (which made
                      // the detail panel flicker open/closed or open the wrong panel).
                      if (e.type === "touchstart") lastTouchRef.current = Date.now();
                      else if (Date.now() - lastTouchRef.current < 700) { e.stopPropagation(); return; }
                      // Bail if user is clicking inside the ⋮ menu — don't start drag
                      if (isMenuInteraction.current) { isMenuInteraction.current = false; return; }
                      if (e.type !== "touchstart") {
                        // Stop bubbling so onWeekMouseDown on the grid container never sees
                        // this event — without this, both handlers fire and the data-wslot
                        // check is the only thing preventing a spurious draw gesture from
                        // starting. Touch is deliberately excluded: onWeekTouchStart already
                        // no-ops via that same data-wslot check on its own, and blocking a
                        // touchstart here also blocked handleCalTouchStart (the sideways
                        // swipe-between-periods handler) on the cal-subview ancestor from
                        // ever arming — which is why swiping sideways starting on a task/
                        // event block silently did nothing while swiping empty grid space
                        // worked fine.
                        e.stopPropagation();
                      }
                      // Never start a new long-press timer if a drag is already active
                      if (isDraggingRef.current) return;
                      if (e.type === "touchstart") {
                        const touch = e.touches[0];
                        dragTouchId.current = touch.identifier;
                        tapStartRef.current = { x: touch.clientX, y: touch.clientY };
                        tapMovedRef.current = false;
                        longPressTimer.current = setTimeout(() => {
                          closeSlotPopover(); closeWeekBlockPop();
                          // ── Imperative hide: kill opacity BEFORE setState so there
                          //    is no flash between the JS callback and React's next paint ──
                          if (weekItemRef.current) {
                            weekItemRef.current.style.transition = "none";
                            weekItemRef.current.style.opacity = "0";
                            hiddenDragBlockRef.current = weekItemRef.current;
                          }
                          dragSnapRef.current = null; // never drop at the previous drag's spot
                          const di = { item, type, origDate: occDate ?? ds };
                          dragItemRef.current = di; setDragItem(di);
                          setDragPos({ x: touch.clientX, y: touch.clientY });
                          isDraggingRef.current = true; setIsDragging(true);
                          if (navigator.vibrate) navigator.vibrate(40);
                        }, 450);
                      } else if (e.button === 0) {
                        tapStartRef.current = { x: e.clientX, y: e.clientY };
                        tapMovedRef.current = false;
                        // A mouse drag starts after the long-press OR as soon as the
                        // pointer moves while held (handleBlockPointerMove), like Google
                        // Calendar (Jade, 2026-10-20). `mouse` keeps the grid scrollable
                        // during it, so the wheel and the edges can scroll the week.
                        const beginMouseDrag = (x, y) => {
                          mouseDragStartRef.current = null;
                          closeSlotPopover();
                          // ── Imperative hide: same trick for mouse ──
                          if (weekItemRef.current) {
                            weekItemRef.current.style.transition = "none";
                            weekItemRef.current.style.opacity = "0";
                            hiddenDragBlockRef.current = weekItemRef.current;
                          }
                          dragSnapRef.current = null; // never drop at the previous drag's spot
                          const di = { item, type, origDate: occDate ?? ds, mouse: true };
                          dragItemRef.current = di; setDragItem(di);
                          setDragPos({ x, y });
                          lastDragPointRef.current = { x, y };
                          isDraggingRef.current = true; setIsDragging(true);
                        };
                        mouseDragStartRef.current = beginMouseDrag;
                        longPressTimer.current = setTimeout(() => beginMouseDrag(e.clientX, e.clientY), 450);
                      }
                    };

                    // Flags the gesture as a swipe once the pointer moves beyond a small
                    // threshold. A swipe must do NOTHING — no popup, no accidental drag —
                    // so we also cancel the pending long-press timer here.
                    const handleBlockPointerMove = (e) => {
                      if (!tapStartRef.current || isDraggingRef.current || isResizingRef.current) return;
                      const p = e.touches ? e.touches[0] : e;
                      const dx = Math.abs(p.clientX - tapStartRef.current.x);
                      const dy = Math.abs(p.clientY - tapStartRef.current.y);
                      // Mouse held down and moved: start the drag right away (touch keeps
                      // the long-press, since a finger moving is a scroll or a swipe).
                      if (!e.touches && mouseDragStartRef.current && (e.buttons & 1) && (dx > 5 || dy > 5)) {
                        if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
                        tapMovedRef.current = true;
                        mouseDragStartRef.current(p.clientX, p.clientY);
                        return;
                      }
                      if (dx > 10 || dy > 10) {
                        tapMovedRef.current = true;
                        if (longPressTimer.current) {
                          clearTimeout(longPressTimer.current);
                          longPressTimer.current = null;
                        }
                      }
                    };

                    const handlePointerUp = (e) => {
                      // Ignore the synthesized mouseup that follows a touchend (see handlePointerDown).
                      if (e.type === "touchend") lastTouchRef.current = Date.now();
                      else if (Date.now() - lastTouchRef.current < 700) return;
                      // Do NOT call e.stopPropagation() here — we need the native window
                      // mouseup listener to fire so it can commit/clear the drag state.
                      // (stopPropagation on a React synthetic event also stops the native
                      // event from reaching window listeners, breaking drag-commit on release.)
                      // If a resize is actively in progress (finger moved enough), ignore this.
                      if (isResizingRef.current) { tapStartRef.current = null; tapMovedRef.current = false; return; }
                      // If a resize was PENDING (touch started on resize handle) but never
                      // activated (no significant movement), clear it so the global onUp skips
                      // committing and we fall through to open the popup instead.
                      if (resizeItemRef.current) {
                        resizeItemRef.current = null;
                        setResizeItem(null);
                        // CRITICAL: also drop resizeActive here. This handler nulls
                        // resizeItemRef *before* the window-level onUp runs, so that
                        // onUp early-returns (it guards on resizeItemRef) and never
                        // clears resizeActive itself. Left set, resizeActive keeps a
                        // non-passive touchmove listener on window, which disables the
                        // browser's fast-scroll path for the WHOLE app — that's why the
                        // edit panel (opened after tapping a block) scrolled janky while
                        // the add panel (never touches a block handle) stayed smooth.
                        setResizeActive(false);
                      }
                      if (longPressTimer.current) {
                        clearTimeout(longPressTimer.current);
                        longPressTimer.current = null;
                      }
                      mouseDragStartRef.current = null;
                      // Swipes do nothing — only a clean tap (no significant movement) opens the panel.
                      if (tapMovedRef.current) {
                        tapStartRef.current = null;
                        tapMovedRef.current = false;
                        return;
                      }
                      tapStartRef.current = null;
                      // If not dragging, treat as click/tap → open centered detail panel
                      if (!isDraggingRef.current) {
                        // Suppress the browser's synthesized "ghost click" that fires ~300ms
                        // after touchend — without this it lands on the popup's full-screen
                        // backdrop and instantly closes the panel we just opened.
                        if (e.type === "touchend" && e.cancelable) e.preventDefault();
                        closeSlotPopover();
                        setCalPanel(null);  // close any open day/add panel that might have its backdrop intercepting clicks
                        setWeekBlockPop({ item, type, centered: true, occDate: ds });
                      }
                    };

                    // ── Resize handle touch/mouse ──
                    // Simply set resize state; the global resize useEffect handles
                    // mousemove/mouseup/touchmove/touchend on window unconditionally.
                    const handleResizeDown = (e) => {
                      // Week view lock: bail out entirely, without stopping propagation
                      // or preventing default, so the touch falls through as a normal
                      // scroll/tap on the block underneath — see the handle's own
                      // touchAction below for why this alone is enough to restore
                      // native scrolling once locked.
                      if (resizeLocked) return;
                      // Always stop propagation here (touch included) — unlike the
                      // block body, this handle doesn't skip it. Letting a resize
                      // touch also bubble up to handleCalTouchStart (the swipe-between-
                      // periods arm) made every resize race against the swipe's own
                      // axis-detection threshold, which read as the resize itself
                      // having gone laggy. The resize handle is a tiny, deliberate
                      // target — worth keeping snappy over supporting a swipe that
                      // happens to start on those 12px.
                      e.stopPropagation();
                      e.preventDefault();
                      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
                      const startY = e.touches ? e.touches[0].clientY : e.clientY;
                      const origEndMins = endMin || (startMin + 60);
                      const ri = { item, type, origEndMins, startY, deltaMins: 0, occDate: occDate ?? ds };
                      // Park the resize start info but do NOT activate isResizingRef yet.
                      // onMove activates it only after significant finger movement so that a
                      // tap on the resize handle still falls through to the popup path.
                      resizeItemRef.current = ri;
                      setResizeItem(ri);
                      setResizeActive(true); // attach the passive:false move listeners for this drag
                    };

                    return (
                      <div
                        key={blockKey}
                        id={`resize-preview-${blockKey}`}
                        ref={role === "live" ? (el => { weekItemRefsMap.current[blockKey] = el; weekItemRef.current = el; }) : undefined}
                        onMouseDown={handlePointerDown}
                        onMouseMove={handleBlockPointerMove}
                        onMouseUp={handlePointerUp}
                        onTouchStart={handlePointerDown}
                        onTouchMove={handleBlockPointerMove}
                        onTouchEnd={handlePointerUp}
                        onTouchCancel={cancelDrag}
                        onMouseEnter={() => onBlockMouseEnter(item.id)}
                        onMouseLeave={onBlockMouseLeave}
                        style={{
                          position: "absolute",
                          top: topPx,
                          // Mobile columns are narrow, so a 10px inset leaves the block
                          // too thin to read — use a slimmer 4px inset on mobile (still a
                          // small gap from the grid line) and keep the roomier 10px on desktop.
                          left: colLeft,
                          width: `calc(${colW} - ${isDesktop ? 10 : 4}px)`,
                          height: heightPx,
                          background: color,
                          borderRadius: 5,
                          padding: 0,
                          zIndex: isBeingDragged ? 1 : isBeingResized ? 10 : hoveredBlockId === item.id ? 20 : 5,
                          cursor: isDragging ? "grabbing" : isBeingResized ? "ns-resize" : "default",
                          overflow: "visible",
                          boxShadow: isBeingDragged ? "none" : hoveredBlockId === item.id ? "0 4px 16px rgba(0,0,0,0.22)" : "0 1px 4px rgba(0,0,0,0.15)",
                          opacity: isBeingDragged ? 0 : 1,
                          transition: "box-shadow 0.15s",
                          userSelect: "none",
                          WebkitUserSelect: "none",
                          // touch-action is a static, per-touch browser decision made at
                          // touchstart — it can't be relaxed mid-gesture once a finger is
                          // down, which is why the Week view lock has to flip this at
                          // render time (before the next touch begins) rather than try to
                          // detect "this is really a scroll" after the fact.
                          touchAction: resizeLocked ? "pan-y" : "none",
                          clipPath: "none",
                        }}>
                        {/* Ghost outline — shown in place of original while dragging */}
                        {isBeingDragged && (
                          <div style={{
                            position: "absolute", inset: 0,
                            borderRadius: 5,
                            border: `2px dashed ${color}`,
                            background: color + "22",
                            pointerEvents: "none",
                            opacity: 1,
                          }} />
                        )}
                        {/* Inner content — overflow:hidden here so text clips, not the resize handle */}
                        <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: 5, padding: heightPx < 24 ? "1px 4px" : "3px 5px", pointerEvents: "none" }}>
                          {/* Title wraps to 2 lines once the block is tall enough to
                              actually show a 2nd line without also crowding out the
                              time row below it — otherwise it stays a single
                              ellipsized line like before. Was always single-line, so
                              anything past ~4 characters in these narrow columns
                              ellipsized immediately even when the block had the
                              height to spare.
                              -webkit-line-clamp truncates the 2nd line with a
                              trailing "…" instead of the hard cut a plain maxHeight
                              gives. It needs display:-webkit-box + WebkitBoxOrient
                              to actually clamp — every field has to be present
                              together or it silently no-ops (that's what produced
                              a blank title during earlier testing: a stray
                              duplicate DOM node was being measured, not a real
                              rendering failure of the clamp itself). */}
                          <div style={{
                            fontSize: 11, fontWeight: 800, color: itemTextColor(item, type, occDate ?? ds), lineHeight: 1.2,
                            overflow: "hidden",
                            ...(heightPx >= 42
                              ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", whiteSpace: "normal", wordBreak: "break-word" }
                              : { whiteSpace: "nowrap", textOverflow: "ellipsis" }),
                          }}>
                            {item.title}
                          </div>
                          {/* Phone: start time only, to stay readable in the narrow
                              columns. Desktop: the full range, "4:00 – 5:00 PM"
                              (Jade, 2026-10-20). */}
                          {heightPx >= 26 && (
                            <div style={{ fontSize: 10, color: itemTextColor(item, type, occDate ?? ds) === "#fff" ? "rgba(255,255,255,0.7)" : BLOCK_INK_SOFT, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {isDesktop ? formatTimeRange(item.time, item.endTime) : item.time}
                            </div>
                          )}
                        </div>
                        {/* Resize handle at bottom — outside overflow:hidden so it's always clickable */}
                        <div
                          onMouseDown={handleResizeDown}
                          onTouchStart={handleResizeDown}
                          style={{
                            position: "absolute", bottom: -4, left: 0, right: 0,
                            height: 12, cursor: resizeLocked ? "default" : "ns-resize",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            touchAction: resizeLocked ? "pan-y" : "none",
                            zIndex: 20,
                          }}>
                          <div style={{ width: 18, height: 2, borderRadius: 1, background: resizeLocked ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.6)" }} />
                        </div>
                      </div>
                    );
                  });
                })}

                {/* Ghost block while drawing (week draw-to-create) */}
                {weekDrawState && (() => {
                  const { colIdx } = weekDrawState;
                  const colW = `calc((100% - ${LABEL_W}px) / 7)`;
                  const colLeft = `calc(${LABEL_W}px + ${colIdx} * (100% - ${LABEL_W}px) / 7 + 2px)`;
                  return (
                    <div style={{
                      position: "absolute",
                      top: wGhostTop,
                      left: colLeft,
                      width: `calc(${colW} - 5px)`,
                      height: wGhostHeight,
                      background: "rgba(212,120,154,0.18)",
                      border: "2px dashed #d4789a",
                      borderRadius: 6,
                      zIndex: 15,
                      pointerEvents: "none",
                      display: "flex", flexDirection: "column", justifyContent: "center",
                      padding: "3px 5px", boxSizing: "border-box",
                      backdropFilter: "blur(2px)",
                    }}>
                      <span style={{ fontSize: 9, fontWeight: 700, color: "var(--c-text-accent)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {wGhostStart} – {wGhostEnd}
                      </span>
                    </div>
                  );
                })()}

                {/* Drag time indicator — shows snapped time in gutter */}
                {isDragging && dragSnap && (
                  <div style={{
                    position: "absolute",
                    top: (dragSnap.mins / 60) * PX_PER_HOUR - 8,
                    left: 0, width: LABEL_W - 2,
                    textAlign: "right", paddingRight: 4,
                    fontSize: 9, fontWeight: 800, color: "var(--c-text-accent)",
                    pointerEvents: "none", zIndex: 25,
                    background: "rgba(253,245,249,0.9)",
                    borderRadius: 4,
                  }}>
                    {minsToTime(toAbsoluteMinutes(dragSnap.mins, dayStartHour))}
                  </div>
                )}
                {isDragging && dragItem && dragSnap && (() => {
                  const { item, type, origDate } = dragItem;
                  // Grid-relative — kept as-is for topPx below; converted to
                  // real time only where displayed.
                  const startMin = dragSnap.mins;
                  const origStart = parseTime(item.time) || 0;
                  const origEnd   = parseTime(item.endTime) || origStart + 60;
                  const dur = origEnd - origStart;
                  const topPx = (startMin / 60) * PX_PER_HOUR;
                  const heightPx = Math.max(20, (dur / 60) * PX_PER_HOUR - 2);
                  const colIdx = wDays.indexOf(dragSnap.date);
                  if (colIdx < 0) return null;
                  const colW = `calc((100% - ${LABEL_W}px) / 7)`;
                  const colLeft = `calc(${LABEL_W}px + ${colIdx} * (100% - ${LABEL_W}px) / 7 + 1px)`;
                  const color = itemColor(item, type, origDate);
                  return (
                    <div style={{
                      position: "absolute",
                      top: topPx, left: colLeft,
                      width: `calc(${colW} - 3px)`,
                      height: heightPx,
                      background: color,
                      borderRadius: 5,
                      padding: "2px 4px",
                      zIndex: 20,
                      opacity: 0.92,
                      pointerEvents: "none",
                      border: "2px solid rgba(255,255,255,0.9)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.32), 0 2px 8px rgba(0,0,0,0.18)",
                      transform: "scale(1.03)",
                      transition: "none",
                    }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: getContrastText(color) }}>{item.title}</div>
                      <div style={{ fontSize: 8, color: getContrastText(color) === "#fff" ? "rgba(255,255,255,0.7)" : BLOCK_INK_SOFT }}>{minsToTime(toAbsoluteMinutes(startMin, dayStartHour))} – {minsToTime(toAbsoluteMinutes(startMin + dur, dayStartHour))}</div>
                    </div>
                  );
                })()}

              </div>
            </div>

            {/* close unified panel wrapper */}
            </div>
        );
        };
        const prevA = (() => { const d = new Date(weekAnchor + "T00:00:00"); d.setDate(d.getDate() - 7); return fmt(d); })();
        const nextA = (() => { const d = new Date(weekAnchor + "T00:00:00"); d.setDate(d.getDate() + 7); return fmt(d); })();
        // While the full add/edit panel is open on top, the heavy 168-cell week
        // grid is fully covered by the modal — but the modal's backdrop is only
        // 38% opaque, so without this the browser keeps compositing/repainting the
        // whole grid behind it on every scroll frame, which is what makes the
        // panel scroll janky on mobile (desktop has the paint budget to hide it).
        // Hiding the grid entirely while the panel is open removes that cost, so
        // the panel scrolls over just the plain app background — smooth. Kept
        // mounted (display:none, not unmounted) so the scroll position and swipe
        // state survive closing the panel.
        // Was: hide/unmount the grid while an add/edit panel is open (a scroll-jank
        // workaround). Jade, 2026-10-05: that teardown made the add panel open late and the
        // grid vanishing behind the dim read as a second fade — so the grid now stays put.
        const panelCoversWeek = false;
        return (
          <div className="cal-subview" {...calSwipeProps} style={{ display: panelCoversWeek ? "none" : "flex", flexDirection: "column", gap: 0, ...(isDesktop ? { flex: 1, minHeight: 0 } : { height: "calc(100vh - var(--cal-topbar-h, 96px) - 108px)" }), paddingBottom: 8, overflow: "hidden", paddingTop: 0 }}>
            {/* Fully UNMOUNT the 168-cell week grid while the add/edit panel is open —
                display:none alone still leaves the whole subtree in React (reconciled on
                every re-render) and can keep its GPU backing stores alive on Android,
                which is why editing over the week view scrolled janky while the same
                edit over the lighter month grid did not. Unmounting drops all of that;
                on close it remounts and re-centres on "now". */}
            {!panelCoversWeek && renderCalCarousel(renderPane, prevA, weekAnchor, nextA, {
              // Must match renderPane's LABEL_W and the card's 2px border.
              gutter: 54, overlay: () => renderPane(weekAnchor, "overlay"),
              frameStyle: { background: "var(--c-cal-surface)", border: "2px solid var(--c-cal-line-outer)", borderRadius: 14, boxShadow: "var(--c-cal-shadow)" },
            })}
          </div>
        );
      })()}


      {/* ── Month-only content ───────────────────────────────────────────────── */}
      {/* Mobile needs its own scroll container (like the Year view) — the calendar
          wrapper is overflow:hidden, so without this the last week(s) of a 5–6 row
          month get clipped off the bottom. Desktop stays a flex passthrough. */}
      {calSubTab === "calendar" && (() => {
      // One month's grid, parameterised by {y, m} so the pager can render
      // prev/current/next side by side during a swipe.
      const renderPane = ({ y, m }) => {
        const calYear = y, calMonth = m;
        const dayCount = daysInMonth(calYear, calMonth);
        const startOffset = dowOffset(new Date(calYear, calMonth, 1).getDay());
        // Always 6 rows, so every month's lines meet exactly during a swipe.
        const prevDayCount = daysInMonth(calMonth === 0 ? calYear - 1 : calYear, (calMonth + 11) % 12);
        const trailing = 42 - startOffset - dayCount;
        const adjacentCell = (key, num, colIdx) => (
          <div key={key} style={{ borderTop: `var(--c-cal-line-w) solid ${monthGridLine}`, borderRight: colIdx === 6 ? "none" : `var(--c-cal-line-w) solid ${monthGridLine}`, background: "var(--c-cal-other-month)", boxSizing: "border-box", padding: "6px 6px 6px" }}>
            <div style={{ width: 28, height: 28, marginLeft: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "var(--c-cal-ink-soft)" }}>{num}</div>
          </div>
        );
        return (
        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0, padding: 0, boxSizing: "border-box", gridAutoRows: "minmax(0, 1fr)", flex: 1, minHeight: 0 }}
          onDragLeave={e => {
            if (!e.currentTarget.contains(e.relatedTarget) && monthDragOverState.current) {
              monthDragOverState.current.style.background = "";
              monthDragOverState.current.style.outline = "none";
              monthDragOverState.current = null;
            }
          }}
        >
          {Array.from({ length: startOffset }, (_, i) => adjacentCell(`e${i}`, prevDayCount - startOffset + 1 + i, i))}
          {Array.from({ length: dayCount }, (_, i) => {
            const day = i + 1;
            const ds = `${calYear}-${String(calMonth + 1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
            const isToday = ds === today;
            const isSelected = ds === selectedDate;
            const colIdx = (startOffset + i) % 7;
            const isLastCol = colIdx === 6;

            // Gather filtered items (memoized in monthItemsByDate above)
            const dayItems = monthItemsByDate.get(ds) || { dayTasks: [], dayEvents: [], dayReminders: [] };
            const { dayTasks, dayEvents, dayReminders } = dayItems;
            const filteredItems = applyFilter([
              ...dayEvents.map(e => ({ item: e, type: "event" })),
              ...dayTasks.map(t => ({ item: t, type: "task" })),
              ...dayReminders.map(r => ({ item: r, type: "reminder" })),
            ]);

            // Color bars per type — occurrence override wins over the shared item color.
            const bars = filteredItems.map(({ item, type }) => {
              if (type === "event") {
                const ov = calEventOccurrenceOverrides[`${item.id}:${ds}`];
                return { label: item.title, color: ov?.color || eventColor(item) };
              }
              if (type === "task") {
                const ov = taskOccurrenceOverrides[`${item.id}:${ds}`];
                const color = isTaskDoneOn(item, ds, taskOccurrenceOverrides) ? "#c8d8b0" : (ov?.color || taskColor(item));
                return { label: item.title, color };
              }
              const rCat = item.categoryId ? categories.find(c => c.id === item.categoryId) : null;
              return { label: item.title, color: rCat?.color || "#5bbfb5" };
            });
            // A public holiday leads the cell (shown whatever the category filter).
            if (holidays[ds]) bars.unshift({ label: holidays[ds], color: HOLIDAY_BG, text: HOLIDAY_TEXT });
            const visibleBars = bars.slice(0, 3);

            // --c-cal-* follow Settings → Calendar Grid (white card / match background).
            const defaultBg = isSelected && !isToday ? "var(--c-cal-selected)" : (colIdx === 0 || colIdx === 6) ? "var(--c-cal-weekend)" : "var(--c-cal-surface)";
            return (
              <div key={ds}
                onClick={() => openDate(ds)}
                onDragOver={e => {
                  e.preventDefault();
                  if (!listDragState.current) return;
                  e.dataTransfer.dropEffect = "copy";
                  const el = e.currentTarget;
                  if (monthDragOverState.current && monthDragOverState.current !== el) {
                    monthDragOverState.current.style.background = "";
                    monthDragOverState.current.style.outline = "none";
                  }
                  monthDragOverState.current = el;
                  el.style.background = "rgba(212,120,154,0.18)";
                  el.style.outline = "2px solid var(--c-primary)";
                  el.style.outlineOffset = "-2px";
                }}
                onDragLeave={e => {
                  if (e.currentTarget.contains(e.relatedTarget)) return;
                  e.currentTarget.style.background = "";
                  e.currentTarget.style.outline = "none";
                  if (monthDragOverState.current === e.currentTarget) monthDragOverState.current = null;
                }}
                onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.style.background = "";
                  e.currentTarget.style.outline = "none";
                  if (monthDragOverState.current === e.currentTarget) monthDragOverState.current = null;
                  const raw = e.dataTransfer.getData("clarity-list-item");
                  if (!raw) return;
                  const { id, type: itemType } = JSON.parse(raw);
                  if (itemType === "task") {
                    setTasks(prev => prev.map(t => t.id === id ? { ...t, dueDate: ds } : t));
                  } else if (itemType === "event") {
                    setCalEvents(prev => prev.map(ev => ev.id === id ? { ...ev, date: ds } : ev));
                  } else if (itemType === "reminder") {
                    setCalReminders(prev => prev.map(r => r.id === id ? { ...r, date: ds } : r));
                  }
                  listDragState.current = null;
                }}
                style={{
                  borderTop: `var(--c-cal-line-w) solid ${monthGridLine}`,
                  borderRight: isLastCol ? "none" : `var(--c-cal-line-w) solid ${monthGridLine}`,
                  background: defaultBg,
                  cursor: "pointer",
                  display: "flex", flexDirection: "column",
                  // Trimmed from 6/6/6 + a 3px gap: freed-up room is what lets a
                  // 3rd bar actually fit inside the cell instead of being clipped
                  // by overflow:hidden below the visible 2 (see the bars-container
                  // note further down for the rest of the budget).
                  // Side padding 2px (was 6): event bars span almost the whole
                  // cell, like Google Calendar's month view (Jade, 2026-10-10).
                  padding: "5px 2px 1px",
                  gap: 1,
                  boxSizing: "border-box",
                  transition: "background 0.1s",
                  position: "relative",
                  overflow: "hidden",
                  height: "100%",
                  minHeight: 0,
                }}
                onMouseEnter={e => { if (!isToday && !isSelected) e.currentTarget.style.background = "#fdf0f7"; }}
                onMouseLeave={e => { e.currentTarget.style.background = defaultBg; }}
              >
                {/* Day number — top-left Google Calendar style. marginBottom trimmed
                    (4->1) so the first event bar sits right under the date number
                    instead of leaving a gap — frees up the vertical room a 3rd
                    bar needs. */}
                <div style={{ alignSelf: "flex-start", marginLeft: 8, marginBottom: 1, flexShrink: 0 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    // Same as the Week view's today circle: several backgrounds' text-accent is too pale to show (Terra, Mist).
                    background: isToday ? "var(--c-primary)" : "transparent",
                    // Selected = ring + number in the calendar ink, which every background sets
                    // to read on its grid (dark on light ones, white on Dusk etc.). text-accent was
                    // too pale on several, and the accent vanishes on dark ones (Jade, 2026-10-16).
                    border: isSelected && !isToday ? "2px solid var(--c-cal-ink)" : "none",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13,
                    fontWeight: isToday || isSelected ? 700 : 400,
                    color: isToday ? "var(--c-on-primary)" : isSelected ? "var(--c-cal-ink)" : (colIdx === 0 || colIdx === 6) ? "var(--c-cal-ink-soft)" : "var(--c-cal-ink)",
                  }}>
                    {day}
                  </div>
                </div>

                {/* Event bars — full-width colored bars with truncated text. Up to
                    3 render (visibleBars slices to 3), but at the old padding/gap
                    a 3rd bar's height didn't fit inside the cell and was clipped
                    by overflow:hidden above — hence "only 2 ever show" even though
                    a 3rd was already in the DOM. Tighter padding/gap here (plus the
                    day-number trims above) makes the 3rd bar's full height fit. */}
                <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
                  {visibleBars.map((bar, idx) => (
                    <div key={idx} style={{
                      width: "100%", boxSizing: "border-box",
                      background: bar.color,
                      borderRadius: 4,
                      padding: "1px 3px",
                      // Smaller text and no "…": the bar shows as many letters
                      // as fit and just stops, like Google Calendar (Jade).
                      fontSize: 9.5, fontWeight: 600, lineHeight: 1.4, letterSpacing: "-0.01em",
                      color: bar.text || getContrastText(bar.color),
                      overflow: "hidden", whiteSpace: "nowrap", textOverflow: "clip",
                      flexShrink: 0,
                    }}>
                      {bar.label}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {Array.from({ length: trailing }, (_, i) => adjacentCell(`te${i}`, i + 1, (startOffset + dayCount + i) % 7))}
        </div>
        );
      };
      const prevM = calMonth === 0 ? { y: calYear - 1, m: 11 } : { y: calYear, m: calMonth - 1 };
      const nextM = calMonth === 11 ? { y: calYear + 1, m: 0 } : { y: calYear, m: calMonth + 1 };
      // Same treatment as the week view: while an add/edit panel is open, unmount
      // the month grid behind it so the panel scrolls over nothing heavy — matching
      // the buttery-smooth List view (which mounts no calendar grid at all).
      // Was: hide/unmount the grid while an add/edit panel is open (a scroll-jank
        // workaround). Jade, 2026-10-05: that teardown made the add panel open late and the
        // grid vanishing behind the dim read as a second fade — so the grid now stays put.
        const monthPanelOpen = false;
      return (
        <div className="cal-subview" {...calSwipeProps} style={{ display: monthPanelOpen ? "none" : "flex", flexDirection: "column", flex: 1, minHeight: 0, overflowY: isDesktop ? "hidden" : "auto", WebkitOverflowScrolling: "touch" }}>
          {!monthPanelOpen && (
            /* Frame + weekday row stay put; only the date grid slides. */
            <div style={{ marginLeft: 0, marginRight: 0, border: "2px solid var(--c-cal-line-outer)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--c-cal-shadow)", marginBottom: isDesktop ? 0 : 12, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
                background: "var(--c-cal-surface)",
                borderBottom: `var(--c-cal-line-w) solid ${monthGridLine}`,
                alignItems: "center",
              }}>
                {weekdayHeaderShort.map((d, idx) => (
                  <div key={d} style={{
                    textAlign: "center",
                    fontSize: 11,
                    color: "var(--c-cal-ink-strong)", // black, white on dark backgrounds (Jade)
                    fontWeight: 700,
                    padding: "10px 0 9px",
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                  }}>{d}</div>
                ))}
              </div>
              {renderCalCarousel(renderPane, prevM, { y: calYear, m: calMonth }, nextM, { seam: monthGridLine })}
            </div>
          )}
        </div>
      );
      })()}      {/* ── Edit Event Modal (centered popup, same UI as task edit) ─────────── */}
      {showEditEventModal && editEventForm && createPortal(
        <>
          <div onClick={() => { setShowEditEventModal(false); setEditingEventId(null); setEditEventForm(null); }}
            style={{ position: "fixed", inset: 0, zIndex: 8000, background: "rgba(60,30,50,0.5)" }} />
          <div onClick={e => e.stopPropagation()} style={{
            position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            zIndex: 8001, width: "min(640px,95vw)", maxHeight: "90vh", overflowY: "auto",
            background: "var(--c-bg-card)", borderRadius: 20, boxShadow: "0 8px 40px rgba(212,120,154,0.28)",
            padding: "24px 28px 28px", fontFamily: "'Quicksand','Nunito',sans-serif",
          }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 1 }}>📅 Edit Event</span>
              <button onClick={() => { setShowEditEventModal(false); setEditingEventId(null); setEditEventForm(null); }}
                style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--c-text-soft)", lineHeight: 1, padding: 0 }}><X size={16} /></button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Title */}
              <div><p style={S.label}>TITLE</p>
                <input value={editEventForm.title || ""} onChange={e => setEditEventForm(p => ({ ...p, title: e.target.value }))} style={S.input} placeholder="Event title" />
              </div>
              {/* Date + Times */}
              <div><p style={S.label}>DATE</p>
                <NativePickerInput type="date" value={editEventForm.date || ""} onChange={e => setEditEventForm(p => ({ ...p, date: e.target.value }))} style={S.input} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}><p style={S.label}>START TIME</p>
                  <NativePickerInput type="time" value={editEventForm.time || ""} onChange={e => setEditEventForm(p => ({ ...p, time: e.target.value }))} style={S.input} />
                </div>
                <div style={{ flex: 1 }}><p style={S.label}>END TIME</p>
                  <NativePickerInput type="time" value={editEventForm.endTime || ""} onChange={e => setEditEventForm(p => ({ ...p, endTime: e.target.value }))} style={S.input} />
                </div>
              </div>
              {/* Location */}
              <div><p style={S.label}>LOCATION / PLACE</p>
                <LocationAutocomplete value={editEventForm.location || ""} onChange={(v) => setEditEventForm(p => ({ ...p, location: v }))} inputStyle={S.input} />
              </div>
              {/* Reminders — native-only, see CalendarExtras.jsx for why */}
              {Capacitor.isNativePlatform() && (
                <div>
                  {/* See the matching note in App.jsx's Edit Task/Event modal:
                      without this there was no way to stop a saved event's
                      reminders short of deselecting each chip individually. */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <p style={{ ...S.label, margin: 0 }}>REMINDERS</p>
                    {!!(editEventForm.reminders && editEventForm.reminders.length) && (
                      <button type="button" onClick={() => setEditEventForm(p => ({ ...p, reminders: [] }))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-soft)", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 3, padding: 0, fontFamily: "inherit" }}
                        onMouseEnter={e => e.currentTarget.style.color = "#e8647a"}
                        onMouseLeave={e => e.currentTarget.style.color = "#c0a0b8"}>
                        <X size={12} /> Remove
                      </button>
                    )}
                  </div>
                  <ReminderPicker value={editEventForm.reminders} onChange={(reminders) => setEditEventForm(p => ({ ...p, reminders }))} onlyDayWeek={!editEventForm.time}
                    alertType={editEventForm.reminderType} onAlertTypeChange={(reminderType) => setEditEventForm(p => ({ ...p, reminderType }))}
                    alarmSound={editEventForm.alarmSound} onAlarmSoundChange={(alarmSound) => setEditEventForm(p => ({ ...p, alarmSound }))}
                    alarmSoundUri={editEventForm.alarmSoundUri} alarmSoundLabel={editEventForm.alarmSoundLabel}
                    onCustomSoundChange={(picked) => setEditEventForm(p => ({ ...p, alarmSoundUri: picked?.uri || "", alarmSoundLabel: picked?.label || "" }))} />
                </div>
              )}
              {/* Attachments */}
              <div><p style={S.label}>ATTACHMENTS</p>
                <AttachmentField value={editEventForm.attachments} onChange={(attachments) => setEditEventForm(p => ({ ...p, attachments }))} />
              </div>
              {/* Category + Color */}
              <div><p style={S.label}>CATEGORY (AUTO-COLOR)</p>
                <select value={editEventForm.categoryId || ""} onChange={e => setEditEventForm(p => ({ ...p, categoryId: e.target.value }))} style={{ ...S.input, cursor: "pointer" }}>
                  <option value="">No category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.title}</option>)}
                </select>
              </div>
              {/* Full Color Picker — Edit Event */}
              <ModalColorPicker
                currentColor={editEventForm.color || ""}
                onColorChange={(c) => setEditEventForm(p => ({ ...p, color: c, categoryId: c ? "" : p.categoryId }))}
                onSaveColor={addSavedColor}
                savedColors={savedColors}
                S={S}
              />
              {/* Repeat */}
              <div><p style={S.label}>REPEAT</p>
                <select value={editEventForm.repeat || "None"} onChange={e => setEditEventForm(p => ({ ...p, repeat: e.target.value, repeatDays: [], repeatDates: [] }))} style={{ ...S.input, cursor: "pointer" }}>
                  {["None","Daily","Weekly","Monthly"].map(r => <option key={r}>{r}</option>)}
                </select>
                {editEventForm.repeat === "Weekly" && (
                  <div style={{ marginTop: 8 }}>
                    <p style={{ ...S.label, marginBottom: 10 }}>REPEAT ON DAYS</p>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                        <button key={d} onClick={() => setEditEventForm(p => ({ ...p, repeatDays: p.repeatDays?.includes(i) ? p.repeatDays.filter(x => x !== i) : [...(p.repeatDays||[]), i] }))}
                          style={{ ...S.btn, fontSize: 12, minWidth: 36, background: editEventForm.repeatDays?.includes(i) ? "var(--c-bg-card)" : "transparent", color: editEventForm.repeatDays?.includes(i) ? "#d4789a" : "#a07090", borderColor: editEventForm.repeatDays?.includes(i) ? "#d4789a" : "#e0d0e8" }}>{d}</button>
                      ))}
                    </div>
                  </div>
                )}
                {editEventForm.repeat === "Monthly" && (
                  <div style={{ marginTop: 8 }}>
                    <p style={{ ...S.label, marginBottom: 10 }}>REPEAT ON DATES</p>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <button key={d} onClick={() => setEditEventForm(p => ({ ...p, repeatDates: p.repeatDates?.includes(d) ? p.repeatDates.filter(x => x !== d) : [...(p.repeatDates||[]), d] }))}
                          style={{ ...S.btn, fontSize: 11, minWidth: 30, padding: "3px 0", background: editEventForm.repeatDates?.includes(d) ? "var(--c-bg-card)" : "transparent", color: editEventForm.repeatDates?.includes(d) ? "#d4789a" : "#a07090", borderColor: editEventForm.repeatDates?.includes(d) ? "#d4789a" : "#e0d0e8" }}>{d}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Notes — rich text editor */}
              <div><p style={S.label}>NOTES</p>
                <RichNotesEditor value={editEventForm.notes || ""} onChange={notes => setEditEventForm(p => ({ ...p, notes }))} placeholder="Add notes, links…" />
              </div>
              {/* Buttons */}
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button onClick={saveEditEvent} className="clarity-add-btn" style={S.addBtn}><Save size={14} style={{marginRight:4}} />Save</button>
                <button onClick={() => { setShowEditEventModal(false); setEditingEventId(null); setEditEventForm(null); }} style={S.btn}><X size={14} style={{marginRight:4}} />Cancel</button>
              </div>
            </div>
          </div>
        </>,
        (document.getElementById("portal-root") || document.body)
      )}

      {/* ── Edit Reminder Modal (centered popup, same UI as task edit) ────────── */}
      {showEditReminderModal && editReminderForm && createPortal(
        <>
          <div onClick={() => { setShowEditReminderModal(false); setEditingReminderId(null); setEditReminderForm(null); }}
            style={{ position: "fixed", inset: 0, zIndex: 8000, background: "rgba(60,30,50,0.5)" }} />
          <div onClick={e => e.stopPropagation()} style={{
            position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            zIndex: 8001, width: "min(640px,95vw)", maxHeight: "90vh", overflowY: "auto",
            background: "var(--c-bg-card)", borderRadius: 20, boxShadow: "0 8px 40px rgba(212,120,154,0.28)",
            padding: "24px 28px 28px", fontFamily: "'Quicksand','Nunito',sans-serif",
          }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 1 }}>🔔 Edit Reminder</span>
              <button onClick={() => { setShowEditReminderModal(false); setEditingReminderId(null); setEditReminderForm(null); }}
                style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--c-text-soft)", lineHeight: 1, padding: 0 }}><X size={16} /></button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Title */}
              <div><p style={S.label}>TITLE</p>
                <input value={editReminderForm.title || ""} onChange={e => setEditReminderForm(p => ({ ...p, title: e.target.value }))} style={S.input} placeholder="Reminder title" />
              </div>
              {/* Date + Times */}
              <div><p style={S.label}>DATE</p>
                <NativePickerInput type="date" value={editReminderForm.date || ""} onChange={e => setEditReminderForm(p => ({ ...p, date: e.target.value }))} style={S.input} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}><p style={S.label}>NOTIFY AT</p>
                  <NativePickerInput type="time" value={editReminderForm.time || ""} onChange={e => setEditReminderForm(p => ({ ...p, time: e.target.value }))} style={S.input} />
                </div>
                <div style={{ flex: 1 }}><p style={S.label}>END TIME</p>
                  <NativePickerInput type="time" value={editReminderForm.endTime || ""} onChange={e => setEditReminderForm(p => ({ ...p, endTime: e.target.value }))} style={S.input} />
                </div>
              </div>
              {/* Category */}
              <div><p style={S.label}>CATEGORY</p>
                <select value={editReminderForm.categoryId || ""} onChange={e => setEditReminderForm(p => ({ ...p, categoryId: e.target.value }))} style={{ ...S.input, cursor: "pointer" }}>
                  <option value="">No category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.title}</option>)}
                </select>
              </div>
              {/* Full Color Picker — Edit Reminder */}
              <ModalColorPicker
                currentColor={editReminderForm.color || ""}
                onColorChange={(c) => setEditReminderForm(p => ({ ...p, color: c }))}
                onSaveColor={addSavedColor}
                savedColors={savedColors}
                S={S}
              />
              {/* Repeat */}
              <div><p style={S.label}>REPEAT</p>
                <select value={editReminderForm.repeat || "None"} onChange={e => setEditReminderForm(p => ({ ...p, repeat: e.target.value, repeatDays: [], repeatDates: [] }))} style={{ ...S.input, cursor: "pointer" }}>
                  {["None","Daily","Weekly","Monthly"].map(r => <option key={r}>{r}</option>)}
                </select>
                {editReminderForm.repeat === "Weekly" && (
                  <div style={{ marginTop: 8 }}>
                    <p style={{ ...S.label, marginBottom: 10 }}>REMIND ON DAYS</p>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                        <button key={d} onClick={() => setEditReminderForm(p => ({ ...p, repeatDays: p.repeatDays?.includes(i) ? p.repeatDays.filter(x => x !== i) : [...(p.repeatDays||[]), i] }))}
                          style={{ ...S.btn, fontSize: 12, minWidth: 36, background: editReminderForm.repeatDays?.includes(i) ? "var(--c-bg-card)" : "transparent", color: editReminderForm.repeatDays?.includes(i) ? "#d4789a" : "#a07090", borderColor: editReminderForm.repeatDays?.includes(i) ? "#d4789a" : "#e0d0e8" }}>{d}</button>
                      ))}
                    </div>
                  </div>
                )}
                {editReminderForm.repeat === "Monthly" && (
                  <div style={{ marginTop: 8 }}>
                    <p style={{ ...S.label, marginBottom: 10 }}>REMIND ON DATES</p>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <button key={d} onClick={() => setEditReminderForm(p => ({ ...p, repeatDates: p.repeatDates?.includes(d) ? p.repeatDates.filter(x => x !== d) : [...(p.repeatDates||[]), d] }))}
                          style={{ ...S.btn, fontSize: 11, minWidth: 30, padding: "3px 0", background: editReminderForm.repeatDates?.includes(d) ? "var(--c-bg-card)" : "transparent", color: editReminderForm.repeatDates?.includes(d) ? "#d4789a" : "#a07090", borderColor: editReminderForm.repeatDates?.includes(d) ? "#d4789a" : "#e0d0e8" }}>{d}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Notes — rich text editor */}
              <div><p style={S.label}>NOTES</p>
                <RichNotesEditor value={editReminderForm.notes || ""} onChange={notes => setEditReminderForm(p => ({ ...p, notes }))} placeholder="Add notes, links…" />
              </div>
              {/* Buttons */}
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button onClick={saveEditReminder} className="clarity-add-btn" style={S.addBtn}><Save size={14} style={{marginRight:4}} />Save</button>
                <button onClick={() => { setShowEditReminderModal(false); setEditingReminderId(null); setEditReminderForm(null); }} style={S.btn}><X size={14} style={{marginRight:4}} />Cancel</button>
              </div>
            </div>
          </div>
        </>,
        (document.getElementById("portal-root") || document.body)
      )}

      {/* ── Week view block tap popover ──────────────────────────────────────── */}
      {/* WeekBlockPopoverPortal moved to ClarityApp root */}

      {/* ── Feature 1: Centered floating modal for item details      {/* ── Feature 1: Centered floating modal for item details ─────────────── */}
      {inlineModalItem && inlineModalType && (() => {
        const InlineDetailModal = () => {
          const [confirmDelete, setConfirmDelete] = React.useState(false);
          const handleEdit = () => {
            // Open the same app-level edit modal that week view uses.
            onEditItem(inlineModalItem, inlineModalType);
            afterPaint(() => { setInlineModalItem(null); setInlineModalType(null); });
          };
          const handleDelete = () => {
            if (inlineModalType === "task") deleteTaskFromCal(inlineModalItem.id);
            else if (inlineModalType === "event") deleteEvent(inlineModalItem.id);
            else if (inlineModalType === "reminder") deleteReminder(inlineModalItem.id);
            setInlineModalItem(null); setInlineModalType(null);
          };
          return (
        <Modal onClose={() => { setInlineModalItem(null); setInlineModalType(null); }} blur dim={0.45}>
          <div style={{ background: "var(--c-bg-card)", borderRadius: 20, padding: "24px 20px", width: "100%", maxWidth: 420, boxShadow: "0 8px 40px rgba(212,120,154,0.25)", position: "relative" }}>
            {/* Header: label + action buttons */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--c-text-soft)", letterSpacing: 1 }}>
                {inlineModalType === "task" ? "✅ Task" : inlineModalType === "event" ? "📅 Event" : "🔔 Reminder"}
              </span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {/* Edit button */}
                <button onClick={handleEdit}
                  style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--c-bg-soft)", border: "1.5px solid #c8a8f0", borderRadius: 10, padding: "5px 12px", fontSize: 12, color: "#7a50c0", cursor: "pointer", fontWeight: 700 }}>
                  <Pencil size={13} style={{marginRight:4}} />Edit
                </button>
                {/* Delete button with inline confirm */}
                {!confirmDelete ? (
                  <button onClick={() => setConfirmDelete(true)}
                    style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--c-bg-soft)", border: "1.5px solid #f0b8b8", borderRadius: 10, padding: "5px 12px", fontSize: 12, color: "#e06060", cursor: "pointer", fontWeight: 700 }}>
                    <Trash2 size={13} style={{marginRight:4}} />Delete
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: "#e06060", fontWeight: 600 }}>Sure?</span>
                    <button onClick={handleDelete}
                      style={{ background: "#e8647a", border: "none", borderRadius: 8, padding: "4px 10px", fontSize: 11, color: "#fff", cursor: "pointer", fontWeight: 700 }}><Check size={12} style={{marginRight:3}} />Yes</button>
                    <button onClick={() => setConfirmDelete(false)}
                      style={{ background: "transparent", border: "1px solid #e8c0d0", borderRadius: 8, padding: "4px 10px", fontSize: 11, color: "var(--c-text-soft)", cursor: "pointer" }}><X size={12} style={{marginRight:3}} />No</button>
                  </div>
                )}
                <button onClick={() => { setInlineModalItem(null); setInlineModalType(null); }}
                  style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--c-text-soft)", lineHeight: 1, padding: 0 }}><X size={16} /></button>
              </div>
            </div>

            {/* Task detail */}
            {inlineModalType === "task" && (() => {
              const item = inlineModalItem;
              const liveItem = tasks.find(t => t.id === item.id) || item;
              const cat = categories.find(c => c.id === liveItem.goalId || c.id === liveItem.categoryId);
              const liveItemDone = isTaskDoneOn(liveItem, inlineModalDate, taskOccurrenceOverrides);
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: "var(--c-text)", flex: 1, textDecoration: liveItemDone ? "line-through" : "none", opacity: liveItemDone ? 0.6 : 1 }}>{liveItem.title}</span>
                    <span style={{ background: PRIORITY_COLORS[liveItem.priority] + "25", color: PRIORITY_COLORS[liveItem.priority], borderRadius: 8, padding: "3px 10px", fontSize: 12, fontWeight: 700, textTransform: "capitalize" }}>{liveItem.priority}</span>
                  </div>
                  <button
                    onClick={() => toggleTaskDoneFromCal(liveItem.id, inlineModalDate)}
                    style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, border: `2px solid ${liveItemDone ? "#7bc67e" : "#d4789a"}`, background: liveItemDone ? "var(--c-bg-active)" : "var(--c-bg-card)", color: liveItemDone ? "#4caf50" : "#d4789a", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    {liveItemDone ? <><CheckCircle size={13} style={{marginRight:4}} /> Completed. Tap to undo</> : <><CheckSquare size={13} style={{marginRight:4}} /> Mark as done</>}
                  </button>
                  {liveItem.startDate && <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-soft)" }}>📅 Start: {liveItem.startDate}</p>}
                  {liveItem.dueDate && <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-soft)" }}>⏰ Due: {liveItem.dueDate}</p>}
                  {liveItem.subtasks?.length > 0 && (
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text-soft)", textTransform: "uppercase", marginBottom: 6, letterSpacing: 1 }}>Subtasks</p>
                      <div className="clarity-no-scrollbar" style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflowY: "auto", scrollbarWidth: "none", border: "1.5px solid #1a1a1a", borderRadius: 14, padding: 8 }}>
                        {liveItem.subtasks.map(s => (
                          <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 4px" }}>
                            <HabitCheckbox checked={!!s.done} color="#d4789a" size={20} burst={false}
                              onChange={() => setTasks(p => p.map(t => t.id === liveItem.id ? { ...t, subtasks: (t.subtasks || []).map(x => x.id === s.id ? { ...x, done: !x.done } : x) } : t))} />
                            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.5 : 1, wordBreak: "break-word" }}>{s.title}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {liveItem.embeds?.length > 0 && (
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text-soft)", textTransform: "uppercase", marginBottom: 6, letterSpacing: 1 }}>Embedded Links</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                        {liveItem.embeds.map(em => <EmbedPill key={em.id} url={em.url} description={em.description} />)}
                      </div>
                    </div>
                  )}
                  {liveItem.notes && (
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text-soft)", textTransform: "uppercase", marginBottom: 6, letterSpacing: 1 }}>Notes</p>
                      <div className="clarity-no-scrollbar clarity-notes-view" style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", border: "1.5px solid #1a1a1a", borderRadius: 14, padding: "12px 16px", maxHeight: 120, overflowY: "auto", scrollbarWidth: "none" }}
                        onClick={e => {
                          const box = e.target.closest && e.target.closest(".rn-check-box");
                          if (!box) return;
                          const label = box.parentElement && box.parentElement.querySelector(".rn-check-label");
                          const checked = box.getAttribute("data-checked") === "1";
                          if (checked) {
                            box.removeAttribute("data-checked"); box.textContent = ""; box.style.background = "transparent";
                            if (label) { label.style.textDecoration = "none"; label.style.opacity = "1"; }
                          } else {
                            box.setAttribute("data-checked", "1"); box.textContent = "✓"; box.style.background = "#d4789a";
                            if (label) { label.style.textDecoration = "line-through"; label.style.opacity = "0.55"; }
                          }
                          const html = e.currentTarget.innerHTML;
                          setTasks(p => p.map(t => t.id === liveItem.id ? { ...t, notes: html } : t));
                        }}
                        dangerouslySetInnerHTML={{ __html: liveItem.notes }} />
                    </div>
                  )}
                  {cat && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: cat.color }} />
                      <span style={{ fontSize: 12, color: "var(--c-text-soft)" }}><CategoryIcon cat={cat} size={12} /> {cat.title}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Event detail */}
            {inlineModalType === "event" && (() => {
              const item = inlineModalItem;
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 18, fontWeight: 700, color: "var(--c-text)" }}>{item.title}</span>
                  </div>
                  {item.date && <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-soft)" }}>📅 {item.date}{item.time ? " · " + item.time + (item.endTime ? " – " + item.endTime : "") : ""}</p>}
                  {item.location && <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-soft)" }}>📍 {item.location}</p>}
                  {item.description && <p style={{ margin: 0, fontSize: 13, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", borderRadius: 10, padding: "8px 12px" }}>{item.description}</p>}
                  {item.repeat && item.repeat !== "None" && <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-soft)" }}>🔁 Repeats {item.repeat}{item.repeat === "Weekly" && item.repeatDays?.length > 0 && ` · ${item.repeatDays.sort((a,b)=>a-b).map(i => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]).join(", ")}`}{item.repeat === "Monthly" && item.repeatDates?.length > 0 && ` · ${item.repeatDates.sort((a,b)=>a-b).join(", ")}`}</p>}
                  {item.embeds?.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {item.embeds.map(em => <EmbedPill key={em.id} url={em.url} description={em.description} />)}
                    </div>
                  )}
                  {item.notes && <div className="clarity-notes-view" style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", borderRadius: 10, padding: "8px 12px", maxHeight: 120, overflowY: "auto" }} dangerouslySetInnerHTML={{ __html: item.notes }} />}
                </div>
              );
            })()}

            {/* Reminder detail */}
            {inlineModalType === "reminder" && (() => {
              const item = inlineModalItem;
              const cat = categories.find(c => c.id === item.categoryId);
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: "var(--c-text)", flex: 1 }}>{item.title}</span>
                    <span style={{ background: PRIORITY_COLORS[item.priority] + "25", color: PRIORITY_COLORS[item.priority], borderRadius: 8, padding: "3px 10px", fontSize: 12, fontWeight: 700, textTransform: "capitalize" }}>{item.priority}</span>
                  </div>
                  {cat && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: cat.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: "var(--c-text-soft)", fontWeight: 600 }}><CategoryIcon cat={cat} size={12} /> {cat.title}</span>
                    </div>
                  )}
                  {item.date && <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-soft)" }}>📅 {item.date}{item.time ? " · Notify at " + item.time + (item.endTime ? " – " + item.endTime : "") : ""}</p>}
                  {item.repeat && item.repeat !== "None" && <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-soft)" }}>🔁 Repeats {item.repeat}{item.repeat === "Weekly" && item.repeatDays?.length > 0 && ` · ${item.repeatDays.sort((a,b)=>a-b).map(i => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]).join(", ")}`}{item.repeat === "Monthly" && item.repeatDates?.length > 0 && ` · ${item.repeatDates.sort((a,b)=>a-b).join(", ")}`}</p>}
                  {item.notes && <div className="clarity-notes-view" style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", borderRadius: 10, padding: "8px 12px", maxHeight: 120, overflowY: "auto" }} dangerouslySetInnerHTML={{ __html: item.notes }} />}
                </div>
              );
            })()}

            {/* Links section — shown if item has links */}
            {inlineModalItem?.links?.length > 0 && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #f0e0ec" }}>
                <p style={{ ...S.label, marginBottom: 6 }}>LINKS</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {inlineModalItem.links.map(l => {
                    const meta = LINK_CATS.find(c => c.key === l.cat);
                    const display = l.label || meta?.label || l.cat;
                    return (
                      <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer"
                        style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--c-bg-soft)", border: "1.5px solid #f0c8e0", borderRadius: 20, padding: "4px 12px", fontSize: 12, color: "var(--c-text-mid)", fontWeight: 600, textDecoration: "none" }}>
                        <span>{meta?.emoji || "🔗"}</span><span>{display}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Modal>
          );
        };
        return <InlineDetailModal />;
      })()}


      {/* ── FAB (Floating Action Button) ───────────────────────────────────────── */}
      {(calSubTab === "month" || calSubTab === "day" || calSubTab === "year") && !(isDesktop && plannerView === "list") && (
        <button
          className="cal-fab-desktop"
          onClick={() => { setSelectedDate(today); setCalPanel("add"); }}
          style={{
            position: "fixed", bottom: "calc(140px + var(--sab, 0px))", right: 20,
            width: 52, height: 52, borderRadius: "50%",
            background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))",
            color: "#fff", fontSize: 28, border: "none",
            boxShadow: "0 4px 16px rgba(212,88,154,0.4)",
            cursor: "pointer", zIndex: 150,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 300, lineHeight: 1,
          }}>
          +
        </button>
      )}
      {/* Mobile FAB — visible on small screens for month and day views only */}
      {(calSubTab === "month" || calSubTab === "day") && plannerView !== "list" && (
      <button
        className="cal-fab-mobile"
        onClick={() => { setSelectedDate(today); setCalPanel("add"); }}
        style={{
          position: "fixed", bottom: "calc(88px + var(--sab, 0px))", right: 20,
          width: 52, height: 52, borderRadius: "50%",
          background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))",
          color: "#fff", fontSize: 28, border: "none",
          boxShadow: "0 4px 20px rgba(212,88,154,0.45)",
          cursor: "pointer", zIndex: 150,
          display: "none", alignItems: "center", justifyContent: "center",
          fontWeight: 300, lineHeight: 1,
        }}>
        +
      </button>
      )}

      {/* ── Backdrop for any open panel ────────────────────────────────────────── */}
      {/* Not for the day / detail panels: they draw their own DETAIL_BACKDROP, and this
          dark 0.5 dim stacked under it made them much darker than the Categories panel. */}
      {calPanel && calPanel !== "day" && calPanel !== "detail" && calSubTab !== "week" && createPortal(
        <div style={{ position: "fixed", inset: 0, background: "rgba(60,30,50,0.5)", zIndex: 205 }}
          onClick={closeAllPanels} />,
        (document.getElementById("portal-root") || document.body)
      )}

      {/* ── DAY DETAIL PANEL ───────────────────────────────────────────────────── */}
      {calPanel === "day" && selectedDate && (calSubTab !== "week" || weekDayPanel) && createPortal(
        <>
          {/* Blur backdrop — the shared DETAIL_BACKDROP (same as the Week view's
              popover); fades in with the panel, only when the panel itself pops. */}
          <div
            className={dayPanelPopsRef.current ? "clarity-fade" : undefined}
            onClick={closeAllPanels}
            style={{ position: "fixed", inset: 0, zIndex: 206, ...DETAIL_BACKDROP }}
          />
          {/* Centered card — pops only when opened from a day tap (dayPanelPopsRef). */}
          <div className={dayPanelPopsRef.current ? "clarity-pop-c" : undefined}
            onClick={e => e.stopPropagation()}
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 207, width: "min(90vw, 420px)",
              maxHeight: "80vh",
              background: "var(--c-bg-card)",
              borderRadius: 24,
              boxShadow: "0 24px 80px rgba(100,30,80,0.30)",
              overflowY: "auto",
              padding: "22px 20px 28px",
              fontFamily: "'Quicksand', sans-serif",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <span style={{ fontWeight: 800, fontSize: 15, color: "var(--c-text-accent)", letterSpacing: "-0.01em" }}>
                {fmtSelected(selectedDate)}
              </span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button onClick={() => setCalPanel("add")}
                  style={{ background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", border: "none", borderRadius: "50%", width: 30, height: 30, color: "var(--c-on-primary)", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 300 }}>
                  +
                </button>
                {closeBtn(closeAllPanels)}
              </div>
            </div>

            {holidays[selectedDate] && (
              <div style={{ margin: "-8px 0 16px" }}>
                <span style={{ display: "inline-block", maxWidth: "100%", boxSizing: "border-box", padding: "4px 11px", borderRadius: 999, background: HOLIDAY_BG, color: HOLIDAY_TEXT, fontSize: 12, fontWeight: 700, lineHeight: 1.4 }}>
                  {holidays[selectedDate]}
                </span>
              </div>
            )}

            {panelTasks.length + panelEvents.length + panelReminders.length === 0 ? (
              (() => {
                const rawCount = panelTasksRaw.length + panelEventsRaw.length + panelRemindersRaw.length;
                const isFiltered = visibleFilters !== null && rawCount > 0;
                return (
                  <div style={{ textAlign: "center", padding: "18px 0" }}>
                    <p style={{ ...S.empty, margin: 0 }}>
                      {isFiltered ? "🔍 Some items hidden by filters" : "Nothing here yet. Tap + to add something!"}
                    </p>
                    {isFiltered && (
                      <button onClick={() => { closeAllPanels(); selectAllFilters(); }}
                        style={{ marginTop: 8, fontSize: 11, color: "var(--c-text-accent)", background: "transparent", border: "1px solid #f0c8e0", borderRadius: 8, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}>
                        <Filter size={11} style={{marginRight:4}} />Show everything
                      </button>
                    )}
                  </div>
                );
              })()
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {/* Tasks group */}
                {panelTasks.length > 0 && (
                  <div>
                    <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: "0.09em" }}>Tasks</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {panelTasks.map(t => (
                        <div key={t.id}
                          onClick={() => { setDayDetailItem(t); setDayDetailType("task"); setDayDetailDate(activeDateForPanel); setCalPanel("detail"); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "11px 14px",
                            background: "var(--c-bg-soft)",
                            borderRadius: 14,
                            border: "1.5px solid var(--c-border-light)",
                            cursor: "pointer",
                            transition: "background 0.14s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--c-bg-active)"}
                          onMouseLeave={e => e.currentTarget.style.background = "var(--c-bg-soft)"}
                        >
                          <div style={{ width: 4, alignSelf: "stretch", borderRadius: 4, background: PRIORITY_COLORS[t.priority] || "var(--c-border)", minHeight: 26, flexShrink: 0 }} />
                          <span style={{ fontSize: 13, flex: 1, color: "var(--c-text)", lineHeight: 1.35, wordBreak: "break-word", textDecoration: isTaskDoneOn(t, activeDateForPanel, taskOccurrenceOverrides) ? "line-through" : "none", opacity: isTaskDoneOn(t, activeDateForPanel, taskOccurrenceOverrides) ? 0.5 : 1, fontWeight: 600 }}>
                            {t.title}
                          </span>
                          {t.time && <span style={{ fontSize: 10, color: "var(--c-text-soft)", flexShrink: 0 }}>{t.time}</span>}
                          <ChevronRight size={15} style={{ color: "var(--c-text-soft)", flexShrink: 0 }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Events group */}
                {panelEvents.length > 0 && (
                  <div>
                    <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: "0.09em" }}>Events</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {panelEvents.map(ev => (
                        <div key={ev.id}
                          onClick={() => { setDayDetailItem(ev); setDayDetailType("event"); setDayDetailDate(activeDateForPanel); setCalPanel("detail"); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "11px 14px",
                            background: "var(--c-bg-soft)",
                            borderRadius: 14,
                            border: "1.5px solid var(--c-border-light)",
                            cursor: "pointer",
                            transition: "background 0.14s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--c-bg-active)"}
                          onMouseLeave={e => e.currentTarget.style.background = "var(--c-bg-soft)"}
                        >
                          <div style={{ width: 4, alignSelf: "stretch", borderRadius: 4, background: eventColor(ev), minHeight: 26, flexShrink: 0 }} />
                          <span style={{ fontSize: 13, flex: 1, color: "var(--c-text)", lineHeight: 1.35, wordBreak: "break-word", fontWeight: 600 }}>
                            {ev.title}
                          </span>
                          {ev.time && <span style={{ fontSize: 10, color: "var(--c-text-soft)", flexShrink: 0 }}>{ev.time}</span>}
                          <ChevronRight size={15} style={{ color: "var(--c-text-soft)", flexShrink: 0 }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Reminders group */}
                {panelReminders.length > 0 && (
                  <div>
                    <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: "0.09em" }}>Reminders</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {panelReminders.map(r => (
                        <div key={r.id}
                          onClick={() => { setDayDetailItem(r); setDayDetailType("reminder"); setDayDetailDate(activeDateForPanel); setCalPanel("detail"); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "11px 14px",
                            background: "var(--c-bg-soft)",
                            borderRadius: 14,
                            border: "1.5px solid var(--c-border-light)",
                            cursor: "pointer",
                            transition: "background 0.14s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--c-bg-active)"}
                          onMouseLeave={e => e.currentTarget.style.background = "var(--c-bg-soft)"}
                        >
                          <div style={{ width: 4, alignSelf: "stretch", borderRadius: 4, background: PRIORITY_COLORS[r.priority] || "#5bbfb5", minHeight: 26, flexShrink: 0 }} />
                          <span style={{ fontSize: 13, flex: 1, color: "var(--c-text)", lineHeight: 1.35, wordBreak: "break-word", fontWeight: 600 }}>
                            {r.title}
                          </span>
                          {r.time && <span style={{ fontSize: 10, color: "var(--c-text-soft)", flexShrink: 0 }}>{r.time}</span>}
                          <ChevronRight size={15} style={{ color: "var(--c-text-soft)", flexShrink: 0 }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>,
        (document.getElementById("portal-root") || document.body)
      )}

      {/* ── CUSTOM DELETE CONFIRMATION MODAL ─────────────────────────────────── */}
      {dayPanelDeleteConfirm && createPortal(
        <div
          onClick={() => setDayPanelDeleteConfirm(null)}
          style={{
            // Above every panel that can open it, including the Year view's day-list
            // (zIndex 4000/4001) and item-detail (zIndex 4210) panels — otherwise the
            // confirm dialog rendered underneath them, invisible until both were closed.
            position: "fixed", inset: 0, zIndex: 4300,
            background: "rgba(20,10,30,0.62)",
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "var(--c-bg-card)", borderRadius: 24, padding: "30px 26px",
              maxWidth: 320, width: "100%", textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.28)", fontFamily: "'Quicksand','Nunito',sans-serif",
            }}>
            <ConfirmHeader title="Are you sure you want to delete this item?" />
            <div style={CONFIRM_STACK}>
              <button onClick={() => setDayPanelDeleteConfirm(null)} style={CONFIRM_CANCEL}>
                Cancel
              </button>
              <button
                onClick={() => {
                  const { type, id, item } = dayPanelDeleteConfirm;
                  if (item) softDeleteItems([{ id, type, item }]);
                  if (type === "task") setTasks(p => p.filter(t => t.id !== id));
                  else if (type === "event") setCalEvents(p => removeEventCascade(p, id));
                  else setCalReminders(p => p.filter(r => r.id !== id));
                  setDayDetailItem(null);
                  setDayDetailType(null);
                  setCalPanel(calSubTab === "year" ? null : "day");
                  setDayPanelDeleteConfirm(null);
                }}
                style={CONFIRM_DANGER}>
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── ADD OPTIONS SHEET ──────────────────────────────────────────────────── */}
      {/* Routed through the shared <Modal> — same as the list-view add panel and
          the edit modal — so this add form has an IDENTICAL width to the edit
          panel (Modal's 24px padding) and inherits Modal's smooth-scroll
          container (WebkitOverflowScrolling: touch, overscroll-behavior:
          contain, own GPU layer). Previously it used a bare fixed div, which
          made it ~28px wider than the edit panel on mobile and scroll janky. */}
      {/* Lay the add panel out once, invisibly, after load — see PanelWarmup. */}
      {calPanel !== "add" && !showListAddPanel && !editModalItem && (
        <PanelWarmup id="planner-item-panel">
          <ItemPanel
            mode="add" initialType="task" defaultDate={today} defaultTime="" defaultTitle=""
            categories={categories} setCategories={() => {}} savedColors={savedColors} addSavedColor={() => {}}
            onClose={() => {}} onSubmit={() => {}}
          />
        </PanelWarmup>
      )}
      {calPanel === "add" && (
        // No pop-in on Month/Year (Jade, 2026-10-06): the panel just appears,
        // like the edit panels. Week view keeps it.
        <Modal onClose={closeAllPanels} blur={false} dim={0.5} animate={false}>
          <ItemPanel
            mode="add"
            initialType={quickAddType === "task" ? "task" : "event"}
            defaultDate={activeDateForPanel || today}
            defaultTime={quickAddTime}
            defaultTitle={quickAddTitle}
            categories={categories} setCategories={setCategories} savedColors={savedColors} addSavedColor={addSavedColor}
            onClose={closeAllPanels}
            onSubmit={(t, payload) => {
              if (t === "event") setCalEvents(p => [...p, payload]);
              else setTasks(p => [...p, payload]);
              closeAllPanels();
            }}
          />
        </Modal>
      )}

      {/* ── FULL DETAIL VIEW ──────────────────────────────────────────────────── */}
      {calPanel === "detail" && dayDetailItem && (calSubTab !== "week" || weekDayPanel) && (
        <ItemDetailPanel
          item={dayDetailItem} type={dayDetailType} date={dayDetailDate}
          categories={categories} tasks={tasks} calEvents={calEvents} calReminders={calReminders}
          setTasks={setTasks} setCalEvents={setCalEvents} setCalReminders={setCalReminders}
          taskOccurrenceOverrides={taskOccurrenceOverrides} setTaskOccurrenceOverrides={setTaskOccurrenceOverrides}
          calEventOccurrenceOverrides={calEventOccurrenceOverrides} setCalEventOccurrenceOverrides={setCalEventOccurrenceOverrides} weekStart={weekStart}
          onEdit={(it, ty) => { onEditItem(it, ty); afterPaint(closeDetail); }}
          onSkip={openSkipDetail}
          onDelete={deleteDetailItem}
          onClose={closeDetail}
          zIndexBase={calSubTab === "year" ? 4209 : 208}
          animate={false}
          
        />
      )}


      </></div>}

      {/* ── Skip / Reschedule Modal — always rendered (week, month, year, list) ── */}
      {listSkipFlow && (() => {
        const { item: skipItem, type: skipType, step, rescheduledTo, origDate } = listSkipFlow;
        const skipLabel = skipItem.title || skipItem.name || "Untitled";
        const MONTHS_SK = ["January","February","March","April","May","June","July","August","September","October","November","December"];
        const { y, m } = listSkipCalMonth;
        const firstDay = dowOffset(new Date(y, m, 1).getDay());
        const daysInMonth = new Date(y, m + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        const todayFmt = todayStr();

        // Recurring items don't have one single date to overwrite — that would
        // shift the WHOLE series and silently corrupt every other occurrence
        // (see the investigation this fixes). Tasks/events instead get a
        // per-occurrence entry in taskOccurrenceOverrides/calEventOccurrenceOverrides
        // — the exact same store the week-view drag-to-move feature already
        // writes to and every calendar view (week grid, list) already reads —
        // so a rescheduled occurrence renders identically to a dragged one.
        // Reminders have no such external store, so they carry the same info
        // on themselves instead (movedOccurrences/skippedOccurrences — see
        // _computeOccurringOn in taskLogic.js). One-time items keep the old,
        // simple direct-mutation path.
        const isRecurring = skipType === "task"
          ? !!(skipItem.frequencyType && skipItem.frequencyType !== "once")
          : !!((skipItem.frequencyType && skipItem.frequencyType !== "once") || (skipItem.repeat && skipItem.repeat !== "None"));
        const occDate = origDate || (skipType === "task" ? (skipItem.startDate || skipItem.dueDate) : skipItem.date);

        const saveSkip = () => {
          if (skipType === "task") {
            if (isRecurring && occDate) {
              setTaskOccurrenceOverrides(prev => {
                const key = `${skipItem.id}:${occDate}`;
                return { ...prev, [key]: { ...prev[key], ...(rescheduledTo ? { date: rescheduledTo } : { skipped: true }) } };
              });
            } else {
              setTasks(p => p.map(t => t.id === skipItem.id ? {
                ...t,
                ...(rescheduledTo ? { dueDate: rescheduledTo, startDate: rescheduledTo } : { skipped: true }),
              } : t));
            }
          } else if (skipType === "event") {
            if (isRecurring && occDate) {
              setCalEventOccurrenceOverrides(prev => {
                const key = `${skipItem.id}:${occDate}`;
                return { ...prev, [key]: { ...prev[key], ...(rescheduledTo ? { date: rescheduledTo } : { skipped: true }) } };
              });
            } else {
              setCalEvents(p => p.map(e => e.id === skipItem.id ? {
                ...e,
                ...(rescheduledTo ? { date: rescheduledTo } : { skipped: true }),
              } : e));
            }
          } else {
            if (isRecurring && occDate) {
              setCalReminders(p => p.map(r => r.id === skipItem.id ? (
                rescheduledTo
                  ? { ...r, movedOccurrences: [...(r.movedOccurrences || []), { from: occDate, to: rescheduledTo }] }
                  : { ...r, skippedOccurrences: [...(r.skippedOccurrences || []), occDate] }
              ) : r));
            } else {
              setCalReminders(p => p.map(r => r.id === skipItem.id ? {
                ...r,
                ...(rescheduledTo ? { date: rescheduledTo } : { skipped: true }),
              } : r));
            }
          }
          setListSkipFlow(null);
        };

        const cancelSkipFlow = () => { setListSkipFlow(null); };

        if (step === "confirm") {
          return createPortal(
            <div style={{ position: "fixed", inset: 0, zIndex: 99990, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 20px" }}>
              {/* No pop-in on the task/event Skip panel (Jade, 2026-10-06). */}
              <div onClick={cancelSkipFlow} style={{ position: "absolute", inset: 0, background: "rgba(60,30,50,0.38)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} />
              <div style={{ position: "relative", background: "var(--c-bg-card)", borderRadius: 20, padding: "26px 22px 20px", width: "100%", maxWidth: 360, boxShadow: "0 8px 32px rgba(100,40,80,0.18)", zIndex: 1, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
                <ConfirmHeader tone="accent" compact icon={SkipForward} title={`Skip "${skipLabel}"?`} subtitle="Pick a day below to reschedule it, or just skip it." />

                {/* Mini calendar */}
                <div style={{ background: "var(--c-bg-card)", borderRadius: 12, padding: "10px 8px", marginBottom: 14, border: "none", boxShadow: SKIP_CAL_SHADOW }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <button onClick={() => setListSkipCalMonth(prev => { const d = new Date(prev.y, prev.m - 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; })}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-accent)", fontSize: 16, padding: "0 6px" }}><ChevronLeft size={16} /></button>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text)", fontFamily: "'Quicksand','Nunito',sans-serif" }}>{MONTHS_SK[m]} {y}</span>
                    <button onClick={() => setListSkipCalMonth(prev => { const d = new Date(prev.y, prev.m + 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; })}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-accent)", fontSize: 16, padding: "0 6px" }}><ChevronRight size={16} /></button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, textAlign: "center" }}>
                    {weekdayHeaderTwo.map(d => (
                      <div key={d} style={{ fontSize: 9, color: "var(--c-text-soft)", fontWeight: 700, paddingBottom: 4 }}>{d}</div>
                    ))}
                    {cells.map((day, idx) => {
                      if (!day) return <div key={"e"+idx} />;
                      const dateStr = `${y}-${String(m+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                      const isPast = dateStr <= todayFmt;
                      const isSelected = listSkipFlow.rescheduledTo === dateStr;
                      return (
                        <button key={dateStr} disabled={isPast}
                          onClick={() => setListSkipFlow(f => ({ ...f, rescheduledTo: isSelected ? null : dateStr }))}
                          style={{ width: "100%", aspectRatio: "1", borderRadius: 8,
                            border: isSelected ? "2px solid var(--c-primary)" : "1px solid transparent",
                            background: isSelected ? "var(--c-bg-card)" : isPast ? "transparent" : "var(--c-bg-active)",
                            color: isPast ? "#d8c8d8" : isSelected ? "var(--c-text-accent)" : "#4a3040",
                            fontWeight: isSelected ? 700 : 400, fontSize: 12,
                            cursor: isPast ? "not-allowed" : "pointer", padding: 0,
                            fontFamily: "'Quicksand','Nunito',sans-serif",
                          }}>
                          {day}
                        </button>
                      );
                    })}
                  </div>
                  {listSkipFlow.rescheduledTo && (
                    <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--c-text-accent)", textAlign: "center", fontWeight: 600 }}>
                      📅 Rescheduling to {listSkipFlow.rescheduledTo}
                    </p>
                  )}
                </div>

                <div style={CONFIRM_STACK}>
                  <button onClick={cancelSkipFlow} style={CONFIRM_CANCEL}>Cancel</button>
                  <button onClick={saveSkip} style={CONFIRM_PRIMARY}>{listSkipFlow.rescheduledTo ? "Reschedule" : "Skip"}</button>
                </div>
              </div>
            </div>,
            document.getElementById("portal-root") || document.body
          );
        }
        return null;
      })()}

      {/* ── Edit Modal — always rendered regardless of plannerView ──
          blur DISABLED: a live backdrop-filter re-samples everything behind it every
          scroll frame. Over the calendar view (fixed topbar + pills + gradient in a
          locked layout) that re-blur is expensive and janks the scroll — which is why
          the same modal over the lightweight List view scrolls perfectly smoothly. A
          plain dim composites once, so the calendar edit panel now matches List. */}
      {editModalItem && editModalType && editModalForm && (
        <Modal onClose={closeEditModal} blur={false} dim={0.5} animate={false}>
          {(editModalType === "task" || editModalType === "event") ? (
            <ItemPanel
              mode="edit" initialType={editModalType} item={editModalForm}
              categories={categories} setCategories={setCategories} savedColors={savedColors} addSavedColor={addSavedColor}
              onClose={closeEditModal}
              onSubmit={(t, payload, orig) => {
                if (orig && orig !== t) {
                  if (orig === "task") setTasks(p => p.filter(x => x.id !== payload.id));
                  else if (orig === "event") setCalEvents(p => p.filter(x => x.id !== payload.id));
                }
                if (t === "task") setTasks(p => p.some(x => x.id === payload.id) ? p.map(x => x.id === payload.id ? payload : x) : [...p, payload]);
                else setCalEvents(p => p.some(x => x.id === payload.id) ? p.map(x => x.id === payload.id ? payload : x) : [...p, payload]);
                closeEditModal();
              }}
            />
          ) : (
            <div style={{ background: "var(--c-bg-card)", borderRadius: 16, padding: "24px 28px 28px", width: "min(640px,95vw)", maxHeight: "80vh", overflowY: "auto", position: "relative", zIndex: 401 }}
              onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text-accent)", textTransform: "uppercase", letterSpacing: 0.7 }}>Edit Reminder</span>
                <button onClick={closeEditModal} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--c-text-soft)", padding: 0, lineHeight: 1 }}><X size={16} /></button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <p style={S.label}>TITLE</p>
                  <input value={editModalForm.title || ""} onChange={e => setEditModalForm(p => ({ ...p, title: e.target.value }))} style={S.input} placeholder="Reminder title" />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <p style={S.label}>DATE</p>
                    <NativePickerInput type="date" value={editModalForm.date || ""} onChange={e => setEditModalForm(p => ({ ...p, date: e.target.value }))} style={S.input} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={S.label}>NOTIFY AT</p>
                    <NativePickerInput type="time" value={editModalForm.time || ""} onChange={e => setEditModalForm(p => ({ ...p, time: e.target.value }))} style={S.input} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={S.label}>END TIME</p>
                    <NativePickerInput type="time" value={editModalForm.endTime || ""} onChange={e => setEditModalForm(p => ({ ...p, endTime: e.target.value }))} style={S.input} />
                  </div>
                </div>
                <div>
                  <p style={S.label}>PRIORITY</p>
                  <div style={{ display: "flex", gap: 6 }}>
                    {["low","medium","high"].map(pr => (
                      <button key={pr} onClick={() => setEditModalForm(p => ({ ...p, priority: pr }))}
                        style={{ ...S.btn, fontSize: 12, background: editModalForm.priority === pr ? PRIORITY_COLORS[pr] + "30" : "transparent", color: PRIORITY_COLORS[pr], borderColor: PRIORITY_COLORS[pr] + "88", textTransform: "capitalize" }}>{pr}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p style={S.label}>CATEGORY</p>
                  <select value={editModalForm.categoryId || ""} onChange={e => setEditModalForm(p => ({ ...p, categoryId: e.target.value }))} style={{ ...S.input, cursor: "pointer" }}>
                    <option value="">No category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.title}</option>)}
                  </select>
                </div>
                <div>
                  <p style={S.label}>NOTES</p>
                  <textarea rows={2} value={editModalForm.notes || ""} onChange={e => setEditModalForm(p => ({ ...p, notes: e.target.value }))} style={{ ...S.input, resize: "none", height: 56 }} />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button onClick={saveEditModal} className="clarity-add-btn" style={S.addBtn}><Save size={14} style={{marginRight:4}} />Save</button>
                  <button onClick={closeEditModal} style={S.btn}><X size={14} style={{marginRight:4}} />Cancel</button>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}

    </div>
  );
}
